import MaturixStrengthUnit from '../../enums/MaturixStrengthUnit'
import MaturixTemperatureUnit from '../../enums/MaturixTemperatureUnit'
import { TagType } from '../../enums/TagType'
import { getBandTitleByTagType } from '../../utils/TagHelper'
import { brandName, brandShortName } from '../brand'
import LanguageTreeType from '../LanguageTreeType'
import countries from './countries'
import timezones from './timezones'

const ending = (x: number, single: string = '', plural: string = 's') =>
  x === 1 ? single : plural

const users = (x: number) => `Usuario${ending(x, '', 's')}`
const sensors = (x: number) => `Sensor${ending(x, '', 'es')}`
const struxTagsCount = (x: number) => `StruxTag${ending(x, '', 's')}`

const xUsers = (x: number) => `${x} usuario${ending(x)}`
const xFiles = (x: number) => `${x} archivo${ending(x)}`
const activities = (x: number) => `Actividad${ending(x, '', 'es')}`
const xHours = (x: number) => `${x} hora${ending(x, '', 's')}`
const deliveries = (x: number) => `Entrega${ending(x, '', 's')}`
const plans = (x: number) => `Plan${ending(x, '', 'es')}`
const workflows = (x: number) => `Workflow${ending(x, '', 's')}`
const equipment = (x: number) => `artículo${ending(x, '', 's')} de equipo`
const materials = (x: number) => `material${ending(x, '', 'es')}`
const xDates = (x: number) => `${x} Fecha${ending(x)}`
const xForms = (x: number) => `${x} Formulario${ending(x)}`
const xFields = (x: number) => `${x} campo${ending(x)}`
const xFieldsAdded = (x: number) =>
  `${x} campo${ending(x)} agregado${ending(x)}`
const xProjects = (x: number) => `${x} proyecto${ending(x)}`
const xProperties = (x: number) => `${x} Propiedad${ending(x, '', 'es')}`

const SpanishTree: LanguageTreeType = {
  _brandName: brandName,
  _brandShortName: brandShortName,
  label: 'Etiqueta',
  avatar: 'Avatar',
  ok: 'OK',
  alert: 'Alerta',
  warning: 'Advertencia',
  cancel: 'Cancelar',
  bulkAddOptions: 'Añadir opciones en bloque',
  dropdownOptions: 'Opciones desplegables',
  optionLabel: 'Etiqueta de la opción',
  selectAllOptions: 'Seleccionar todas las opciones',
  hierarchyOptionsIntro:
    'Cree una lista de opciones que los usuarios puedan seleccionar. Cree listas jerárquicas anidando opciones bajo elementos principales.',
  cantChangeNestingChecking:
    'No se puede cambiar el anidamiento todavía — aún se está comprobando qué opciones usan los registros. Inténtelo de nuevo en un momento.',
  optionUsedCannotNest:
    'Esta opción la usan registros existentes y no se puede convertir en un elemento principal. Solo las opciones sin registros pueden tener opciones anidadas.',
  cantRestoreExceedsDepth:
    'No se puede restaurar — anidar estas opciones juntas superaría la profundidad máxima.',
  restoredDeletedOptions: (labels: string, count: number) =>
    `Se ${
      count === 1
        ? 'restauró la opción eliminada'
        : 'restauraron las opciones eliminadas'
    } anteriormente: ${labels}`,
  savesAs: 'Se guarda como',
  alreadyUsedBy: (label: string) => `Ya lo usa «${label}»`,
  useValue: (value: string) => `Usar ${value}`,
  matchesDeletedOptionWillRestore: (label: string) =>
    `Coincide con la opción eliminada «${label}» — se restaurará`,
  restoreAndApply: 'Restaurar y aplicar',
  collapseOption: 'Contraer opción',
  expandOption: 'Expandir opción',
  pickedUpOption: (label: string) => `Opción ${label} tomada.`,
  optionIsOver: (active: string, over: string) =>
    `La opción ${active} está sobre ${over}.`,
  optionDroppedOver: (active: string, over: string) =>
    `La opción ${active} se soltó sobre ${over}.`,
  optionDropped: (label: string) => `La opción ${label} se soltó.`,
  reorderingOptionCancelled: (label: string) =>
    `Se canceló el reordenamiento de la opción ${label}.`,
  optionsSelected: (count: number) =>
    `${count} ${
      count === 1 ? 'opción seleccionada' : 'opciones seleccionadas'
    }`,
  indent: 'Sangrar',
  outdent: 'Reducir sangría',
  reorderOption: (label: string) => `Reordenar ${label}`,
  selectOption: (label: string) => `Seleccionar ${label}`,
  bulkAddOptionEnterEachLine: 'Escribe cada opción en su propia línea.',
  bulkAddOptionIndentToNest:
    'Aplica sangría a una opción para anidarla bajo un elemento principal.',
  bulkAddOptionCopyFromExcel:
    'Copia y pega conjuntos de datos completos desde Excel',
  seeTips: 'ver consejos',
  addOptions: 'Añadir opciones',
  copyingAndPastingFromExcel: 'Copiar y pegar datos desde Excel',
  bulkImportGuideSubtitle:
    'Tres formas de dar formato a las opciones al pegar desde una hoja de cálculo.',
  flatDatasets: 'Conjuntos de datos planos',
  flatDatasetsHint:
    'Coloca cada opción en una fila diferente antes de copiar y pegar.',
  hierarchicalDatasetMethod1: 'Conjunto de datos jerárquico (Método 1)',
  hierarchicalDatasetMethod1Hint:
    'Coloca cada nivel en una columna diferente y cada opción en una fila diferente antes de copiar.',
  hierarchicalDatasetMethod2: 'Conjunto de datos jerárquico (Método 2)',
  hierarchicalDatasetMethod2Hint:
    'Igual que el Método 1, pero los elementos principales pueden duplicarse opcionalmente en cada fila.',
  back: 'Atrás',
  addOptionBelow: (label: string) => `Añadir opción debajo de ${label}`,
  optionCount: (count: number) =>
    `${count} ${count === 1 ? 'opción' : 'opciones'}`,
  optionMatchCount: (matched: number, total: number) =>
    `${matched} de ${total}`,
  tipXOfY: (current: number, total: number) => `Consejo ${current} de ${total}`,
  placeHere: 'Colocar aquí',
  failedToSavePlacement: 'No se pudo guardar la ubicación. Inténtelo de nuevo.',
  placedAtMapCenterDragToAdjust:
    'Colocado en el centro del mapa — arrastre para ajustar',
  notPlacedOnMap: 'No colocado en el mapa',
  placeOnMap: 'Colocar en el mapa',
  placedOnLocationDragToAdjust: (name: string) =>
    `Colocado sobre ${name} — arrastre para ajustar`,
  placingMultipleItems: (count: number) =>
    `Colocando ${count} elementos — arrastre cada uno y confirme`,
  addedToPlacementQueue: (remaining: number) =>
    `Añadido a la cola de colocación — ${remaining} restantes`,
  open: 'Abrir',
  openWorkflowForm: 'Abrir flujo de trabajo',
  minimize: 'Minimizar',
  opened: 'Abierto',
  close: 'Cerrar',
  closed: 'Cerrado',
  contacts: 'Contactos',
  loading: 'Cargando',
  language: 'Idioma',
  notSpecified: 'No especificado',
  default: 'Predeterminado',
  done: 'Hecho',
  save: 'Guardar',
  apply: 'Aplicar',
  edit_verb: 'Editar',
  merge_verb: 'Unir',
  link_verb: 'Vincular',
  re_link_verb: 'Volver a vincular',
  unlink_verb: 'Desconectar',
  unlinking: 'Desvinculación',
  linkToX: (x: string) => `Vincular a ${x}`,
  linkedToX: (x: string) => `Vinculado a ${x}`,
  savingEllipsis: 'Guardando...',
  details: 'Detalles',
  delete: 'Eliminar',
  rename: 'Renombrar',
  reorder: 'Reordenar',
  renameMapView: 'Renombrar vista de mapa',
  mapViewName: 'Nombre de la vista',
  deletedField: 'Campo eliminado',
  duplicate: 'Duplicada',
  duplicatedName: (name: string) => `${name} - Copia`,
  initials: 'Iniciales',
  invite: 'Invitar',
  search: 'Buscar',
  selectAll_people: 'Seleccionar todo',
  selectAll_items: 'Seleccionar todo',
  selectNone_people: 'Seleccionar ninguno',
  selectNone_items: 'Seleccionar ninguno',
  clearAll_items: 'Borrar todo',
  clear: 'Borrar',
  uploading: 'Subiendo',
  noResults: 'No hay resultados',
  add: 'Agregar',
  update_verb: 'Actualizar',
  updating: 'Actualizando',
  submitting: 'Enviando',
  error: 'Error',
  goBack: 'Volver',
  confirm: 'Confirmar',
  select: 'Seleccionar',
  selectAll: 'Seleccionar todo',
  selected: 'Seleccionado',
  start_verb: 'Comenzar',
  finish_verb: 'Finalizar',
  followers: 'Seguidores',
  view_verb: 'Ver',
  download: 'Descargar',
  export: 'Exportar',
  downloadingStatus: {
    starting: 'Comenzando',
    fetching: 'Obteniendo',
    compiling: 'Compilando',
    generating: 'Generando',
    downloading: 'Descargando',
  },
  generatingLink: 'Generando enlace...',
  searchCompany: 'Buscar empresa',
  thereIsNoCompanies: 'No hay empresas',
  addCompany: 'Agregar empresa',
  locationClosure: 'Cierre de ubicación',
  ifDeliveryHasTheFollowingTags: 'Si la entrega tiene las siguientes etiquetas',
  ifEntityHasTheFollowingTags: (entityName: string) =>
    `Si ${entityName} tiene las siguientes etiquetas`,
  thenDoAction: 'Entonces hacer acción',
  toUserTeamRoleOrCompany: 'A usuario, equipo, rol o empresa',
  addRule: 'Agregar regla',
  shouldDeleteRule: 'Debe eliminar regla',
  tag: 'Etiqueta',
  searchForOrSelectTag: 'Buscar o seleccionar etiqueta',
  addTag: 'Agregar etiqueta',
  addUserTeamCompany: 'Agregar Usuario, Equipo, Empresa',
  tagCreation: 'Creación de etiquetas',
  deliveryStatus: 'Estado de la entrega',
  sendNotification: 'Enviar notificación',
  toExternalCalendar: 'Al calendario externo',
  notificationRules: 'Reglas de notificación',
  mention: 'Mencionar',
  gotToDirectory: 'Ir al directorio',
  goToProjectHome: 'Ir a la página principal del proyecto',

  daily: 'Diario',
  monSat: 'Lun-Sáb',
  d_daysShort: 'd',
  xSecondsAgoShort: (x: number) => `hace ${x}s`,
  xMinutesAgoShort: (x: number) => `hace ${x} min`,
  xHoursAgoShort: (x: number) => `hace ${x} h`,
  xDaysAgoShort: (x: number) => `hace ${x} d`,
  yesterday: 'Ayer',
  today: 'Hoy',
  tomorrow: 'Mañana',

  loggingIn: 'Iniciando sesión',
  loggingOut: 'Cerrando sesión',
  loginFailed: 'Error al iniciar sesión',
  backToLogin: 'Volver al inicio de sesión',
  processingLogin: 'Procesando inicio de sesión...',
  login: 'Iniciar sesión',
  logo: 'Logotipo',
  procoreLogin: 'Iniciar sesión con Procore',
  loginWithMicrosoft: 'Iniciar sesión con Microsoft',
  loginWithGoogle: 'Iniciar sesión con Google',
  tryAgain: 'Intentar de nuevo',
  contactAdminForAccess:
    'Si cree que debería tener acceso, comuníquese con su administrador.',
  ssoSessionExpired:
    'Su sesión de inicio de sesión ha expirado. Por favor, inténtelo de nuevo.',
  ssoProviderError:
    'No se pudo completar la autenticación con el proveedor. Por favor, inténtelo de nuevo.',
  ssoInvalidUser:
    'No se pudo recuperar la información de su cuenta. Por favor, inténtelo de nuevo.',
  ssoUserNotFound: 'Esta cuenta no está agregada a StruxHub.',
  ssoCodeExpired:
    'Su enlace de inicio de sesión ha expirado. Por favor, inicie sesión de nuevo.',
  ssoInternalError:
    'Ocurrió un error inesperado. Por favor, inténtelo más tarde.',
  ssoProviderNotAllowed:
    'Este método de inicio de sesión no está disponible para su organización.',
  ssoUseAllowedMethod:
    'Su organización requiere uno de los siguientes métodos de inicio de sesión:',
  ssoPersonalAccountsNotAllowed:
    'No se pueden usar cuentas personales para iniciar sesión. Utilice su cuenta de trabajo.',
  ssoInitiationFailed:
    'No se pudo iniciar sesión con este proveedor. Inténtelo de nuevo o utilice otro método de inicio de sesión.',
  ssoDomainRequiresMethods: (domain: string) =>
    `Los usuarios con @${domain} deben usar uno de los siguientes métodos de inicio de sesión:`,
  passwordLoginNotAvailableForDomain: (providers: string) =>
    `El inicio de sesión con correo electrónico y contraseña no está disponible para su dominio. Utilice: ${providers}`,
  emailAndPassword: 'Correo electrónico y contraseña',
  noColumnsDefined: 'No hay columnas definidas',
  changePassword: 'Cambiar contraseña',
  setPassword: 'Establecer contraseña',
  passwordWillBeUsedForAllSites:
    'Esta contraseña se utilizará para todos los sitios de StruxHub',
  setPasswordToSignBackIn:
    'Tu enlace de invitación es temporal. Una contraseña te permite volver a iniciar sesión. También puedes establecerla más tarde en tu perfil.',
  forgotPassword: '¿Olvidaste tu contraseña?',
  reset: 'Restablecer',
  resetPassword: 'Restablecer contraseña',
  logOut: 'Cerrar sesión',
  common: 'Común',

  hideAll: 'Ocultar todo',
  hideGroup: 'Ocultar grupo',
  hideObject: 'Ocultar objeto',

  showAll: 'Mostrar todo',
  showGroup: 'Mostrar grupo',
  showObject: 'Mostrar objeto',

  contactHelpAtMail: (email: string) => 'Contactar ayuda en ' + email,
  contactHelp: 'Contactar ayuda',

  userNotFound: 'Usuario no encontrado',
  noUserMatchingYourSearch: 'Ningún usuario coincide con tu búsqueda',
  allUsersAlreadyAssigned: 'Todos los usuarios ya asignados',
  noTagMatchingYourSearch: 'Ninguna etiqueta coincide con tu búsqueda',

  enterYourEmailBelowAndStruxHubWillSendAResetLink: `Ingrese su e-mail a continuación y ${brandName} enviará un enlace para restablecer`,
  pleaseEnterYourPasswordForTheExistingAccountToLinkDifferentProviders:
    'Ingrese su contraseña de la cuenta existente para vincular diferentes proveedores:',
  pleaseOpenTheLinkFromTheLetterAgain:
    'Por favor, abra el enlace de la carta otra vez',
  passwordResetLinkWillBeSentIfAccountExists:
    'Si existe una cuenta con este correo electrónico, recibirá un enlace para restablecer la contraseña',
  checkYourEmail: 'Revise su correo electrónico',
  passwordResetLinkSentIfAccountExists: (email: string) =>
    `Si existe una cuenta para ${email}, le hemos enviado un enlace para restablecer su contraseña. Puede tardar unos minutos.`,
  didNotGetItTryAnotherEmail:
    '¿No lo recibió? Pruebe con otro correo electrónico',
  passwordResetLinkInvalid:
    'Este enlace para restablecer la contraseña no es válido o ha caducado. Solicite uno nuevo.',
  invalidEmailError: 'La dirección de e-mail está mal formateada.',
  userNotFoundError:
    'No hay un registro de usuario correspondiente a este identificador. El usuario puede haber sido eliminado.',
  enterANewPasswordBelow: 'Ingrese una nueva contraseña a continuación',
  setYourPassword: 'Establezca su contraseña',
  tooManyUnsuccessfulLoginAttempts:
    'Demasiados intentos de inicio de sesión fallidos.',
  incorrectEmailOrPassword:
    'Correo electrónico o contraseña incorrectos. Usa «¿Olvidaste tu contraseña?» para restablecerla.',
  passwordErrors: {
    weakPassword:
      'Las contraseñas deben tener al menos 8 caracteres, mayúsculas y minúsculas' +
      ', un número y un carácter especial (!@#$%&)',
    doNotMatch: 'Las contraseñas no coinciden',
    empty: 'La contraseña no puede estar vacía',
    invalid: 'La contraseña no es válida o el usuario no tiene una contraseña',
  },
  authFieldErrors: {
    enterYourEmail: 'Ingrese su correo electrónico',
    enterValidEmail: 'Ingrese un correo electrónico válido',
    enterYourPassword: 'Ingrese su contraseña',
    enterNewPassword: 'Ingrese una nueva contraseña',
    enterNewPasswordAgain: 'Repita la contraseña',
    enterYourPhoneNumber: 'Ingrese su número de teléfono',
    enterValidPhoneNumber: 'Ingrese un número de teléfono válido',
    enterYourFirstName: 'Ingrese su nombre',
    enterYourLastName: 'Ingrese su apellido',
    enterAtLeastXCharacters: (x: number) => `Ingrese al menos ${x} caracteres`,
  },
  chat: 'Charlar',
  activities: 'Actividades',
  deliveries: 'Entregas',
  closures: 'Cierres',
  noActiveClosures: 'No hay cierres activos',
  activeClosuresHeading: 'Cierres activos:',
  logistics: 'Logística',
  logisticObject: 'Objeto logístico',
  site: 'Sitio',
  siteNews: 'Noticias de la obra',
  home: 'Inicio',
  hub: 'Hub',
  siteLogistics: 'Logística del sitio',
  materials: 'Materiales',
  materialsLegacy: 'Materiales (legacy)',
  photos: 'Fotos',
  analytics: 'Analíticas',
  analyticsSetting: 'Configuración de analíticas',
  showInReports: 'Mostrar en informes',
  dashboards: 'Tableros',
  widgetDescriptions: {
    workflowsCount:
      'Shows the total number of active workflows in the selected time period. This includes all workflows regardless of their status.',
    companiesCount:
      'Displays the count of unique companies involved in workflows during the selected period. Each company is counted once regardless of how many workflows they participate in.',
    usersCount:
      'Shows the number of unique users associated with workflows in the selected time range.',
    averageDuration:
      'Calculates the average number of days from workflow start date to completion date. Only includes workflows that have both start and end dates.',
    advancedPlanning:
      'Measures the average number of days between when a workflow was requested and when it actually started. Higher values indicate better advance planning.',
    equipmentBookingsWorkflows:
      'Counts how many workflows have equipment bookings attached to them during the selected period.',
    equipmentBookingsHours:
      'Shows the total hours of equipment bookings across all workflows in the selected time range.',
    locationsWorkflows:
      'Shows the distribution of workflows across different locations. Helps identify which locations have the most activity.',
    locationsByType:
      'Displays the breakdown of workflows by location type. Shows the percentage distribution across different workflow types.',
    workflowsByStatus:
      'Visualizes the distribution of workflows across different statuses (Requested, Submitted, Active, Changed, Ended). Helps understand the workflow pipeline.',
    activeVsCompleted:
      'Compares in-progress workflows (Requested, Submitted, Changed, Accepted, Reviewed, Active, OnSite, ToInspect) versus closed workflows (Done, Ended, Passed, Failed, Denied) in the selected period. Draft and deleted workflows are excluded.',
    workflowsByCategory:
      'Shows how workflows are distributed across different categories. Helps identify which workflow categories are most common.',
    workflowsTimeline:
      'Shows the number of workflows started over time. Useful for identifying trends and patterns in workflow creation.',
    recentWorkflows:
      'Table listing the most recent workflows with their status and dates. Provides quick access to the latest workflow activity.',
  },
  dashboard: {
    settings: 'Configuración del panel',
    settingsDescription:
      'Activa o desactiva los widgets. Los widgets están organizados por categoría para una mejor gestión del panel.',
    resetLayout: 'Restablecer diseño',
    widgetInfo: 'Información del widget',
    backToWidget: 'Volver al widget',
    downloadChart: 'Descargar gráfico',
    downloadAsPng: 'Descargar como PNG',
    downloadAsJpeg: 'Descargar como JPEG',
    noChange: 'Sin cambios',
    chartType: 'Tipo de gráfico',
    chartTypes: {
      pie: 'Gráfico circular',
      bar: 'Gráfico de barras',
      donut: 'Gráfico de anillo',
      line: 'Gráfico de líneas',
    },
    chartTypeNotSupported: 'Tipo de gráfico no compatible',
    noWorkflowsFound: 'No se encontraron flujos de trabajo para este período',
    workflowsCreated: 'Flujos de trabajo creados',
    retry: 'Reintentar',
    refresh: 'Actualizar',
    lastUpdated: 'Última actualización',
    justNow: 'ahora mismo',
    minutesAgo: (m: number) => `hace ${m}min`,
    hoursAgo: (h: number) => `hace ${h}h`,
    widgetTitles: {
      totalWorkflows: 'TOTAL DE FLUJOS DE TRABAJO',
      companies: 'EMPRESAS',
      usersEditors: 'USUARIOS',
      averageDuration: 'DURACIÓN PROMEDIO',
      advancedPlanning: 'PLANIFICACIÓN AVANZADA',
      workflowsTimeline: 'CRONOLOGÍA DE FLUJOS DE TRABAJO',
      workflowsByStatus: 'FLUJOS DE TRABAJO POR ESTADO',
      activeVsCompleted: 'ACTIVOS VS COMPLETADOS',
      workflowsByCategory: 'FLUJOS DE TRABAJO POR CATEGORÍA',
      recentWorkflows: 'FLUJOS DE TRABAJO RECIENTES',
      locationsWorkflows: 'UBICACIONES (FLUJOS DE TRABAJO)',
      locationsByType: 'UBICACIONES (POR TIPO)',
      equipmentBookings: 'RESERVAS DE EQUIPOS',
      equipmentHours: 'HORAS DE EQUIPOS',
    },
    widgetLabels: {
      activeWorkflowsInPeriod:
        'Flujos de trabajo activos en el período seleccionado',
      uniqueCompaniesInvolved: 'Empresas únicas involucradas',
      uniqueUsersEditing: 'Usuarios únicos en flujos de trabajo',
      daysFromStartToCompletion: 'Días desde el inicio hasta la finalización',
      timeRequestedBeforeStart: 'Tiempo solicitado antes de la fecha de inicio',
      workflowsWithEquipment: 'Flujos de trabajo con equipos',
      totalEquipmentBookingHours: 'Total de horas de reserva de equipos',
    },
    sections: {
      keyMetrics: {
        title: 'Métricas clave',
        description: 'Indicadores de rendimiento principales',
      },
      planningEfficiency: {
        title: 'Eficiencia de planificación',
        description: 'Métricas de planificación del trabajo',
      },
      workflowStatus: {
        title: 'Estado de flujos de trabajo',
        description: 'Estado actual y progreso',
      },
      workflowAnalytics: {
        title: 'Analítica de flujos de trabajo',
        description: 'Distribución y tendencias',
      },
      recentActivity: {
        title: 'Actividad reciente',
        description: 'Últimas actualizaciones de flujos de trabajo',
      },
      locationAnalytics: {
        title: 'Analítica de ubicaciones',
        description: 'Dónde se realiza el trabajo',
      },
      equipmentResources: {
        title: 'Equipos y recursos',
        description: 'Utilización de recursos',
      },
    },
  },
  reports: 'Informes',
  filedToGenerateReport: 'No se pudo generar un informe',
  objectProperties: 'Propiedades del objeto',
  formProperties: 'Propiedades del Formulario',
  createNewForm: 'Crear Nuevo Formulario',
  formNameLabel: 'Nombre del Formulario',
  untitled: 'Sin título',
  newFormButton: 'Nuevo Formulario',
  selectEquipmentForm: 'Seleccionar Formulario de Equipo',
  selectMaterialForm: 'Seleccionar Formulario de Material',
  selectLocationForm: 'Seleccionar Formulario de Ubicación',
  noFormsAvailable: 'No hay formularios disponibles',
  askAdminToCreateForm:
    'Pídale a un administrador que cree un formulario antes de agregar registros.',
  propertiesPlural: 'propiedades',
  createNewEquipment: 'Crear Nuevo Equipo',
  createNewMaterial: 'Crear Nuevo Material',
  createNewLocation: 'Crear Nueva Ubicación',
  createNewStruxTag: 'Crear Nuevo StruxTag',
  equipmentForm: 'Formulario de Equipo',
  materialForm: 'Formulario de Material',
  equipmentCreationForm: 'Formulario de creación de equipo',
  materialCreationForm: 'Formulario de creación de material',
  anyForm: 'Cualquier formulario',
  equipmentPropertiesLabel: 'Propiedades del Equipo',
  materialPropertiesLabel: 'Propiedades del Material',
  notifications: 'Notificaciones',
  userSettings: 'Configuración del usuario',
  projectSettings: 'Configuración del proyecto',
  projectSettingsOnDesktop:
    'Configuración del proyecto en la versión de escritorio',
  adminSettings: 'Configuraciones de Administración',
  settings: 'Configuraciones',
  pinSettingsSidebar:
    'Fijar la barra lateral para mostrarla en todas las páginas',
  unpinSettingsSidebar: 'Desfijar la barra lateral',
  messages: 'Mensajes',
  themeDark: 'Tema oscuro',
  themeLight: 'Tema claro',
  signIn: 'Iniciar sesión',
  signInWithSms: 'Iniciar sesión con SMS',
  signUp: 'Registrarse',
  signedInAs: 'Conectado como',
  account: 'Cuenta',
  profile: 'Perfil',
  editor: 'Editor',
  editProfile: 'Editar perfil',
  myPortal: 'Mi portal',
  portalSidebar: {
    projects: 'Proyectos',
    navigation: 'Navegación del portal',
    accountMenu: 'Menú de cuenta',
    editProfile: 'Editar perfil',
    openNavigation: 'Abrir navegación',
    closeNavigation: 'Cerrar navegación',
    expandSidebar: 'Expandir barra lateral',
    collapseSidebar: 'Contraer barra lateral',
    signOut: 'Cerrar sesión',
    reportABug: 'Reportar un error',
  },
  portalProjects: {
    view: 'Vista del proyecto',
    gridView: 'Vista de cuadrícula',
    mapView: 'Vista de mapa',
    welcomeBack: (name: string) => `Bienvenido de vuelta, ${name} 👋`,
    chooseProject: 'Elige un proyecto para comenzar',
    openingProject: (name: string) => `Abriendo ${name}…`,
    couldNotOpenProject: 'No se pudo abrir el proyecto',
    loadFailedFor: (tenants: string) =>
      `No se pudieron cargar los proyectos de ${tenants}.`,
    someProjectsMissing:
      'Algunos de tus proyectos pueden faltar en esta lista.',
    loadingProblem: 'Es un problema de carga, no una cuenta vacía.',
    loadingProjects: 'Cargando proyectos…',
    noProjectsMatchSearch: 'Ningún proyecto coincide con tu búsqueda.',
    noProjectsMatchFilters: 'Ningún proyecto coincide con tus filtros.',
    noProjectsLoaded: 'No se pudieron cargar los proyectos.',
    noProjectsYet: 'Aún no tienes ningún proyecto.',
    loadingMap: 'Cargando mapa…',
    mapUnavailable:
      'Mapa no disponible — no hay configuración de Mapbox en este host.',
    mapDescription:
      'Cada pin se encuentra en la ubicación real del Proyecto y lleva su logo — toca para abrir.',
    lastOpened: (when: string) => `Abierto por última vez ${when}`,
    daysAgo: (n: number) => `hace ${n} días`,
    oneWeekAgo: 'hace 1 semana',
    weeksAgo: (n: number) => `hace ${n} semanas`,
    loadingPortal: 'Cargando portal…',
    lastOpenedLabel: 'Último acceso',
    loadFailed:
      'No se pudieron cargar tus proyectos. Verifica tu conexión e inténtalo de nuevo.',
  },
  portalProfile: {
    profileSettings: 'Configuraci\u00f3n del perfil',
    personalData: 'Datos personales',
    accountData: 'Datos de la cuenta',
    profilePicture: 'Foto de perfil',
    upload: 'Subir',
    uploading: 'Subiendo\u2026',
    removing: 'Eliminando\u2026',
    firstName: 'Nombre',
    lastName: 'Apellido',
    jobTitle: 'Cargo',
    phoneNumber: 'N\u00famero de tel\u00e9fono',
    email: 'Correo electr\u00f3nico',
    password: 'Contrase\u00f1a',
    currentPassword: 'Contrase\u00f1a actual',
    newPassword: 'Nueva contrase\u00f1a',
    confirmPassword: 'Confirmar contrase\u00f1a',
    enterCurrentPassword: 'Ingrese la contrase\u00f1a actual',
    minimumCharacters: 'M\u00ednimo 8 caracteres',
    reEnterPassword: 'Reingrese la contrase\u00f1a',
    passwordsMismatch: 'Las contrase\u00f1as no coinciden.',
    change: 'Cambiar',
    updatePassword: 'Actualizar contrase\u00f1a',
    saveChanges: 'Guardar cambios',
    saving: 'Guardando\u2026',
    backToPortal: 'Volver al Portal',
    tenant: 'Inquilino',
    errorLoadProfile: 'No se pudo cargar tu perfil. Inténtalo de nuevo.',
    errorSaveProfile: 'No se pudieron guardar los cambios. Inténtalo de nuevo.',
    errorUploadAvatar: 'No se pudo subir la foto. Inténtalo de nuevo.',
    errorRemoveAvatar: 'No se pudo eliminar la foto. Inténtalo de nuevo.',
    errorChangePassword: 'Error al cambiar la contraseña.',
    currentPasswordIncorrect: 'La contraseña actual es incorrecta.',
    tooManyPasswordAttempts:
      'Demasiados intentos. Espera unos minutos e inténtalo de nuevo.',
    phoneOnlySignInMethod:
      'Tu número de teléfono es tu único método de inicio de sesión, por lo que no se puede eliminar.',
    phoneInUse: 'Este número de teléfono ya lo usa otra cuenta.',
    invalidPhone: 'Introduce un número de teléfono válido.',
    invalidProfile:
      'Algunos datos no son válidos. Revisa la información e inténtalo de nuevo.',
    firstNameRequired: 'El nombre es obligatorio.',
    setPassword: 'Establecer contraseña',
    projectCoverAlt: (name: string) => `Portada de ${name}`,
    struxHubVersion: (v: string) => `StruxHub versión: ${v}`,
    fileTooLarge: (maxMB: number) =>
      `El archivo es demasiado grande. El tamaño máximo es ${maxMB} MB.`,
    phoneNotVerifiedYet: 'Este número de teléfono aún no está verificado',
    verify: 'Verificar',
    remove: 'Eliminar',
    addPhoneNumber: 'Agregar número de teléfono',
    changePhoneNumber: 'Cambiar número de teléfono',
    newNumber: 'Número nuevo',
    codeSmsNotice:
      'Enviaremos un código de 6 dígitos por SMS a este número. Pueden aplicarse tarifas de mensajes y datos.',
    sendCode: 'Enviar código',
    sendingCode: 'Enviando\u2026',
    enterTheCode: 'Introduzca el código',
    verificationCode: 'Código de verificación',
    codeSentTo: (phone: string) => `Enviado a ${phone}.`,
    wrongNumber: '¿Número incorrecto?',
    edit: 'Editar',
    resendCodeIn: (time: string) => `Reenviar código en ${time}`,
    resendCode: 'Reenviar código',
    sendNewCode: 'Enviar un código nuevo',
    verifyingCode: 'Verificando\u2026',
    phoneNumberVerified: 'Número de teléfono verificado.',
    phoneAlreadyVerified: 'Este número ya está verificado.',
    removePhoneTitle: 'Eliminar número de teléfono',
    removePhoneQuestion: (phone: string) => `¿Eliminar ${phone} de su perfil?`,
    invalidCode: (triesLeft: number) =>
      `Ese código no es correcto. ${ending(triesLeft, 'Queda', 'Quedan')} ${triesLeft} ${ending(triesLeft, 'intento', 'intentos')}.`,
    codeExpired: 'Este código ha caducado. Solicite uno nuevo.',
    verificationPhoneInUse:
      'Este número ya está en uso por otra cuenta. Si es suyo, contacte con soporte.',
    invalidMobilePhone: 'Introduzca un número de móvil válido.',
    tooManyCodesRequested: (minutes: number) =>
      `Se solicitaron demasiados códigos. Inténtelo de nuevo en ${minutes} ${ending(minutes, 'minuto', 'minutos')}.`,
    tooManyCodesRequestedHours: (hours: number) =>
      `Se solicitaron demasiados códigos. Inténtelo de nuevo en ${hours} ${ending(hours, 'hora', 'horas')}.`,
    smsUnavailable:
      'No pudimos enviar un SMS en este momento. Inténtelo más tarde.',
    unsupportedDestination: 'Todavía no se admiten SMS a este país.',
    errorPhoneVerification:
      'No se pudo verificar su número de teléfono. Inténtelo de nuevo.',
    qrCode: 'Código QR',
    qrCodeDescription:
      'Usa este código en los escáneres de cualquiera de tus proyectos de StruxHub.',
  },
  portalProjectRequest: {
    title: 'Solicitud de nuevo proyecto',
    description:
      'Para crear un nuevo proyecto, envía una solicitud con el formulario a continuación',
    firstName: 'Nombre',
    lastName: 'Apellido',
    jobTitle: 'Cargo',
    workEmail: 'Correo del trabajo',
    phoneNumber: 'Número de teléfono',
    projectName: 'Nombre del proyecto',
    additionalDetails: 'Detalles adicionales',
    submit: 'Enviar',
    submitting: 'Enviando…',
    fieldRequired: 'Este campo es obligatorio.',
    invalidEmail: 'Introduce un correo electrónico válido.',
    successTitle: 'Solicitud enviada',
    successDescription:
      'Tu solicitud de proyecto se envió correctamente. La revisaremos y te contactaremos pronto.',
    errorSubmit: 'No se pudo enviar tu solicitud. Inténtalo de nuevo.',
    errorInvalid: 'Algunos datos no son válidos. Revisa el formulario.',
    errorTooManyRequests:
      'Demasiadas solicitudes. Espera un momento e inténtalo de nuevo.',
  },
  preferences: 'Preferencias',
  administration: 'Administración',
  projectDetails: 'Detalles del proyecto',
  integrations: 'Integraciones',
  userDirectory: 'Directorio de usuarios',
  team: 'Equipo',
  crew: 'Equipo',
  teams: 'Equipos',
  defaultTeams: 'Equipos predeterminados',
  presentations: 'Presentaciones',
  tracker: 'Rastreador',
  uploadSchedule: 'Programar carga',
  filterBy: 'Filtrar por',
  filterByMe: "Filtrar por 'Mis'",
  filterByMyCompany: "Filtrar por 'Mi empresa'",
  filter: 'Filtro',
  filters: 'Filtros',
  createNewFilter: 'Crear nuevo filtro',
  addScheduleData: 'Agregar datos de programación',
  editScheduleData: 'Editar datos de programación',
  struxhubObject: `Objeto ${brandName}`,
  selectObject: 'Seleccionar objeto',
  selectWorkForm: 'Seleccionar formulario de trabajo',
  hierarchies: 'Jerarquías',
  dataToSchedule: 'Datos para programar',
  searchUserOrGroup: 'Buscar usuario o grupo',
  tool: 'Herramienta',
  workflow: 'Workflow',
  workflowDetails: 'Detalles del Workflow',
  workflows: 'Workflows',
  inactiveWorkflowTypeTooltip:
    'Este tipo de workflow no está disponible para la creación.',
  workflowConfigurator: 'Configurador de workflow',
  manageItIn: 'Gestiónalo en el',
  manageInWorkflowConfiguratorOnDesktop:
    'Gestiónalo en el Configurador de workflow en la versión de escritorio',
  fields: 'Campos',
  submissions: 'Presentaciones',
  fieldsSelected: 'campos seleccionados',
  struxHubFields: 'Campos de StruxHub',
  primaryWorkflowFields: 'Principales',
  secondaryWorkflowFields: 'Secundarios',
  structureFields: 'Estructura',
  contentFields: 'Contenido',
  primaryWorkflowFieldsDescription:
    'Los Campos Primarios de Workflow definen la información esencial que define el registro y se utiliza en todo StruxHub para mostrar, administrar y controlar cada Workflow. Esto incluye detalles principales como Fecha y Hora, Ubicación, Empresa, Equipo y Material, junto con Usuarios de Workflow basados en roles como el Usuario Responsable (el propietario principal del Workflow), el Solicitante y Contactos Adicionales. Cada Campo Primario de Workflow se puede agregar una sola vez',
  secondaryWorkflowFieldsDescription:
    'Agrega contexto de apoyo y relaciones utilizadas en todo StruxHub para aclarar, organizar y complementar los detalles principales del Workflow',
  struxHubFieldsDescription:
    'Campos nativos de StruxHub que aparecen en toda la plataforma',
  structureFieldsDescription:
    'Elementos de diseño utilizados para organizar un Workflow',
  contentFieldsDescription:
    'Texto instructivo mostrado a los usuarios. No recopila datos',
  customFieldsDescription:
    'Campos de entrada genéricos para capturar datos no cubiertos por los Campos de StruxHub',
  goToStep: 'Ir al paso',
  goToWorkflow: 'Ir al Workflow',
  sitemapSetup: 'Configuración del mapa del sitio',
  templateUpdating: 'Actualización de la plantilla',
  selectExistingWorkflow: 'Seleccionar Workflow existente',
  createNewWorkflow: 'Crear nuevo Workflow',
  enterWorkflowType: 'Ingresar tipo de Workflow',
  basedOnWorkflow: 'Basado en',

  publishTo: 'Publicar en',
  geoReferencing: 'Georreferenciación',
  tags: 'Etiquetas',
  permittedCompanies: 'Empresas permitidas',
  notPermitted: 'No permitido',
  hoursOfOperation: 'Horas de operación',
  inheritedHoursDescription:
    'Heredado del horario laboral del proyecto. Gestiónelo en',
  inheritedClosuresDescription:
    'Heredado de los cierres del proyecto. Gestiónelo en',
  outsideOperatingHours: 'Fuera del horario de operación',
  dateClosures: 'Cierres de fechas',
  showInMap: 'Mostrar en los mapas',
  showInMapTooltip:
    'Mostrar en los mapas:\nActivado: el objeto de recurso se puede mostrar en las vistas de mapa y alternar allí entre visible y oculto.\nDesactivado: el objeto de recurso no está disponible en los mapas.',
  resourceShownInMaps:
    'El objeto de recurso se puede mostrar en las vistas de mapa y alternar allí entre visible y oculto.',
  resourceHiddenFromMaps:
    'El objeto de recurso no está disponible en los mapas.',
  workflowTypes: 'Tipos de workflow',
  workflowResource: 'WR',
  workflowResourceTooltip:
    'WR (Workflow Resource):\nActivado: el objeto de recurso se puede reservar y asignar a un Workflow.\nDesactivado: el objeto de recurso no está disponible para reservar.',
  workflowResourceBulk: 'Recurso de workflow',
  resourceNotAvailableForBooking:
    'El objeto de recurso no está disponible para reservar.',
  resourceAvailableForBooking:
    'El objeto de recurso se puede reservar y asignar a un Workflow.',
  maxBookingDuration: 'Duración máxima de reserva',
  maxOverlapping: 'Solapamiento máximo',
  maxFormBookingDurationDays: (duration, unit) =>
    `Max Booking Duration: La duración máxima de reserva para este Workflow es de ${duration} ${unit}`,
  objects: 'Objetos',
  image: 'Imagen',
  groupBy: 'Agrupar por',
  basemap: 'Mapa base',
  newView: 'Crear nueva vista',
  addView: 'Agregar vista',
  savedViews: 'Vistas guardadas',
  createNewView: 'Crear nueva vista',
  createNewWhiteboard: 'Crear nueva pizarra',
  mapFileUpload: {
    uploadMapFile: 'Subir archivo de mapa',
    uploadWhiteboard: 'Subir pizarra',
    replaceThisPlan: 'Reemplazar este plano',
    withThisOne: '... con este.',
    dragMapHere: 'Arrastre el mapa aquí',
    dragMapFileHere: 'Arrastre el archivo de mapa aquí',
    acceptedExtensions: (extensions: string) => `(se aceptan ${extensions})`,
    selectPage: 'Seleccionar página',
    planName: 'Nombre del plano',
    whiteboardName: 'Nombre de la pizarra',
    planUploaded: 'Plano subido',
    mapFileUploaded: 'Archivo de mapa subido',
    basemapUploaded: 'Mapa base subido',
    uploadingPlan: 'Subiendo plano',
    uploadingBasemap: 'Subiendo mapa base',
    tilesetLimits: (maxNumber: number, maxSize: string) =>
      `Puede subir hasta ${maxNumber} conjuntos de teselas de hasta ${maxSize} cada uno.`,
    tooManyTilesets: 'Ha subido demasiados conjuntos de teselas',
    fileTooLarge: 'Ha subido un archivo demasiado grande',
    tilesetNameTooLong: (maxLength: number) =>
      `Nombre del conjunto de teselas demasiado largo. Tamaño máximo: ${maxLength} caracteres`,
    invalidFileName:
      'El nombre del archivo solo puede contener letras, números y los símbolos “.”, “-” y “_”',
  },
  mapExport: {
    exportMap: 'Exportar mapa',
    output: 'Salida',
    map: 'Mapa',
    frameTag: 'Área exportada · arrastre el mapa para ajustar',
    backToSavedView: 'Restaurar el encuadre guardado',
    restoreViewportReason: 'Disponible después de mover o acercar el mapa.',
    framing: 'Encuadre',
    previewReduced: 'Se muestra más pequeño que el tamaño real',
    background: 'Fondo',
    blank: 'En blanco',
    basemapLabels: 'Etiquetas del mapa base',
    notOnBlank: 'No disponible en blanco',
    buildings3d: 'Edificios 3D',
    streetOnly: 'Solo Calle',
    angle: 'Ángulo',
    topDown: 'Cenital',
    tilted: 'Inclinada',
    toScale: 'A escala',
    notToScale: 'Sin escala',
    topDownBody: (deg: number) =>
      `Aplanada desde la inclinación guardada de ${deg}°, así que está a escala.`,
    tiltedBody: 'Una imagen, no un plano: sin escala gráfica ni referencias.',
    whatYouLose: 'Qué se pierde',
    tiltTradeOffsTitle: 'Una exportación inclinada es una imagen, no un plano',
    tiltTradeOffs: {
      noScale: 'Sin escala gráfica. No mida sobre ella.',
      mapLabelsOnly: 'Las etiquetas propias del mapa, sin referencias.',
      farSofter: 'La parte lejana sale menos nítida y más pequeña.',
      northCentreSlower:
        'La flecha del norte solo es exacta en el centro, y la exportación tarda más.',
      buildingsHide: 'Los edificios 3D pueden ocultar pines.',
      buildingsHideIcons: 'Los edificios 3D pueden ocultar iconos.',
    },
    format: 'Formato',
    png: 'PNG',
    pdf: 'PDF',
    useFor: 'Para',
    useForChoices: {
      print: 'Impresión',
      screen: 'Pantalla',
    },
    paperSize: 'Tamaño del papel',
    paperOption: (paper: string, size: string) => `${paper} · ${size}`,
    screenShape: 'Proporción',
    screenShapes: {
      '16x9': '16:9 Panorámico',
      '16x10': '16:10',
      '4x3': '4:3',
    },
    closestToMap: 'La más cercana a su mapa',
    tooLargeForDevice: 'Demasiado grande para este dispositivo',
    fileSize: (mb: number) => `Tamaño del archivo ~ ${mb} MB`,
    fileSizeRange: (low: number, high: number) =>
      `Tamaño del archivo ~ ${low}–${high} MB`,
    pdfSizeHint: (mb: string) => `Un PDF sería mucho más pequeño: ~ ${mb} MB.`,
    exportAs: (format: string) => `Exportar ${format}`,
    exporting: 'Exportando…',
    standardQuality: 'Estándar',
    highQuality: 'Alta',
    screenStandardHint: (width: number, height: number) =>
      `${width} × ${height} px: ideal para correo, chat y presentaciones.`,
    screenHighHint: (width: number, height: number) =>
      `${width} × ${height} px: se mantiene nítido al hacer zoom.`,
    screenHighExceedsDevice: 'Alta es demasiado grande para este dispositivo.',
    paperStandardHint: (dpi: number) =>
      `${dpi} ppp: ideal para impresoras de oficina.`,
    paperHighHint: (dpi: number) =>
      `${dpi} ppp: impresiones nítidas en gran formato.`,
    highQualityExceedsDevice:
      'Alta es demasiado grande aquí para este dispositivo.',
    screenImageryPointer: 'El satélite se ve más nítido en papel grande.',
    useSharpestPaper: (useFor: string, paper: string, quality: string) =>
      `Cambiar a ${useFor} · ${paper} · ${quality}`,
    letter: 'Carta',
    a4: 'A4',
    tabloid: 'Tabloide',
    a3: 'A3',
    a2: 'A2',
    a1: 'A1',
    archD: 'ARCH D',
    a0: 'A0',
    archA: 'ARCH A',
    archB: 'ARCH B',
    ansiC: 'ANSI C',
    archC: 'ARCH C',
    ansiD: 'ANSI D',
    archE1: 'ARCH E1',
    ansiE: 'ANSI E',
    archE: 'ARCH E',
    paperGroups: {
      imperial: 'ANSI y ARCH',
      metric: 'ISO',
    },
    moreSizes: (family: string) => `Más tamaños (${family})`,
    withFamiliarName: (standard: string, name: string) =>
      `${standard} (${name})`,
    orientation: 'Orientación',
    landscape: 'Horizontal',
    portrait: 'Vertical',
    millimetres: (width: number, height: number) => `${width} × ${height} mm`,
    inches: (width: number, height: number) =>
      `${width.toLocaleString('es')} × ${height.toLocaleString('es')} pulg.`,
    pageElements: 'Título y leyenda',
    pageElementsHint: 'Título, fecha, leyenda, escala y flecha del norte.',
    pageElementsHintTilted:
      'Título, fecha y flecha del norte, con la nota Sin escala.',
    northLetter: 'N',
    clusterLegend: 'Workflows',
    clusterLegendKeyed: 'Workflows · ver referencias',
    keyTagLegend: 'Ver referencias',
    truePositionLegend: 'Posición real',
    metres: (length: number) => `${length.toLocaleString('es')} m`,
    kilometres: (length: number) => `${length.toLocaleString('es')} km`,
    miles: (length: number) => `${length.toLocaleString('es')} mi`,
    mapFiles: 'Archivos de mapa',
    mapFileList: 'Lista de archivos de mapa',
    subLayersOf: (layer: string) => `Subcapas de ${layer}`,
    mapFilesOutsideFrame: (count: number) =>
      `${count} archivo${ending(count)} de mapa más de esta vista ${ending(count, 'está', 'están')} fuera del encuadre`,
    shapes: 'Formas',
    markers: 'Iconos',
    layerLabels: 'Etiquetas',
    pins: 'Pines',
    markups: 'Anotaciones',
    needsPins: 'Requiere pines',
    needsMarkups: 'Requiere anotaciones',
    readability: 'Legibilidad',
    sizing: 'Dimensionado',
    optimized: 'Optimizado',
    asOnMap: 'Como el mapa',
    readabilityHints: {
      optimized: 'Tamaño según el papel y la distancia, sin solapamientos.',
      asOnMap: 'Pines, iconos y etiquetas mantienen su tamaño del mapa.',
      asOnMapWithoutPins: 'Iconos y etiquetas mantienen su tamaño del mapa.',
      tilted:
        'Optimizado requiere vista cenital: se mantienen los tamaños del mapa.',
    },
    overallSize: 'Tamaño general',
    easyToReadFrom: 'Fácil de leer a',
    readZones: {
      inHand: 'En la mano o en la mesa',
      upClose: 'De cerca',
      acrossRoom: 'De lejos',
    },
    standardFor: (name: string) => `Estándar: ${name}`,
    standardAt: (name: string, distance: string) =>
      `Estándar ${name}: ${distance}`,
    aboutDistance: (distance: string) => `unos ${distance}`,
    readFromValueText: (distance: string, zone: string) =>
      `${distance}, ${zone}`,
    adjustByType: 'Ajustar por tipo',
    adjustByTypeNote: (baseline: string): string => `Base: ${baseline}`,
    adjustByTypeInfo: (baseline: string, isPrint: boolean): string =>
      isPrint
        ? `Cada tipo sigue a «${baseline}» hasta que fije su tamaño. Un tamaño fijado se mantiene hasta que lo restablezca.`
        : `Cada tipo es un porcentaje de «${baseline}» y crece con él.`,
    percent: (value: number) => `${value} %`,
    centimetres: (length: number) => `${length.toLocaleString('es')} cm`,
    feet: (length: number) =>
      `${length.toLocaleString('es')} ${length === 1 ? 'pie' : 'pies'}`,
    customized: 'Personalizado',
    textKinds: {
      workflowPin: 'Etiquetas de Workflows',
      location: 'Etiquetas de ubicaciones',
      markup: 'Etiquetas de anotación',
      markupText: 'Texto de anotación',
      key: 'Referencias y números',
    },
    points: (pt: number) => `${pt.toLocaleString('es')} pt`,
    autoSize: (size: string) => `Auto · ${size}`,
    resetRowToAuto: (row: string) => `Restablecer ${row} a Auto`,
    resetRowToDefault: (row: string) => `Restablecer ${row} al 100 %`,
    markupTextInfo:
      'Se ajusta con el zoom del mapa, como parte de él, así que no sigue la distancia de lectura.',
    atFloorHint: 'Ya es el tamaño más pequeño legible a esta distancia.',
    atFloorScreenHint: 'Ya es el tamaño más pequeño legible.',
    atFitLimitHint: 'El mayor tamaño que cabe en este papel.',
    atFitLimitScreenHint: 'El mayor tamaño que cabe en esta exportación.',
    needsLabelKey: 'Requiere Referencias',
    symbolKinds: {
      workflowPin: 'Pines de Workflows',
      location: 'Iconos de ubicaciones',
    },
    printedMillimetres: (length: number) => `${length.toLocaleString('es')} mm`,
    printedInches: (length: number) => `${length.toLocaleString('es')} pulg.`,
    key: 'Referencias',
    keyExplainer:
      'Las etiquetas que no caben reciben un número o una letra, listados en las referencias.',
    keyAllFit:
      'Todas las etiquetas caben en el mapa: no hacen falta referencias.',
    keyOffHint: (count: number) =>
      `Actívelo para listar ${count === 1 ? 'la etiqueta oculta' : `las ${count} etiquetas ocultas`} en el archivo.`,
    keyOnHint: {
      besideMap: () => 'Junto al mapa en el archivo',
      besidePage: (paper: string) =>
        `Junto a la página: el PNG es más ancho que ${paper}. Un PDF las pone en la página 2.`,
      pageTwo: () => 'En la página 2 del PDF',
    },
    keyViewList: 'Ver la lista',
    keySummaryTitle: (labels: number) =>
      labels === 1
        ? 'Dónde va la etiqueta'
        : `Dónde van las ${labels} etiquetas`,
    labelFates: {
      onMap: 'En el mapa',
      inKey: 'En las referencias',
      notListed: 'Sin listar, referencias llenas',
      asCounts: 'Solo el número, en discos negros',
      notShown: 'Omitidos',
    },
    markupLeftOff: 'Etiquetas de anotación omitidas',
    densityWarning: (isPaper: boolean) =>
      isPaper
        ? 'Demasiado denso: acerque el mapa, reduzca el texto o elija un papel mayor.'
        : 'Demasiado denso: acerque el mapa o reduzca el texto.',
    keyGroupHeader: (count: number) =>
      `${count} ${count === 1 ? 'Workflow' : 'Workflows'}`,
    keyGroupHeaderTrimmed: (count: number, listed: number) =>
      `${count} Workflows, ${listed} listados`,
    keyOverflowRow: (count: number) => `+${count} más, no listados`,
    previewKeyBadge: {
      besideMap: (labels: number) =>
        `Referencias junto al mapa · ${labels} ${labels === 1 ? 'etiqueta' : 'etiquetas'}`,
      besidePage: (labels: number) =>
        `Referencias junto a la página · ${labels} ${labels === 1 ? 'etiqueta' : 'etiquetas'}`,
      pageTwo: (labels: number) =>
        `Referencias en la página 2 · ${labels} ${labels === 1 ? 'etiqueta' : 'etiquetas'}`,
    },
    exportCancelled: 'Exportación cancelada.',
    shareMap: 'Compartir mapa',
    createImage: 'Crear imagen',
    createPdf: 'Crear PDF',
    mapReady: (details: string) => `Mapa listo · ${details}`,
    keepScreenOpen:
      'Mantenga esta pantalla abierta hasta que el archivo esté listo.',
    shareFailed: 'No se pudo compartir el archivo. Inténtelo de nuevo.',
    desktopExportHint: (tool: string) =>
      `Personalice papel, capas y etiquetas en ${tool} en el escritorio.`,
    phases: {
      preparing: 'Preparando…',
      loadingTiles: 'Cargando teselas…',
      placingLabels: 'Colocando etiquetas…',
      rendering: 'Renderizando…',
      encoding: 'Guardando archivo…',
    },
    errors: {
      tilesTimeout: 'El mapa tardó demasiado en cargar. Inténtelo de nuevo.',
      bufferClamped:
        'Este dispositivo no pudo renderizar el tamaño completo. Elija un tamaño menor.',
      zoomTooDeep: 'Aleje el zoom para exportar a este tamaño.',
      exceedsDevice:
        'Demasiado grande para este dispositivo. Elija un tamaño menor o la calidad Estándar.',
      exceedsDeviceScreen:
        'Demasiado grande para este dispositivo. Elija la calidad Estándar.',
      interrupted:
        'La ventana cambió de tamaño durante la exportación. Inténtelo de nuevo.',
      pageReloaded:
        'La página se recargó durante la exportación. Inténtelo de nuevo.',
      failed: 'La exportación falló. Inténtelo de nuevo.',
    },
    phoneErrors: {
      bufferClamped:
        'Este teléfono se quedó sin memoria para el mapa. Cierre otras aplicaciones e inténtelo de nuevo.',
      exceedsDevice: 'Demasiado grande para este teléfono.',
      exceedsDeviceScreen: 'Demasiado grande para este teléfono.',
      interrupted:
        'La pantalla giró o cambió de tamaño durante la exportación. Inténtelo de nuevo.',
      pageReloaded:
        'El teléfono se quedó sin memoria y recargó la página. Cierre otras aplicaciones e inténtelo de nuevo.',
    },
    shareMapFailed: 'No se pudo abrir Compartir mapa. Inténtelo de nuevo.',
    actualSize: 'Tamaño real',
    fitPage: 'Ajustar a la página',
    actualSizeHint: 'Arrastre para recorrer',
    previewViewMode: 'Tamaño de la vista previa',
    actualSizePanLabel:
      'Tamaño real · arrastre o use las flechas del teclado para recorrer, Mayús para pasos más largos',
    actualSizeTooltip:
      'Compruebe que las etiquetas se leen a tamaño real. No mueve el área exportada.',
    actualSizeTopDownOnly: 'Tamaño real · solo cenital',
    fitPageTooltip:
      'Vuelve a mostrar la página completa, con el encuadre anterior.',
    keepTabVisible:
      'Mantenga esta pestaña visible hasta que termine la exportación.',
  },
  newMapView: 'Nueva vista de mapa',
  notPublished: 'No publicado',
  applications: 'Aplicaciones',
  savedViewsDialog: {
    searchHint: 'Vistas de búsqueda',
    columns: {
      viewName: 'Ver nombre',
      filterCount: 'No. Filtros',
      access: 'Compartido en el proyecto',
      creator: 'Creado por',
    },
  },
  createNewTag: 'Crear nueva etiqueta',
  viewBasemap: 'Ver mapa base',
  viewBookings: 'Ver reservas',
  thereIsNoInstanceInTheSelectedCategory:
    'No hay ninguna instancia en la categoría seleccionada',

  directory: 'Directorio',
  name: 'Nombre',
  firstName: 'Nombre de pila',
  lastName: 'Apellido',
  phoneNumber: 'Teléfono',
  globalRole: 'Cargo',
  employeeIDNumber: 'Número de identificación del empleado',
  company: 'Empresa',
  companyAliasLabel: 'Empresa como informada por el usuario',
  companyOther: 'Otra',
  addXAsNewCompany: (name: string) => `Añadir "${name}" como nueva Empresa`,
  selectOrAddCompany: 'Seleccione o añada una Empresa',
  companyDirectory: 'Directorio de la empresa',
  myCompany: 'Mi empresa',
  myCo: 'Mi Co.',
  companyType: 'Tipo de empresa',
  classification: 'Clasificación',
  companyName: 'Nombre de la empresa',
  user: 'Usuario',
  users: 'Usuarios',
  userTags: 'Etiquetas',
  allUsers: 'Todos los usuarios',
  role: 'Rol',
  projectRole: 'Rol del proyecto',
  projectRoles: 'Roles del proyecto',
  projectTrades: 'Oficios del proyecto',
  address: 'Dirección',
  email_noun: 'E-mail',
  phone: 'Teléfono',
  phoneVerification: 'Verificación de teléfono',
  call_verb: 'Llamar',
  email_verb: 'E-mail',
  sendDirectMessageInStruxHub: `Enviar mensaje directo en ${brandName}`,
  password: 'Contraseña',
  currentPassword: 'Contraseña actual',
  newPassword: 'Nueva contraseña',
  confirmNewPassword: 'Confirmar nueva contraseña',
  confirmPassword: 'Confirmar contraseña',
  group: 'Grupo',
  reportsTo: 'Reporta a',
  reportOf: (type: string) => `Informe de ${type}`,
  createReport: 'Crear informe',
  deliveryReport: 'Informe de entrega',
  formReport: 'Informe de Workflow',
  materialReport: 'Informe de material',
  aReportWillBeGeneratedWithTheAboveFilters:
    'Se generará un informe con los filtros anteriores',
  aReportWillBeSentToYourEmailShortly:
    'Se enviará un informe a su e-mail en breve',
  reportDetails: 'Detalles del informe',
  workflowReportTitle: 'Informe de Workflow',
  deliveryReportTitle: 'Informe de entrega',
  scanStationReportTitle: 'Informe de estación de escaneo',
  resourceDetails: 'Detalles del recurso',
  officeField: 'Oficina/Campo',
  accountType: 'Tipo de cuenta',
  status: 'Estado',
  manufacturer: 'Fabricante',
  fuel_type: 'Tipo de combustible',
  bid_package: 'Paquete de licitación',
  assembly: 'Ensamblaje',
  isDockMaster: 'Es Dock Master',
  isInspector: 'Es inspector',
  invalidUser: 'Usuario inválido',
  unknownUser: 'Usuario desconocido',
  unknownDeletedObject: 'Desconocido (Objeto eliminado)',
  ruleProblems: {
    incomplete: 'La regla está incompleta y no se puede guardar.',
    unknownCondition:
      'Esta etiqueta apunta a algo que ya no existe. Elimínela o vuelva a elegirla.',
  },
  inviteStatus: 'Estado de la invitación',
  inviteStatusValues: {
    Not_Invited: 'No invitado',
    Pending: 'Pendiente',
    Accepted: 'Aceptado',
    // service
    Not_Added: 'No agregado',
    Added: 'Agregado',
    Requested: 'Solicitado',
    Not_Requested: 'No solicitado',
  },
  accountPosition: 'Posición de la cuenta',
  accountPositions: {
    office: 'Oficina',
    field: 'Campo',
    management: 'Gestión',
  },
  fieldMapping: 'Mapeo de campos',
  projectAccessTypeValues: {
    admin: 'Administrador',
    member: 'Miembro',
    owner: 'Propietario',
  },
  modalInfoSections: {
    personalInformation: 'Información personal',
    companyInformation: 'Información de la empresa',
    projectInformation: 'Información del proyecto',
    additionalInformation: 'Información adicional',
    workflowPermissions: 'Permisos de Workflow',
    companyContactInfo: 'Información de contacto de la empresa',
    legalInfo: 'Información legal',
    companyAddress: 'Dirección de la empresa',
    users: 'Usuarios',
    qrCodeAccessAndBadges: 'Acceso a códigos QR y credenciales',
    globalInfo: `Información global (nivel ${brandShortName})`,
  },
  projectWorkflows: {
    deliveryBooking: 'Reserva de entrega',
    deliveryInspection: 'Inspección de entrega',
    scheduleComment: 'Comentario del horario',
    formsMaster: 'Maestro de Workflows',
    scanMaster: 'Maestro de escaneo',
    viewSchedule: 'Ver horario',
    scheduleUpdating: 'Actualizar horario',
    scheduleUploading: 'Subir horario',
    scheduleNotes: 'Notas sobre el horario',
  },
  permissions: {
    none: 'Ninguno',
    readOnly: 'Solo lectura',
    standard: 'Standard',
    master: 'Master',
  },
  userProfileRoleNames: {
    admin: 'Administrador del sitio',
    owner: 'Propietario',
    docMaster: 'Maestro de documentos',
    inspector: 'Inspector',
    presentationUser: 'Usuario de presentación',
    formsMaster: 'Maestro de Workflows',
    scheduleViewing: 'Ver horario',
    scheduleUpdating: 'Actualizar horario',
    scheduleUploading: 'Subir horario',
    scheduleNotes: 'Notas sobre el horario',
  },
  companyTypes: {
    GeneralContractorPrimary: 'Contratista general (Primario)',
    GeneralContractorSecondary: 'Contratista general (Secundario)',
    Owner: 'Propietario',
    SubContractor: 'Subcontratista',
    TieredSubContractor: 'Subcontratista escalonado',
  },
  saveProfile: 'Guardar perfil',
  notificationType: 'Tipo de notificación',
  notificationSettings: 'Configuración de notificaciones',
  deliveryNotifications: 'Notificaciones de entrega',
  scheduleNotifications: 'Notificaciones de horario',
  trackerNotifications: 'Notificaciones de seguimiento',
  logisticsNotifications: 'Notificaciones de logística',
  formNotifications: 'Notificaciones de Workflow',
  scannerNotifications: 'Notificaciones del escáner',
  iWouldLikeReceiveMy: 'Me gustaría recibir mi',
  inApp: 'En la aplicación',
  sms: 'SMS',
  bySms: 'Por SMS',
  byEmail: 'Por e-mail',
  successfullyUpdated: 'Actualizado con éxito',
  wrongPhoneNumberFormat: 'Formato de número de teléfono incorrecto',
  userProfileDescriptions: {
    name: `Su nombre se utilizará cuando se comunique con otros miembros del equipo en ${brandName}.`,
    projectRoles:
      'Su función en el proyecto se utiliza para asegurar que las notificaciones y los Workflows en los que está involucrado sean pertinentes. ' +
      'Es posible una mayor personalización en Preferencias en Configuración.',
    notifications:
      `Las aplicaciones ${brandName} le enviarán notificaciones para el trabajo del que es miembro del equipo. ` +
      'Estas notificaciones aparecen en la aplicación junto al icono de la campana en la parte superior derecha de la página, ' +
      'si ha ingresado su número de teléfono en su perfil a través de mensajes de SMS enviados a su teléfono, ' +
      'y algunos resúmenes de notificaciones pueden ser enviados a su dirección de e-mail. ' +
      'Puede desmarcar cualquiera de estas opciones a continuación para dejar de recibir notificaciones por ese método.',
  },
  whitelistedDomains: 'Dominios en la lista blanca',
  projectAdminSummary: 'Resumen del administrador del proyecto',
  lastUpdated: 'Última actualización',
  dateByName: (date: string, name: string) => date + ' por ' + name,
  uploadProjectSchedule: 'Subir horario del proyecto',
  fileUploaded: 'Archivo subido: ',
  totalActivities: 'Actividades totales',
  totalUsers: 'Usuarios totales',
  uploadProcurementPlan: 'Subir plan de adquisiciones',
  bpAssignedToCompanies: (amount: number, totalAmount: number) =>
    amount +
    ' de ' +
    totalAmount +
    ' paquetes de licitación asignados a empresas.',
  downloadCurrentValuesAsCsv: (toLink: (aText: string) => any) => [
    toLink('Descargar'),
    ' los valores actuales como archivo csv.',
  ],
  uploadTeamList: 'Subir la lista del equipo',
  currentTeamMembers: (amount: number) =>
    amount + ' miembros actuales del equipo.',
  emptyTagTypes: (tags: TagType[]) =>
    `Algunas de las etiquetas de tipo necesarias (${tags
      .map(tag => getBandTitleByTagType(tag))
      .toString()}) están vacías para algunas empresas. Se establecerán valores por defecto para ellas.`,
  adminSummaryDescriptions: {
    projectDetails:
      'Defina el nombre y la ubicación de su proyecto para que pueda encontrarlo fácilmente en su selector de proyectos',
    projectSchedule: `Suba su Programa Maestro actualizado desde P6 tan frecuentemente como desee. No se preocupe, no perderá ningún dato en ${brandName}.`,
    procurementPlan:
      'Suba su plan de adquisiciones actualizado a medida que incorpore más subcontratistas al Proyecto y refine sus valores esperados para cada paquete de licitación.',
    teamList:
      'Suba la lista del equipo de su Proyecto a medida que se unan nuevos miembros al Proyecto. Incluir información opcional como la afiliación interna del Departamento/Equipo facilita las asignaciones de recursos a las Actividades.',
  },
  project: 'Proyecto',
  xProjects,
  projectSingularPlural: 'Proyecto(s)',
  notificationsRulesSetupDescriptions: {
    helperText1: (entityType: string) =>
      'Una regla enviará notificaciones a un conjunto de usuarios para todos los futuros ' +
      entityType +
      ' con un conjunto de etiquetas designadas',
    helperText2:
      'Las etiquetas se pueden usar solas o combinadas con operadores booleanos. Por ejemplo:',
  },
  selectProject: 'Seleccionar Proyecto',
  selectProjectToSync: 'Seleccionar Proyecto(s) para Sincronizar',
  selectBookingTime: 'Seleccionar hora de reserva',
  newProject: 'Nuevo Proyecto',
  projectName: 'Nombre del Proyecto',
  projectCode: 'Código del Proyecto',
  projectLocation: 'Ubicación del Proyecto',
  projectLogo: 'Logotipo del Proyecto',
  projectHeaderImage: 'Encabezado del Proyecto',
  streetAddress: 'Dirección',
  city: 'Ciudad',
  state_region: 'Estado',
  zipCode: 'Código Postal',
  country: 'País',
  projectTimeSettings: 'Configuración de tiempo y calendario del proyecto',
  QRCodes: 'Códigos QR',
  xQRCode: (name: string) => `Código QR de ${name}`,
  scanStations: 'Estaciones de escaneo',
  projectAdmins: 'Administradores del Proyecto',
  setTheTimeZoneForThisProject: 'Establecer la zona horaria para este proyecto',
  dateFormat: 'Formato de fecha',
  maturixProjectName: 'Nombre del Proyecto Maturix',
  maturixTemperatureUnit: 'Unidad de temperatura Maturix',
  maturixStrengthUnit: 'Unidad de resistencia Maturix',
  maturixStations: 'Estaciones Maturix',
  undefinedSection: 'Sección indefinida',
  setTheDateTimeFormatForThisProject:
    'Establecer el formato de fecha y hora para este proyecto',
  localTimezone: 'Zona horaria local',
  us: 'EE. UU.',
  international: 'Internacional',
  customProjectClosures: 'Cierres de Proyecto personalizados',
  addCustomProjectClosure: 'Agregar cierre de proyecto personalizado',
  forTheFuture: 'Para el Futuro',
  projectType: 'Tipo de Proyecto',
  apps: 'Aplicaciones',
  admins: 'Administradores',
  theListOfProjectsAdministrators: 'La lista de administradores del proyecto',
  addNew: 'Agregar nuevo',
  addNewValue: 'Agregar',
  addNewAdmin: 'Agregar nuevo Administrador',
  addNewContact: 'Agregar nuevo Contacto',
  defaultWorkingHours: 'Horas de trabajo predeterminadas',
  timeZone: 'Zona horaria',
  everyday: 'Todos los días',
  holidays: 'Días festivos',

  struxHubHome: `Inicio de ${brandName}`,
  struxHubForms: `Workflows de ${brandName}`,
  app: 'Aplicación',
  manageApps: 'Gestionar aplicaciones',
  defaultPage: 'Página predeterminada',
  appsVisibilityInfo: 'Mostrar u ocultar aplicaciones en la navegación.',
  defaultPageInfo:
    'Selecciona qué aplicación sirve como página de inicio cuando los usuarios vuelven al producto.',
  projectDetailsDescriptions: {
    projectNameError:
      'El nombre no debe estar vacío y debe contener solo letras, números, espacios, _, -',
    projectCodeError:
      'El código del proyecto no puede estar vacío y no debe contener caracteres especiales. La longitud máxima para el código del proyecto es de 5 caracteres',

    changeProjectLocationWarning:
      'La Zona Horaria del Proyecto ha sido configurada de manera diferente ' +
      'a la zona horaria en la Ubicación de su Proyecto',
  },
  integrationsSetupDescriptions: {
    invalidToken: 'Token Inválido',
  },
  procoreDescription: 'Software de gestión de construcción',
  loginToProcore: 'Iniciar sesión en Procore',
  procoreLoginDescription:
    'Para configurar esta integración necesitas estar conectado a la cuenta de Procore.',
  procoreNoCompaniesAccess:
    'No tienes acceso a ninguna empresa de Procore. Comunícate con el administrador de tu empresa.',
  procoreNoAccessibleProjects:
    'No tienes ningún proyecto accesible en esta empresa. Comunícate con el administrador de tu empresa.',
  procoreConnectionFailed:
    'No se pudo conectar con Procore. Inténtalo de nuevo o comunícate con el administrador de tu empresa.',
  procoreUsersWithoutContact: (names: string[]) =>
    `No se puede agregar a ${names.join(', ')}: ${names.length === 1 ? 'el usuario no tiene' : 'los usuarios no tienen'} correo electrónico ni número de teléfono. Comunícate con el administrador del proyecto en Procore para obtener acceso al directorio.`,
  integrationEnabled: 'Integración habilitada',
  integrationDisabled: 'Integración deshabilitada',
  deliveriesFieldMapping: 'Mapeo de campos de entregas',
  workflowsFieldMapping: 'Mapeo de campos de flujo de trabajo',
  selectWorkflowTypeFirst:
    'Por favor, seleccione primero el tipo de flujo de trabajo',
  deleteWorkflowMapping: 'Eliminar mapeo de flujo de trabajo',
  synchronizationEnabled: 'Sincronización habilitada',
  synchronizationDisabled: 'Sincronización deshabilitada',
  connecting: 'Conectando...',
  loggedIn: 'Conectado',
  loginToEnableIntegration:
    'Inicie sesión en Procore para habilitar la integración',
  loginToDisableIntegration:
    'Inicie sesión en Procore para deshabilitar la integración',
  procoreLoginSuccess: 'Inicio de sesión exitoso en la cuenta de Procore',
  procoreIntegrationEnabled: 'Integración con Procore habilitada',

  procoreFieldMappings: {
    struxhubDeliveryLog: 'Registro de Entregas Struxhub',
    procoreDeliveryLog: 'Registro de Entregas Procore',
    startTimeOfBookingDate: 'Hora de inicio de la fecha de reserva',
    time: 'Hora',
    requesterCompany: 'Empresa solicitante',
    deliveryFrom: 'Entrega desde',
    bookingId: 'ID de reserva',
    trackingNumber: 'Número de seguimiento',
    vendorVendorEmailsMaterialsDeliveryNote:
      'Proveedor, correos electrónicos del proveedor, materiales, nota de entrega',
    contents: 'Contenido',
    notesFromDeliveryLog: 'Notas del registro de entregas',
    comments: 'Comentarios',
    attachedFilesFromDeliveryLog: 'Archivos adjuntos del registro de entregas',
    attachments: 'Adjuntos',
  },

  countryValues: countries,
  timezoneValues: timezones,
  timezoneWithOffsets: (name: string, offsets: string[]) =>
    `${name} (GMT ${offsets.join('/')})`,

  addCustomProjectClosures: 'Añadir Cierres de Proyecto Personalizados',
  closureName: 'Nombre del Cierre',
  closureStartDate: 'Fecha de Inicio del Cierre',
  closureEndDate: 'Fecha de Finalización del Cierre',

  loadingTeamMembers: 'Cargando Miembros del Equipo',
  loadingMonitoringData: 'Cargando Datos de Monitoreo',
  xTeamMembersSelected: (x: number) =>
    `${x} miembro${ending(x)} del equipo seleccionado${ending(x)}`,
  xCompaniesSelected: (x: number) =>
    `${x} compan${ending(x, 'ía', 'ías')} seleccionada${ending(x)}`,
  importExportTeamMembers: 'Importar/Exportar miembros del equipo',
  teamMembersDropdownStates: {
    downloadTeamMembers: 'Descargar usuarios (.csv)',
    uploadTeamMembers: 'Subir usuarios (.csv)',
    viewCompanyDirectory: 'Ver directorio de la empresa',
    connectToProcore: 'Conectar a Procore',
  },
  selectNotInvited: 'Seleccionar No Invitado',
  selectStatusRequested: 'Seleccionar ESTADO Solicitado',
  setNotInvited: 'Establecer como No Invitado',
  addNewProjectMember: 'Añadir nuevo miembro del proyecto',
  uploadYourUpdatedProjectMemberListCsvFile:
    'Suba su lista actualizada de miembros del proyecto en formato csv.',
  orDragYourFileHere: 'O arrastre su archivo aquí.',
  uploadYourScheduleFileInP6XerOrExcelXlsxXlsmFormat:
    'Suba su archivo de Programación en P6 (.xer) o Excel (.xlsx, .xlsm).',
  sendInviteLinkToUser: 'Enviar Enlace de Invitación al Usuario',
  addUser: 'Añadir Usuario',
  byUsers: 'Por Usuarios',
  byWorkflowField: 'Por Campo de Flujo',
  byCompany: 'Por empresa',
  bicUsers: 'Usuarios BIC',
  editUsers: (x: number) => `Editar Usuario${ending(x)}`,
  userValidationErrors: {
    firstNameRequired: 'Se requiere el primer nombre',
    lastNameRequired: 'Se requiere el apellido',
    emailRequired: 'Se requiere e-mail',
    invalidEmail: 'E-mail no válido',
    invalidPhone: 'Número de teléfono no válido',
    emailIsTaken: 'El e-mail ya ha sido tomado por otro miembro del proyecto',
    phoneIsTaken: 'El teléfono ya ha sido tomado por otro miembro del proyecto',
    memberWithSuchEmailIsAlreadyAdded:
      'Un usuario con este e-mail ya ha sido añadido al proyecto',
    memberWithSuchPhoneNumberIsAlreadyAdded:
      'Un usuario con este número de teléfono ya ha sido añadido al proyecto',
    companyRequired: 'Se requiere la empresa',
  },

  bulkSaveErrors: {
    invalidEmail: 'Dirección de e-mail no válida',
    invalidPhone: 'Número de teléfono no válido',
    duplicateRow: 'Duplica una fila anterior de este lote',
    missingName: 'Se requieren el nombre y el apellido',
    identifierConflict:
      'El e-mail y el teléfono pertenecen a usuarios existentes diferentes',
    authDenied: 'El registro fue rechazado por el servicio de autenticación',
    authUnavailable:
      'El servicio de autenticación no está disponible — inténtelo de nuevo más tarde',
    rowProcessingFailed:
      'La fila no se pudo procesar — contacte con soporte si persiste',
    rowError: (row: number | string, message: string) =>
      `Fila ${row}: ${message}`,
    namedRowError: (name: string, message: string) => `${name}: ${message}`,
    andXMoreRows: (x: number) => `…y ${x} fila${ending(x)} más.`,
    xRowsFailedToSave: (x: number) =>
      `${x} fila${ending(x)} no se ${x === 1 ? 'pudo' : 'pudieron'} guardar`,
    unknownError: 'Error desconocido',
    xMembersImported: (x: number) =>
      `${x} miembro${ending(x)} importado${ending(x)}`,
  },

  companyValidationErrors: {
    nameRequired: 'Se requiere el nombre de la empresa',
    emailRequired: 'Se requiere el e-mail de la empresa',
    invalidEmail: 'E-mail no válido',
    invalidPhone: 'Número de teléfono de la empresa no válido',
    emailIsTaken: 'El e-mail ya ha sido tomado por otra empresa',
    somethingFilledIncorrectly: 'Algo está llenado incorrectamente',
    companyTypeIsRequired: 'Se requiere el tipo de empresa',
    codeRequired: 'Se requiere el código',
    codeIncorrectLength: 'La longitud del código debe ser entre 3 y 5 símbolos',
    codeIsNotUnique:
      'Una empresa con este código ya existe, cambie el valor del campo de código para hacerlo único',
  },
  companyLinkingErrors: {
    anErrorOccurredWhileLinking: 'Se produjo un error al vincular',
    anErrorOccurredWhileUnlinking: 'Se produjo un error al desvincular',
    anErrorOccurredWhileFetchingGlobalStorage:
      'Se produjo un error al obtener el almacenamiento global',
  },
  companyMerging: {
    confirmation: (sourceCompaniesText: string, targetCompanyText: string) =>
      `¿Confirma la fusión de ${sourceCompaniesText} en ${targetCompanyText}`,
  },

  somethingWentWrongDuringAPIInteraction:
    'Algo salió mal durante la interacción con la API',

  signUpLinkInvalid:
    'Este enlace de registro está incompleto. Utilice el código QR de su invitación al proyecto.',

  confirmDeactivationOfTheFollowingXCompanies: (x: number) =>
    `Confirmar la desactivación de la${ending(x)} siguiente${ending(
      x,
    )} ${x} compan${ending(x, 'ía', 'ías')}?`,
  confirmActivationOfTheFollowingXCompanies: (x: number) =>
    `Confirmar la activación de la${ending(x)} siguiente${ending(
      x,
    )} ${x} compan${ending(x, 'ía', 'ías')}?`,
  confirmDeletionOfTheFollowingXProjectMembers: (x: number) =>
    `Confirmar la eliminación de${(ending(x), 'l', ' los')} siguiente${ending(
      x,
    )} ${x} miembro${ending(x)} del proyecto?`,
  confirmInvitationOfTheFollowingXProjectMembers: (x: number) =>
    `Confirmar la invitación de${(ending(x), 'l', ' los')} siguiente${ending(
      x,
    )} ${x} miembro${ending(x)} del proyecto?`,
  confirmSettingAsNotInvitedOfTheFollowingXProjectMembers: (x: number) =>
    `Confirmar la configuración como no invitado de${(ending(x), 'l', ' los')} siguiente${ending(x)} ${x} miembro${ending(x)} del proyecto?`,
  confirmInvitation: 'Confirmar Invitación',
  confirmSettingUsersAsNotInvited:
    'Confirmar configuración de usuarios como no invitados',
  confirmDeletion: 'Confirmar eliminación',
  confirmActivation: 'Confirmar activación',
  confirmDeactivation: 'Confirmar Desactivación',
  confirmDeletionOfTheFollowingXPlannedManpower: (x: number) =>
    `Confirmar la eliminación de la${ending(
      x,
    )} siguiente ${x} mano de obra planificada?`,
  confirmDeletionOfTheFollowingXBidPackages: (x: number) =>
    `Confirmar la eliminación de${ending(x, 'l', ' los')} siguiente${ending(
      x,
    )} ${x} paquete${ending(x)} de licitación?`,
  confirmDeletionOfActivities: (x: number) =>
    `¿Está seguro de que desea eliminar ${x} ${activities(x)}?`,
  confirmDeletionOfEquipment: (x: number) =>
    `¿Está seguro de que desea eliminar el equipo seleccionado?\n\n${x} ${equipment(
      x,
    )} será${ending(x, '', 'n')} eliminado${ending(x, '', 's')}.`,
  confirmDeletionOfSensors: (x: number) =>
    `¿Está seguro de que desea eliminar los sensores seleccionados?\n\n${x} sensor${ending(
      x,
      '',
      'es',
    )} será${ending(x, '', 'n')} eliminado${ending(x, '', 's')}.`,
  confirmDeletionOfLocations: (x: number) =>
    `¿Está seguro de que desea eliminar las ubicaciones seleccionadas?\n\n${x} ubicación${ending(
      x,
      '',
      'es',
    )} será${ending(x, '', 'n')} eliminada${ending(x, '', 's')}.`,
  confirmDeletionOfStruxTags: (x: number) =>
    `¿Está seguro de que desea eliminar los StruxTags seleccionados?\n\n${x} ${struxTagsCount(
      x,
    )} será${ending(x, '', 'n')} eliminado${ending(x, '', 's')}.`,
  confirmDeletionOfMaterials: (x: number) =>
    `¿Está seguro de que desea eliminar los materiales seleccionados?\n\n${x} ${materials(
      x,
    )} será${ending(x, '', 'n')} eliminado${ending(x, '', 's')}.`,
  confirmDeletionOfResources: (x: number, countLabel: string) =>
    `¿Está seguro de que desea eliminar los ${countLabel} seleccionados?\n\n${x} ${countLabel} será${ending(
      x,
      '',
      'n',
    )} eliminado${ending(x, '', 's')}.`,
  resourceCountLabel: (resourceTypeId: string, x: number) => {
    switch (resourceTypeId) {
      case 'offloading_equipment':
        return equipment(x)
      case 'sensor':
        return sensors(x)
      case 'material':
        return materials(x)
      default:
        return `elemento${ending(x)}`
    }
  },
  confirmApprovalOfTheFollowingXProjectMembers: (x: number) =>
    `Confirmar la aprobación de ${x} pedido${ending(x)} a ${brandName}?`,
  confirmApproval: 'Confirmar Apprbación',
  selectProcoreProject: 'Seleccionar proyecto Procore',
  selectProcoreCompany: 'Seleccionar empresa Procore',
  noUnit: 'Sin unidad',
  noCompanies: 'Sin empresas',
  noDirectoryAccess: 'Sin acceso al directorio',
  xUsers,
  xUsersShowing: (x: number) => `${x} usuario${ending(x)} mostrando`,
  xUsersCount: (x: number) => `${x} Usuario${ending(x)}`,
  xTeams: (x: number) => `${x} equipo${ending(x)}`,
  addXUsers: (x: number) => {
    if (!x) {
      return 'Añadir'
    }
    return `Añadir ${x} usuario${ending(x)}`
  },
  xFiles,
  xFields,
  xFieldsAdded,

  files: 'Archivos',
  multiLevelScreen: 'Pantalla Multi-Nivel',
  levelsViewsWithZoneSummary: 'Vistas de niveles con resumen de zona',
  levelsViewsWithCompanySummary: 'Vistas de niveles con resumen de empresa',
  gantt: 'Gantt',
  presentationMode: 'Modo de presentación',
  play: 'Reproducir',
  stop: 'Detener',
  turnOn: 'Encender',
  presentationScreens: 'Pantallas de presentación',
  xMinutes: (x: number) => `${x} minuto${ending(x)}`,
  order_noun: 'Orden',
  order_number: (x: number) => {
    return `${x}º`
  },
  display: 'Mostrar',
  presentationModeDescriptions: {
    multiLevelScreen:
      'Muestra la actividad y el rendimiento de la mano de obra real frente a lo planificado ' +
      'para todos los niveles del edificio. Se desplaza para mostrar todos los niveles',
    levelsViewsWithZoneSummary:
      'Recorre cada nivel del edificio mostrando ' +
      'el rendimiento agregado de la actividad y la mano de obra para cada zona. ' +
      'Se desplaza para mostrar los datos de cada zona.',
    levelsViewsWithCompanySummary:
      'Recorre cada nivel del edificio mostrando ' +
      'el rendimiento agregado de la empresa. Se desplaza para mostrar los datos de cada empresa.',
    gantt:
      'Muestra la vista gantt del proyecto para la anticipación actual. ' +
      'Se desplaza para mostrar todas las actividades.',
    deliveries:
      'Muestra el calendario de entregas semanal para la semana actual.',
    logistics: 'Muestra el mapa logístico.',
    confirmText:
      'El modo de presentación te cerrará la sesión y configurará este dispositivo para la reproducción. Para ' +
      'salir del modo de presentación, vaya a Configuración. Deberá volver a iniciar sesión una vez que ' +
      'salga del modo de presentación.',
    header:
      'Activar el modo de presentación cerrará tu sesión y registrará este dispositivo en ' +
      'un modo de presentación solamente.',
    periodOfInactivity:
      'La presentación se ejecutará automáticamente después de un período de inactividad de',
    stop:
      'Detener el modo de presentación te llevará de vuelta a una pantalla de inicio de sesión ' +
      'donde podrás acceder a tus proyectos.',
  },
  include: 'Incluir',
  saveConfiguration: 'Guardar configuración',
  preTaskChecklist: 'Lista de verificación previa a la tarea',
  requireDailyInspections: 'Requerir inspecciones diarias',
  inspectionFrequency: 'Frecuencia de inspección',
  deadLineForRecordingAnInspection:
    'Fecha límite para registrar una inspección',
  checklist: 'Lista de verificación',
  instructions: 'Instrucciones',
  xOptions: (x: number) => `${x} opción${ending(x, '', 'es')}`,
  NA: 'N/A',
  remove: 'Eliminar',
  typeOfList: 'Tipo de lista',
  addAdditionalItem: 'Agregar ítem adicional',
  enterInstruction: 'Ingresar instrucción',
  enterTheXDigitCodeSentTo: (x: number) =>
    `Ingrese el código de ${x} dígitos enviado a`,
  areYouSureWantToDeleteInstructionItem:
    '¿Está seguro de que desea eliminar el ítem de instrucción?',
  instructionListTypes: {
    enumerated: 'Enumerado',
    bulleted: 'Con viñetas',
    none: 'Ninguno',
  },
  fieldStates: {
    mandatory: 'Obligatorio',
    optional: 'Opcional',
    hidden: 'Oculto',
  },

  revertingProjectData: 'Revertir datos del proyecto',
  loadingUpdatingProject: 'Cargando/Actualizando proyecto',
  downloadProjectDataCsv: 'Descargar datos del proyecto .csv',
  exploreYourScheduleInStruxHub: `Explora tu calendario en ${brandName}`,
  scheduleIsNotUploaded: 'El calendario no está subido',
  gettingXerProjects: 'Obteniendo proyectos xer',
  gettingExcelSheets: 'Obteniendo hojas de Excel',
  parsingTheScheduleFileStarted:
    'Comenzó el análisis del archivo de calendario',
  readingScheduleFile: 'Leyendo archivo de calendario',
  extractingScheduleData: 'Extrayendo datos del calendario',
  xEntriesInTheFile: (x: number) =>
    `${x} entr${ending(x, 'ada', 'adas')} en el archivo`,
  savingScheduleDataCollections:
    'Guardando colecciones de datos del calendario',
  xOfYEntriesSaved: (x: number, y: number) =>
    `${x} de ${y} entrada${ending(y)} guardada${ending(x)}`,
  recalculatingStatusUpdates: 'Recalculando actualizaciones de estado',
  savingSchedule: 'Guardando calendario',
  uploadingScheduleFailedAt: 'La subida del calendario falló en',
  uploadingScheduleDone: 'Subida del calendario realizada',
  seeGanttInStruxHub: `Ver Gantt en ${brandName}`,
  noActivitiesFound: 'No se encontraron actividades',
  noPropertiesFoundForForm:
    'No se encontraron propiedades para este formulario',
  noPropertiesMatchSearch: 'Ninguna propiedad coincide con tu búsqueda',
  continueToNextStep: 'Continuar al siguiente paso',
  thereAreNoProjectsInProvidedFile:
    'No hay proyectos en el archivo proporcionado',
  thereAreNoInstancesToDisplay: (instancesName: string = 'Instancias') =>
    `No hay ${instancesName.toLowerCase()} para mostrar`,
  scheduleUpdateSummary: 'Resumen de actualización de calendario',
  current: 'Actual',
  upload_noun: 'Subida',
  fileName: 'Nombre del archivo',
  dateUploaded: 'Fecha de subida',
  uploadScheduleDescriptions: {
    warning: (delta: number) =>
      `Las ${delta} ${activities(delta)} ` +
      `que ${ending(delta, 'está', 'están')} ausente${ending(
        delta,
      )} en el calendario actualizado ` +
      `serán eliminada${ending(
        delta,
      )} de la vista. Todo el contenido asociado con ` +
      `est${ending(delta, 'a', 'as')} ${delta} ` +
      `${activities(delta)} será eliminado ` +
      `de ${brandName} también.`,
    chooseProject:
      'El archivo P6 xer actual contiene datos de múltiples proyectos. ' +
      'Por favor, seleccione el proyecto para importar. Los datos en este proyecto deben ' +
      'corresponder a los datos contenidos en otras fuentes ' +
      '(por ejemplo, Plan de Compras, lista de miembros del equipo).',
    chooseSheet:
      'Los archivos actuales contienen múltiples hojas. ' +
      'Por favor, seleccione la hoja para importar.',
    mergeData: 'Actualizar calendario actual.',
    mapFields: 'Por favor, seleccione qué campo se utilizará para cada columna',
  },
  activityId: 'ID de Actividad',
  activityUniqueId: 'ID Único de Actividad',
  activityName: 'Nombre de la Actividad',
  activityCode: 'Código de la Actividad',
  activityStatus: 'Estado de la Actividad',
  activityPercentComplete: 'Porcentaje de la Actividad Completada',
  plannedActivityStart: 'Inicio Planeado de la Actividad',
  plannedActivityFinish: 'Finalización Planeada de la Actividad',
  actualActivityStart: 'Inicio Real de la Actividad',
  actualActivityFinish: 'Finalización Real de la Actividad',
  resourceId: 'ID del Recurso',
  resourceName: 'Nombre del Recurso',
  trade: 'Oficio',
  assignee: 'Asignado',
  primarySafetyHazard: 'Peligro Principal de Seguridad',
  secondarySafetyHazard: 'Peligro Secundario de Seguridad',
  categoryOfVariance: 'Categoría de Variación',
  categoryOfVarianceOpened: 'Categoría de Variación Abierta',
  activityCodeTypes: 'Tipos de Códigos de Actividad',
  resources: 'Recursos',
  uploadAnother: 'Subir Otro',
  next: 'Siguiente',
  saveAndViewUpdatedGantt: 'Guardar y Ver Gantt Actualizado',
  lastUpload: 'Última subida',
  companies: 'Empresas',
  targetCompany: 'Empresa objetivo',
  activitiesWithTheFollowingActivityCodesAreExcluded:
    'Las actividades con los siguientes códigos de actividad están excluidas',
  activityCodesSelected: 'Códigos de actividad seleccionados',
  scheduleRestoringConfirmation: 'Confirmación de Restauración de Calendario',
  doYouConfirmRestoringFileFromDate: (file: string, wrappedDate) => [
    `¿Confirma la restauración de ${file} desde `,
    wrappedDate,
    '?',
  ],
  errorSendingOneTimeCode:
    'Error al enviar SMS. Por favor, inténtelo de nuevo. Si el problema persiste, póngase en contacto con un administrador.',
  noNonsenseInOneTimeCodeResponse:
    'Número de teléfono no encontrado. Por favor, ingrese su número de teléfono completo con código de área.',
  redirectingYouTo: (x: string) => `Redirigiéndote a ${x}`,
  doYouWantToSaveChanges: '¿Quieres guardar los cambios?',
  doYouAlreadyHaveAnAccount: '¿Ya tienes una cuenta de usuario?',
  uploadHistory: 'Historial de Subidas',
  restore: 'Restaurar',
  chooseTenant: 'Elegir Inquilino',
  chooseAnotherTenant: 'Elegir otro inquilino',
  unableToReachTenant: 'No se puede acceder al inquilino',
  couldNotOpenTenant: (tenant: string) =>
    `No pudimos abrir ${tenant}. Es posible que no tenga acceso o que la solicitud haya excedido el tiempo de espera. Elija otro inquilino o cierre sesión e inténtelo de nuevo.`,
  theSelectedTenant: 'el inquilino seleccionado',
  chooseProject: 'Elegir Proyecto',
  chooseSheet: 'Elegir Hoja',
  mapFields: 'Mapear Campos',
  xActivities: (x: number) => `${x} ${activities(x)}`,
  xHours,
  locationsWithDeletedParent: (x: number): string =>
    x === 1
      ? '1 ubicación perdió su padre LBS'
      : `${x} ubicaciones perdieron su padre LBS`,
  locationsWithDeletedParentDescription: (x: number): string =>
    x === 1
      ? 'Su padre LBS se eliminó antes de octubre de 2026, por lo que no aparece en la barra lateral de mapas. Revísela para eliminarla o asignarle un nuevo padre.'
      : 'Su padre LBS se eliminó antes de octubre de 2026, por lo que no aparecen en la barra lateral de mapas. Revíselas para eliminar cada una o asignarle un nuevo padre.',
  reviewLocations: (x: number): string =>
    x === 1 ? 'Revisar 1 ubicación' : `Revisar ${x} ubicaciones`,
  shownInTableBelow: 'Se muestran en la tabla de abajo',
  xLocations: (x: number) => `${x} Ubicación${ending(x, '', 'es')}`,
  xObjectsHere: (x: number) => `${x} objeto${ending(x)} aquí`,
  xObjectsAtThisSpot: (x: number, names: string[]) =>
    `${x} objeto${ending(x)} en este lugar: ${names.join(', ')}`,
  workflowsHereX: (x: number) => `Flujos de trabajo aquí (${x})`,
  xAttachments: (x: number) => `${x} Anexo${ending(x, '', 's')}`,
  xColumns: (x: number) => `${x} columna${ending(x)}`,
  cancelUpload: 'Cancelar Subida',
  selectProjectMembersLinkedToX: (x: string, isPlural: boolean) =>
    `Seleccionar miembro${isPlural ? 's' : ''} del proyecto vinculado${
      isPlural ? 's' : ''
    } a ${x}`,
  selectActivityCodesLinkedToX: (x: string, isPlural: boolean) =>
    `Seleccionar códigos de actividad vinculado${isPlural ? 's' : ''} a ${x}`,
  selectionOf1ProjectMemberIsPermitted:
    'Se permite la selección de 1 miembro del proyecto.',
  selectionOf1ActivityCodeIsPermitted:
    'Se permite la selección de 1 código de actividad.',
  multipleSelectionsArePermitted: 'Se permiten múltiples selecciones.',
  excludeSectionsOfTheScheduleOptional:
    'Excluir secciones del calendario (opcional)',
  activityFilterDialogDescriptions: {
    hiddenCodes:
      'Si desea ocultar ciertas secciones de su calendario, ' +
      'marque los códigos de actividad que correspondan a las actividades que desea ocultar.',
  },
  only: 'Solo',
  noCompany: 'Sin empresa',
  noMessage: 'Sin mensaje',
  submit_verb: 'Enviar',
  showAllXActivities: (x: number) => `Mostrar todas las ${x} ${activities(x)}`,
  showXOfYActivities: (x: number, y: number) =>
    `Mostrar ${x} de ${y} ${activities(x)}`,
  verification: 'Verificación',
  xActivitiesAdded: (x: number, wrappedX) => [
    wrappedX,
    ` ${activities(x)} añadidas.`,
  ],
  seeDetails: 'Ver detalles',
  xTypeActivityCodesAdded: (x: number, wrappedX, type: string) => [
    wrappedX,
    `Código${ending(x)} de actividad ${type} añadido${ending(x)}.`,
  ],

  activityFilters: 'Filtros de actividad',
  scheduleFilters: 'Filtros de horario',
  customFilter: 'Filtro personalizado',
  filterNameCannotBeEmpty: 'El nombre del filtro no puede estar vacío',
  suchFilterNameIsAlreadyTaken: 'Ese nombre de filtro ya está en uso',
  struxHubData: `Datos de ${brandName}`,
  scheduleData: 'Datos de horario',
  members: 'Miembros',
  projectMembers: 'Miembros del proyecto',
  activityCodes: 'Códigos de actividad',
  projectMembersSelected: 'Miembros del proyecto seleccionados',
  renameFilterOptional: 'Renombrar filtro (opcional)',
  filterName: 'Nombre del filtro',
  xCompaniesIdentified: (x: number) =>
    `${x} Empresa${ending(x)} identificada${ending(x)}`,
  xCompaniesShowing: (x: number) => `Mostrando ${x} empresa${ending(x)}`,
  xCompaniesCount: (x: number) => `${x} Empresa${ending(x)}`,
  xCompanies: (x: number) => `${x} empresa${ending(x)}`,
  activityCodeSelected: 'Código de actividad seleccionado',
  responsibleUser: 'Usuario responsable',
  responsibilityCode: 'Código de responsabilidad',
  responsibilityDescription: 'Descripción de la responsabilidad',
  contractN: 'Contrato #',
  plannedValueS: 'Valor planificado ($)',
  uploadAnUpdatedXlsxFile: 'Subir un archivo xlsx actualizado',
  downloadCurrentListAsXlsxFile: 'Descargar la lista actual como archivo xlsx',
  addNewEntry: 'Añadir nueva entrada',
  addNewUser: 'Añadir nuevo usuario',
  previous: 'Anterior',
  newUserCreatedViaSignUp: 'Nuevo usuario registrado',
  newUserAwaitingApproval: 'Esperando aprobación',
  activityFiltersDescriptions: {
    confirmDeletionOfX: (x: string) =>
      `¿Está seguro de que desea eliminar el filtro ${x}?`,
    title: `${brandName} transforma sus códigos de actividad y datos P6 en filtros convenientes para que sus equipos encuentren fácilmente sus actividades en la web y en dispositivos móviles. Puede configurar estos filtros a continuación y actualizarlos en cualquier momento.`,
    selectProjectMembers:
      'Seleccione el (los) miembro(s) del proyecto que desea incluir en este filtro. Se permiten selecciones múltiples.',
    selectActivityCodes:
      'Seleccione el (los) código(s) de actividad que desea incluir en este filtro. Se permiten selecciones múltiples.',
    company:
      'Este filtro permite a los usuarios seleccionar las empresas responsables de las actividades en su proyecto.',
    level:
      'Este filtro permite a los usuarios seleccionar los niveles de elevación de su edificio.',
    zone: 'Este filtro permite a los usuarios seleccionar las diferentes zonas de cada nivel del edificio.',
    activityCode:
      'Este filtro permite a los usuarios seleccionar códigos adicionales de actividades que desea rastrear.',
    projectMember:
      'Este filtro permite a los usuarios seleccionar los miembros del proyecto que desea rastrear.',
    associateCompanies:
      'Por favor indique cómo está asociando Empresas a sus actividades en el horario.',
    resourceCompanies:
      'Los nombres de las empresas están en el campo Recurso en el archivo .xer. Nota: este es el valor predeterminado si tiene un horario cargado de recursos.',
    bpCompanies:
      'Los nombres de las empresas están en un archivo xlsx separado y asociados con Actividades a través de un código de actividad.',
    bpStep1:
      'Seleccione el tipo de código de actividad en su archivo .xer que esté asociado con las empresas responsables.',
    bpStep2:
      'Suba un archivo de excel que contenga al menos los siguientes 2 encabezados de columna: Código de responsabilidad y Compañía.',
  },
  trackingHierarchy: 'Jerarquía de seguimiento',
  defaultHierarchies: 'Jerarquías predeterminadas',
  defineBandsEnPermissionsForYourTrackingHierarchy:
    'Defina las bandas y permisos para su Jerarquía de seguimiento',
  setupDefaultHierarchiesForDifferentViews:
    'Configure las jerarquías predeterminadas para diferentes vistas',
  selectBands: 'Seleccionar bandas',
  selectControlBand: 'Seleccionar banda de control',
  band: 'Banda',
  addBand: 'Añadir Banda',
  activityUpdatesOn: 'Actualizaciones de actividad activadas',
  activityUpdatesOff: 'Actualizaciones de actividad desactivadas',
  view_noun: 'Vista',
  wbs: 'WBS', // work breakdown structure
  webGantt: 'Web Gantt',
  webSitemap: 'Mapa del sitio web',
  mobile: 'Móvil',
  table: 'Tabla',
  tablet: 'Tableta',
  hierarchyPreview: 'Vista previa de jerarquía',
  unassigned: 'No asignado',
  photo: 'Foto',
  completed: 'Completado',
  startEarly: 'Comenzar temprano',
  PComplete: '% Completado',
  unassignedActivity: 'Actividad no asignada',
  trackingHierarchyDescriptions: {
    title:
      'Configure su Jerarquía de seguimiento a continuación para comenzar a seguir su cronograma por parte de sus Equipos de campo, por parte de sus Subcontratistas o por ambos.',
  },

  editCompany: 'Editar empresa',
  addDataToSchedule: 'Agregar datos al cronograma',
  plannedCompanyManpower: 'Mano de obra planificada de la empresa',
  dataToScheduleDescriptions: {
    title: `Con ${brandName}, puede agregar rápidamente datos a su cronograma importando datos en archivos de Excel`,
  },

  formField: 'Campo del formulario',
  showField_question: '¿Mostrar campo?',
  mandatory_question: '¿Obligatorio?',
  fieldShown: 'Campo mostrado',
  mandatory: 'Obligatorio',
  addNewListElement: 'Añadir nuevo elemento de la lista',
  workflowPreview: 'Vista previa del Workflow',

  setThresholdForDDays: 'Establecer umbral para ∆ Días',
  setThresholdForDManpower: 'Establecer umbral para ∆ Mano de obra',
  onTrack: 'En curso',
  watch: 'Vigilar',
  behind: 'Atrasado',
  dDays: '∆ Días',
  days: 'Días',
  percent: 'Por ciento',
  number: 'Número',
  daysBehind: 'Días Atrasados',
  daysAhead: 'Días Adelantados',
  XBehind: '% Atrasados',
  XAhead: '% Adelantados',
  under: 'Bajo',
  over: 'Sobre',
  XUnder: '% Bajo',
  XOver: '% Sobre',
  dManpower: '∆ Mano de Obra',
  sitemapsDescriptions: {
    title:
      `Con ${brandName}, puedes vincular fácilmente tu Programación con tus mapas del sitio. ` +
      'A continuación, puedes configurar los valores umbral para las métricas de ∆ Días y ' +
      '∆ Mano de Obra que se utilizan para visualizar el progreso en la Programación y la Mano de Obra actual, respectivamente.',
    configure:
      'Configurar Umbrales de Estado para la Programación y Mano de Obra',
  },

  inbox: 'Buzón',
  unread_2form: 'No Leído',
  read_2form: 'Leído',
  readStatus: 'Estado de notificación',
  archived: 'Archivado',
  xArchived: (x: number) => `${x} archivado${x === 1 ? '' : 's'}`,
  xMarkedAsRead: (x: number) =>
    `${x} marcado${x === 1 ? '' : 's'} como leído${x === 1 ? '' : 's'}`,
  xMarkedAsUnread: (x: number) =>
    `${x} marcado${x === 1 ? '' : 's'} como no leído${x === 1 ? '' : 's'}`,
  xUnarchived: (x: number) => `${x} desarchivado${x === 1 ? '' : 's'}`,
  requested: 'Solicitado',
  changed: 'Cambiado',
  scheduled: 'Programado',
  onSite: 'En Sitio',
  denied: 'Denegado',
  passedInspection: 'Inspección Aprobada',
  failedInspection: 'Inspección Fallida',
  done_plural: 'Hecho',
  flags: 'Banderas',
  workflowSubmitted: 'Workflow enviado',
  workflowApproved: 'Workflow aprobado',
  workflowDone: 'Workflow completado',
  workflowChanged: 'Workflow cambiado',
  workflowDenied: 'Workflow denegado',
  workflowReadyToInspect: 'Workflow listo para inspeccionar',
  workflowActivated: 'Workflow activado',
  workflowReviewed: 'Workflow revisado',
  workflowOnSite: 'Workflow en el sitio',
  workflowFailed: 'Workflow fallido',
  workflowPassed: 'Workflow aprobado',
  workflowDeleted: 'Workflow eliminado',
  workflowEnded: 'Workflow terminado',
  workflowFollowed: 'Workflow seguido',
  rfis: 'RFIs',
  scheduleComments: 'Comentarios de la Programación',
  onTime: 'A Tiempo',
  late: 'Atrasado',
  performance: 'Rendimiento',
  parameters: 'Parámetros',
  onTimeWindowHr: 'Ventana de A Tiempo (hr)',
  showLess: 'Mostrar Menos',
  showMore: 'Mostrar Más',
  all_items: 'Todo',
  my_documents: 'Mis Documentos',
  my_company: 'Mi Empresa',
  maps: 'Mapas',
  none: 'Ninguno',
  all: 'Todos',
  important: 'Importante',
  importantTabTooltip:
    'Notificaciones que requieren tu atención o acción — incluye ball-in-court, comunicación de sitio y @-menciones.',
  updates: 'Actualizaciones',
  updatesTabTooltip:
    'Actualizaciones de workflow no marcadas como Importantes — solo informativas.',
  batteryLevel: 'Nivel de batería',
  struxTagState: 'Estado de StruxTag',
  sensorStateInUseMoving: 'En movimiento',
  sensorStateInUseIdle: 'En reposo',
  sensorStateNoSignal: 'Sin señal',
  noStruxTagLinked: 'No hay StruxTag vinculado',
  noActivitySessions: 'No hay sesiones de actividad',
  activitySessionMoving: 'En movimiento',
  activitySessionIdle: 'En reposo',
  activitySessionOpen: 'Abierta',
  activitySessionClosed: 'Cerrada',
  unArchive: 'Desarchivar',
  archive: 'Archivar',
  leaveConversation: 'Salir de la conversación',
  archiveConversation: 'Archivar la conversación',
  unarchiveConversation: 'Desarchivar la conversación',
  conversationIsArchived: 'Esta conversación está archivada',
  photoMessage: '📷 Foto',
  thisWorkflowIsInProject: (projectName: string) =>
    `Este Workflow está en ${projectName}`,
  leaveConversationConfirmation:
    '¿Está seguro de que desea salir de esta conversación? Perderá el acceso a ella.',
  archiveConversationConfirmation:
    '¿Está seguro de que desea archivar esta conversación? Se ocultará para todos los miembros y quedará en solo lectura.',
  conversation: 'Conversación',
  reconnecting: 'Reconectando…',
  noMessagesYet: 'Aún no hay mensajes',
  noMessagesYetSayHello: 'Aún no hay mensajes — salude…',
  couldNotLoadYourInbox: 'No se pudo cargar su bandeja de entrada',
  couldNotLoadMessages: 'No se pudieron cargar los mensajes',
  couldNotOpenConversation:
    'No se pudo abrir la conversación — verifique su conexión',
  noArchivedConversations: 'No hay conversaciones archivadas',
  noConversationsFound: 'No se encontraron conversaciones',
  inboxIsEmpty: 'La bandeja de entrada está vacía',
  xMembersYOnline: (members: number, online: number) =>
    `${members} miembros, ${online} en línea`,
  xMembers: (x: number) => `${x} miembro${ending(x)}`,
  failedToLeave: 'No se pudo salir',
  leave_verb: 'Salir',
  message_noun: 'Mensaje',
  youCanAttachUpToXPhotos: (x: number) =>
    `Puede adjuntar hasta ${x} fotos a un mensaje.`,
  xFilesCouldNotBeAttached: (x: number) =>
    x > 1
      ? `No se pudieron adjuntar ${x} archivos — solo imágenes de hasta 15 MB.`
      : `No se pudo adjuntar 1 archivo — solo imágenes de hasta 15 MB.`,
  notSentTapToRetry: 'No enviado — toque para reintentar',
  newChat: 'Nuevo chat',
  searchUser: 'Buscar usuario',
  newGroup: 'Nuevo grupo',
  noPeopleFound: 'No se encontraron personas',
  addMembers: 'Agregar miembros',
  groupDetails: 'Detalles del grupo',
  userDetails: 'Detalles del usuario',
  groupName: 'Nombre del grupo',
  createdByX: (name: string) => `Creado por ${name}`,
  you: 'Usted',
  couldNotCreateGroup: 'No se pudo crear el grupo — inténtelo de nuevo',
  couldNotUpdateGroup: 'No se pudo actualizar el grupo — inténtelo de nuevo',
  read: 'Leer',
  unread: 'No Leído',
  wwpColoring: 'Coloreado WWP',
  useCSIMaterialCategories: 'Usar Categorías de Material CSI', // Construction Specifications Institute
  noNotificationsPassedFilters:
    'No hay Notificaciones que hayan pasado los filtros',
  replyToActivityFlag: 'Responder a la Bandera de Actividad',
  activityFlag: 'Bandera de Actividad',
  replyToActivityRfi: 'Responder al RFI de Actividad',
  activityRfi: 'RFI de Actividad',
  replyToScheduleComment: 'Responder al Comentario de Programación',
  replyToCategoryOfVariance: 'Responder a la Categoría de Varianza',
  replyToSafetyHazard: 'Responder a Riesgo de Seguridad',
  scheduleComment: 'Comentario de Programación',
  activityUpdate: 'Actualización de Actividad',
  scheduleUpdate: 'Actualización de Programación',
  safetyHazard: 'Riesgo de Seguridad',
  safetyHazardOpened: 'Riesgo de Seguridad Abierto',
  bulk: 'En masa',
  bulkEdit: 'Edición en Masa',
  bulkUpdate: 'Actualización en Masa',
  bulkEditConfirmQuestion: (fieldCount: number, recordCount: number) =>
    `Actualizar ${xFields(fieldCount)} para ${recordCount} registro${ending(
      recordCount,
    )}?`,
  replyToActivityUpdate: 'Responder a Actualización de Actividad',
  activityFollowed: 'Actividad Seguida',
  deliveryFollowed: 'Entrega Seguida',
  replyToScheduleUpdate: 'Responder a Actualización de Programación',
  replyToBulkUpdate: 'Responder a Actualización en Masa',
  deliveryRequested: 'Entrega solicitada',
  deliveryRequestedByLocationClosure: (id: string) =>
    `La Entrega (${id}) ha sido afectada por un Cierre de Ubicación y debe ser reprogramada`,
  deliveryScheduled: 'Entrega programada',
  deliveryOnSite: 'Entrega en sitio',
  deliveryPassedInspection: 'Entrega aprobada en inspección',
  deliveryFailedInspection: 'Entrega fallida en inspección',
  deliveryInspected: 'Entrega Inspeccionada',
  deliveryDone: 'Entrega realizada',
  deliveryIncompletelyDone: 'Entrega incompletamente realizada',
  deliveryUpdated: 'Entrega actualizada',
  deliveryDeleted: 'Entrega eliminada',
  deliveryDeletedBy: (id: string, user: string, company: string) =>
    `La Entrega (${id}) ha sido eliminada por ${user} en ${company}`,
  deliveryDenied: 'Entrega denegada',
  deliveryCanceled: 'Entrega cancelada',
  deliveryOnHold: 'Entrega en espera',
  deliveryPaused: 'Entrega pausada',
  updateMaterials: 'Actualizar materiales',
  materialsLoading: 'Los materiales se están cargando',
  xMaterials: (count: number) => `${count} Material${count > 1 ? 'es' : ''}`,
  xFilesShowing: (count: number) => `${count} Archivos se están mostrando`,
  planned: 'Planificado',
  actual: 'Real',
  scannerDeactivated: 'Escáner desactivado',
  scheduleUpdated: 'Horario actualizado',
  passed: 'Aprobado',
  failed: 'Fallido',
  scheduling: 'Programación',
  schedule: 'Horario',
  schedule_noun: 'Horario',
  pass_verb: 'Aprobar',
  deny_verb: 'Denegar',
  change_verb: 'Cambiar',
  accept: 'Aceptar',
  fail: 'Fallar',
  incompletelyDone: 'Incompletamente hecho',
  deleted: 'Eliminado',
  deactivated: 'Desactivado',
  activate: 'Activar',
  deactivate: 'Desactivar',
  canceled: 'Cancelado',
  siteNotReady: 'Sitio no listo',
  materialProblem: 'Problema con el material',
  laborIssue: 'Problema de trabajo',
  qualityIssue: 'Problema de calidad',
  clarificationNeeded: 'Se necesita aclaración',
  incorrectSpecification: 'Especificación incorrecta',
  materialQuestion: 'Pregunta sobre el material',
  superintendentNeeded: 'Se necesita superintendente',
  plusXMore: (x: number) => `+ ${x} más`,
  submittalInReview: 'Envío en revisión',
  submittalComplete: 'Envío completo',
  inFabrication: 'En fabricación',
  fabricationComplete: 'Fabricación completa',
  inStorage: 'En almacenamiento',
  installed: 'Instalado',
  delivery: 'Entrega',
  newRequest: 'Nueva solicitud',
  deleteDelivery: 'Eliminar entrega',
  yesDeleteDelivery: 'Sí, eliminar entrega',
  cancelDelivery: 'Cancelar entrega',
  yesCancelDelivery: 'Sí, cancelar entrega',
  changeDate: 'Cambiar fecha',
  yesMoveTheDate: 'Sí, mover la fecha',
  requestIsNotValid: 'La solicitud de entrega no es válida',
  datePassedDeadline: 'Fecha límite pasada',
  requestAnyway: 'Solicitar de todos modos',
  requestAccessTo: 'Solicitar acceso a',
  thisRequestHasPassedTheDeadline: 'Esta solicitud ha pasado la fecha límite',
  noDeadline: 'Sin fecha límite',
  twentyFourHours: '24 horas',
  fortyEightHours: '48 horas',
  seventyTwoHours: '72 horas',
  fourDays: '4 días',
  fiveDays: '5 días',
  oneWeek: '1 semana',
  twoWeeks: '2 semanas',
  oneMonth: '1 Mes',
  twoMonths: '2 Meses',
  threeMonths: '3 Meses',
  sixMonths: '6 Meses',
  twelveMonths: '12 Meses',
  dateAndTime: 'Fecha y hora',
  startDate: 'Fecha de inicio',
  endDate: 'Fecha de fin',
  finishDate: 'Fecha de finalización',
  addFinishDate: 'Agregar fecha de finalización',
  removeFinish: 'Eliminar final',
  startTime: 'Hora de inicio',
  endTime: 'Hora de finalización',
  nextDay: 'día siguiente',
  selectTime: 'Seleccionar hora',
  location: 'Ubicación',
  locations: 'Ubicaciones',
  locationFrom: 'Ubicación Desde',
  locationTo: 'Ubicación Hasta',
  formType: 'Tipo de Workflow',
  title: 'Título',
  noAvailableTemplates: 'No hay plantillas disponibles',
  editForm: 'Editar Workflow',
  contact: 'Contacto',
  offloadingEquipment: 'Equipamiento de descarga',
  vehicles: 'Vehículos',
  material: 'Material',
  materialDeliveryLocation: 'Lugar de entrega del material',
  materialsCategory: 'Categoría de materiales',
  inspectionRequired: 'Inspección requerida',
  supplierAndDriver: 'Proveedor y conductor',
  anySpecialInstructions: '¿Algún instrucción especial?',
  deliverySitemap: 'Mapa del sitio de entrega',
  gate: 'Puerta',
  area: 'Área',
  fromToDelivery: 'Entrega desde hasta',
  installationZone: 'Zona de instalación',
  deliveryVehicleType: 'Tipo de vehículo de entrega',
  numberOfVehicles: 'Número de vehículos',
  numberOfVehicleLicensePlate: 'Modelo de vehículo y Lic.#',
  equipment: 'Equipamiento',
  materialResource: 'recurso material',
  struxTags: 'StruxTags',
  registerStruxTag: 'Registrar StruxTag',
  register: 'Registrar',
  registering: 'Registrando...',
  infuseId: 'ID de Infuse',
  infuseIdIsRequired: 'El ID de Infuse es obligatorio',
  infuseIdMustNotContainSpaces: 'El ID de Infuse no debe contener espacios',
  failedToRegisterStruxTag: 'No se pudo registrar StruxTag',
  sharedResources: 'Recursos compartidos',
  units: 'Unidades',
  inspector: 'Inspector',
  vendorEmails: 'Correos electrónicos del proveedor',
  driverMobilePhoneNumbers: 'Número(s) de teléfono móvil del conductor(es)',
  specialInstructions: 'Instrucciones especiales',
  linkedActivity: 'Actividad vinculada',
  customActionButton: 'Botón de acción personalizada',
  building: 'Edificio',
  route: 'Ruta',
  staging: 'Preparación',
  interiorPath: 'Camino interno',
  interiorDoor: 'Puerta interna',
  bookingDate: 'Fecha de reserva',
  requesterCompany: 'Empresa Solicitante',
  duration: 'Duración',
  onsiteContact: 'Contacto en el Sitio',
  onsiteContacts: 'Contactos en el Sitio',
  category: 'Categoría',
  materialNote: 'Nota de Material',
  unnamedMaterial: 'Material sin Nombre',
  unnamedCompany: 'Empresa sin nombre',
  required: 'Requerido',
  optional: 'Opcional',
  multipleSelection: 'Selección Múltiple',
  adding: 'Añadiendo',
  findOrAddNewEntry: 'Buscar o Añadir una Nueva Entrada',
  findEntry: 'Buscar Entrada',
  yes: 'Sí',
  no: 'No',
  unscheduledDelivery: 'Entrega no Programada',
  uploadASitemapToShowGatesAndAreas:
    'Subir un mapa del sitio para mostrar Puertas y Áreas',
  uploadAViewToShowGatesAndAreas:
    'Subir una vista del sitio para mostrar Puertas y Áreas',
  uploadASitemapToShowBuilding: 'Subir un mapa del sitio para mostrar Edificio',
  buildingAccessMap: 'Mapa de Acceso al Edificio',
  uploadASitemapToShowGates: 'Subir un mapa del sitio para mostrar Puertas',
  gateAccessMap: 'Mapa de Acceso a la Puerta',
  uploadASitemapToShowEquipment: 'Subir un mapa del sitio para mostrar Equipos',
  equipmentAccessMap: 'Mapa de Acceso a los Equipos',
  zoneAccessMap: 'Mapa de Acceso a la Zona',
  uploadASitemapToShowAreas: 'Subir un mapa del sitio para mostrar Áreas',
  routeAccessMap: 'Mapa de Acceso a la Ruta',
  uploadASitemapToShowRoute: 'Subir un mapa del sitio para mostrar Ruta',
  deliveryWorkflow: 'Workflow de Entrega',
  workflowMenu: 'Menú de Workflow',
  addANoteToThisDelivery: 'Añadir una nota a esta Entrega',
  send: 'Enviar',
  deliveryDescriptions: {
    confirmDelete:
      'Eliminar esta entrega eliminará la entrega y todas las notas y datos asociados al proyecto.',
    confirmDelete2: 'Haz esto solo si la Solicitud de Entrega fue un error.',
    confirmCancel: `Las entregas canceladas permanecerán en el calendario pero se marcarán como 'Canceladas'.`,
    confirmCancel2: '¿Quieres Cancelar esta entrega?',
    nonWorkTimeDateChange: (days: number) =>
      `Por favor, mueve esta Entrega. Las solicitudes deben ser ${days} día${ending(
        days,
      )} laborable${ending(
        days,
      )} con Antelación a la fecha y hora solicitadas y en una fecha-hora laborable`,
    maximumDeliveriesAreScheduled:
      'El número máximo de entregas ya está programado para esta ubicación',
  },
  loginInfoPageDescriptions: {
    requestSentTitle: 'Solicitud enviada',
    requestApprovedTitle: 'Solicitud aprobada',
    invalidInvitationTitle: 'Enlace de invitación no válido',
    somethingWentWrongTitle: 'Algo salió mal',
    accountRequestPendingEmail:
      'El administrador está revisando tu solicitud. Recibirás un correo electrónico de invitación en cuanto se apruebe. Revisa tu bandeja de entrada.',
    accountRequestPendingSms:
      'El administrador está revisando tu solicitud. Recibirás un enlace de invitación por SMS en cuanto se apruebe.',
    accessRequestPending:
      'El administrador está revisando tu solicitud. Recibirás una invitación en cuanto se apruebe.',
    accountRequestApproved:
      'Te hemos enviado un correo electrónico de invitación. Para iniciar sesión, sigue las instrucciones que contiene.',
    goToAvailableProject: 'Ir a un Proyecto disponible',
    invalidInvitationDescription:
      'Este enlace de invitación está incompleto o dañado. Ábrelo de nuevo desde tu invitación o pide a tu administrador que te envíe uno nuevo.',
    somethingWentWrongDescription:
      'No pudimos completar tu solicitud. Inténtalo de nuevo más tarde o contacta con el soporte si el problema persiste.',
    expiredInvitationTitle: 'Tu invitación ha caducado',
    expiredInvitationDescription: (hours: number) =>
      `Por motivos de seguridad, los enlaces de invitación caducan. Reenvía la invitación para recibir un enlace nuevo, válido durante ${xHours(hours)}.`,
    resendMyInvitationLink: 'Reenviar mi enlace de invitación',
    invitationResentTitle: 'Invitación reenviada',
    invitationResentDescription:
      'Revisa tu correo electrónico y sigue el nuevo enlace.',
  },

  ssoAdminConsentPage: {
    grantedTitle: 'Consentimiento de administrador concedido',
    grantedDescription: `El inicio de sesión único de Microsoft ya está habilitado para tu organización. Tu equipo puede iniciar sesión en ${brandName} con sus cuentas de Microsoft.`,
    notCompletedTitle: 'No se completó el consentimiento',
    notCompletedDescription: `No se concedió el consentimiento de administrador. Puedes volver a intentar el enlace de consentimiento o contactar con el soporte de ${brandName}.`,
    continueToApp: `Continuar a ${brandName}`,
    backToApp: `Volver a ${brandName}`,
    directory: 'Directorio',
  },

  assignResponsibilitiesConfirmation:
    'Confirmación de Asignación de Responsabilidades',
  assignResponsibilities: 'Asignar Responsabilidades',
  assignToActivities: 'Asignar a Actividades',
  groupByTeam: 'Agrupar por Equipo',
  noTeam: '[sin equipo]',
  hashActivities: '# Actividades',
  rfi: 'RFI',
  flag: 'Marca',
  reply: 'Responder',
  unspecifiedType: '(Tipo no Especificado)',
  contractsOrRevisions: 'Contratos/Revisiones',
  priorWorkNotComplete: 'Trabajo Anterior No Completado',
  informationNotAvailable: 'Información No Disponible',
  unclearTaskDescription: 'Descripción de la Tarea No Clara',
  staffNotAvailable: 'Personal No Disponible',
  materialsNotAvailable: 'Materiales No Disponibles',
  noInformationAvailableOnThisMonitoring:
    'No hay información disponible sobre esta monitorización',
  conditionsOfSatisfactions: 'Condiciones de Satisfacción',
  taskSequenceChange: 'Cambio de Secuencia de Tarea',
  incorrectTimeEstimate: 'Estimación de Tiempo Incorrecta',
  offProjectDemands: 'Demandas Fuera del Proyecto',
  unforeseenConditions: 'Condiciones Imprevistas',
  weather: 'Clima',
  weatherDescriptions: {
    'clear sky': 'Cielo despejado',
    'few clouds': 'Pocas nubes',
    'scattered clouds': 'Nubes dispersas',
    'overcast clouds': 'Nubes cubiertas',
    'broken clouds': 'Nubes rotas',
    'light rain': 'Lluvia ligera',
    'heavy intensity rain': 'Lluvia fuerte',
    'light snow': 'Nieve ligera',
    'heavy snow': 'Nieve fuerte',
    'shower sleet': 'Aguacero de aguanieve',
    'freezing rain': 'Lluvia helada',
    'very heavy rain': 'Lluvia muy fuerte',
    thunderstorm: 'Tormenta',
    snow: 'Nieve',
    mist: 'Niebla',
  },
  humidity: 'humedad',
  precipitation: 'precipitación',
  temperatureRange: {
    low: 'mínima',
    high: 'máxima',
    avg: 'prom.',
  },
  fiveDayForecast: 'Pronóstico de 5 Días',
  elevatedWork: 'Trabajo Elevado',
  materialHandling: 'Manejo de Materiales',
  groundDistribution: 'Distribución en Tierra',
  craneOrHeavyLift: 'Grúa / Elevación Pesada',
  confinedSpace: 'Espacio Confinado',
  mobileEquipment: 'Equipo Móvil',
  deliver: 'Entregar',
  loto: 'LOTO',
  hotWork: 'Trabajo en Caliente',
  hazardousMaterial: 'Material Peligroso',
  highVoltage: 'Alta Tensión',
  highDecibels: 'Altos Decibelios',
  lifting: 'Levantamiento',
  closeQuarters: 'Espacios Reducidos (<1.83m)',
  rfiOpened: 'RFI Abierto',
  flagOpened: 'Bandera Abierta',
  scheduleCommentOpened: 'Comentario de programación abierto',
  addANote: 'Agregar una nota',
  addToLog: 'Agregar al registro',
  emojiPicker: 'Selector de emojis',
  addPhotoFromLibrary: 'Agregar foto desde la galería',
  addFile: 'Agregar archivo',
  atMentionAUser: 'Mencionar a un usuario',
  atMentionUserDescription:
    'Para notificar y agregar a un usuario como contacto adicional del flujo de trabajo',
  start_noun: 'Inicio',
  end_noun: 'Fin',
  forDate: (date: string) => `Para ${date}`,
  addANoteToThisStatusUpdate: 'Agregue una nota a esta Actualización de Estado',
  chooseADeliveryTime: 'Elija una hora de entrega',
  allZones: 'Todas las Zonas',
  allEquipment: 'Todo el Equipo',
  selectDate: 'Seleccionar fecha',
  selectMonth: 'Seleccionar mes',
  selectDateRange: 'Seleccionar rango de fechas',
  selectField: 'Seleccionar campo',
  selectDateAndTime: 'Seleccionar fecha y hora',
  jumpToDate: 'Saltar a la fecha',
  jumpToMap: 'Saltar al mapa',
  newDelivery: 'Nueva Entrega',
  newForm: 'Nuevo Workflow',
  pending: 'Pendiente',
  confirmChange: 'Confirmar Cambio',
  okToProceed: '¿OK para continuar?',
  proceed: 'Proceder',
  actualStart: 'Inicio Real',
  updated: 'Actualizado',
  approved: 'Aprobado',
  pendingApproval: 'Pendiente de aprobación',
  missing: 'Faltante',
  actualFinish: 'Finalización Real',
  xDaysUpdated: (x: number) => `¡${x} Días Actualizados!`,
  copyToMissing: 'Copiar a Faltante(s)',
  copy: 'Copiar',
  copiedToClipboard: 'Copiado al portapapeles',
  traceId: 'ID de traza',
  copyTraceId: 'Copiar ID de traza',
  copyLinkToDelivery: 'Copiar enlace a entrega',
  copyLink: 'Copiar enlace',
  enabled: 'Activado',
  disabled: 'Desactivado',
  checkInUnscheduledDelivery: 'Registrar entrega no programada',
  paste: 'Pegar',
  estimateMissing: 'Estimación Faltante(s)',
  activityDetails: 'Detalles de la Actividad',
  assigned: 'Asignado',
  followed: 'Seguido',
  following: 'Siguiendo',
  notFollowing: 'No siguiendo',
  predecessors: 'Predecesores',
  successors: 'Sucesores',
  viewTeam: 'Ver equipo',
  dates: 'Fechas',
  plannedStart: 'Inicio Planificado',
  plannedEnd: 'Fin Planificado',
  actualEnd: 'Fin Real',
  activityLog: 'Registro de Actividad',
  when: 'Cuando',
  date: 'Fecha',
  time: 'Hora',
  where: 'Dónde',
  min_shortMinutes: 'min',
  hr_shortHours: 'hr',
  requester: 'Solicitante',
  responsible: 'Responsable',
  subscriber: 'Suscriptor',
  directAssignments: 'Asignaciones Directas',
  otherSubmissions: 'Otras Presentaciones',
  onsiteContactPerson: 'Persona de Contacto en el Sitio',
  onsiteContactPersons: 'Personas de Contacto en el Sitio',
  vehicle: 'Vehículo',
  quantity: 'Cantidad',
  inspection: 'Inspección',
  thisDeliveryWasNotScheduledInAdvance:
    'Esta entrega no fue programada con antelación',
  thisDeliveryWasScheduledForNonWorkTime:
    'Esta entrega fue programada para un día o hora no laborable',
  sitemap: 'Mapa del sitio',
  flagAnIssue: 'Marcar un problema',
  openAnRfi: 'Abrir un RFI',
  requestADelivery: 'Solicitar una Entrega',
  reason: 'Razón',
  addReasonForFlag: 'Agregar razón para la bandera',
  addReasonForRfi: 'Agregar razón para el RFI',
  addReasonForCategoryOfVariance:
    'Agregar razón para la Categoría de Variación',
  addReasonForSafetyHazard: 'Agregar razón para el Riesgo de Seguridad',
  addANoteToThisActivityLog: 'Agregar una nota a este Registro de Actividad',
  uploadingImage: 'Subiendo Imagen',
  ganttDescriptions: {
    assignXUsersToYActivities: (x: number, y: number) =>
      `Asignando ${ending(x, 'el', 'los')} siguiente${ending(x)} ${
        x != 1 ? x : ''
      } miembro${ending(x)} del equipo a ` +
      `${y} ${activities(y)} seleccionada${ending(y)}`,
  },
  actualDatesDescriptions: {
    newStartDateAfterOld: (
      newDate: string,
      oldDate: string,
      wrapper: (x: string) => any,
    ) => [
      'Haciendo ',
      wrapper(newDate),
      ' el Inicio Real desmarcará ',
      wrapper(oldDate),
      ` como el Inicio Real e ignorará todas las actualizaciones antes de ${newDate}.`,
    ],
    newStartDateBeforeOld: (
      newDate: string,
      oldDate: string,
      wrapper: (x: string) => any,
    ) => [
      'Haciendo ',
      wrapper(newDate),
      ' el Inicio Real desmarcará ',
      wrapper(oldDate),
      ' como el Inicio Real y conservará todos los datos asociados.',
    ],
    newEndDateAfterOld: (
      newDate: string,
      oldDate: string,
      wrapper: (x: string) => any,
    ) => [
      'Haciendo ',
      wrapper(newDate),
      ' la Finalización Real desmarcará ',
      wrapper(oldDate),
      ' como la Finalización Real e ignorará su valor de % Completo.',
    ],
    newEndDateBeforeOld: (
      newDate: string,
      oldDate: string,
      wrapper: (x: string) => any,
    ) => [
      'Haciendo ',
      wrapper(newDate),
      ' la Finalización Real desmarcará ',
      wrapper(oldDate),
      ` como la Finalización Real e ignorará todas las actualizaciones después de ${newDate}.`,
    ],
  },

  offloadingEquipmentMaps: (x: number) =>
    `Mapa${ending(x)} de equipo de descarga`,

  filterOptions: 'Filtrar opciones',
  selectOptions: 'Seleccionar Opciones',
  filterByLBS: 'Filtrar por LBS',
  auto: 'auto',

  level: 'Nivel',
  zone: 'Zona',
  siteLocation: 'Ubicación del sitio',
  verticalObject: 'Objeto vertical',
  manpower: 'Mano de obra',
  manPower: 'Mano de obra',
  description: 'Descripción',
  code: 'Código',
  codeVerifying: 'El código se está verificando',
  codeResending: 'El código se está reenviando',
  resendCode: 'Reenviar Código',
  contract: 'Contrato',
  plannedValue: 'Valor Planificado',
  bidStatus: 'Estado de la Oferta',

  street: 'Calle',
  satellite: 'Satélite',
  layers: 'Capas',
  terrain: 'Terreno',
  transit: 'Tránsito',
  traffic: 'Tráfico',
  more: 'Más',
  mapType: 'Tipo de mapa',
  mapSettings: 'Configuración del mapa',
  mapDetails: 'Detalles del mapa',
  overlays: 'Superposiciones',
  labelsDisplay: 'Visualización de etiquetas',

  allowTextAndIconToScale: 'Permitir que el texto y los íconos se escalen',
  allowLabelsToOverlap: 'Permitir que las etiquetas se superpongan',
  showWorkflowPinLabels: 'Mostrar etiquetas de los pines de workflow',
  workflowMarkups: 'Anotaciones de workflow',
  showForMe: 'Mostrar para mí',
  showAssociationLabels: 'Mostrar etiquetas de asociación',
  showWorkflowMarkupsForEveryone:
    'Mostrar por defecto para todos en este proyecto',
  showAdjacent: 'Mostrar adyacentes',
  markupsShortcutHint: 'activa/desactiva',
  clickAgainToEditPoints: 'Haz clic de nuevo para editar los puntos',
  longPressToRemovePoint: 'Mantén pulsado un punto para eliminarlo',
  finishShaping: 'Terminar forma',
  discardShape: 'Descartar forma',
  finishLine: 'Terminar línea',
  discardLine: 'Descartar línea',
  labels: 'Etiquetas',
  threeDBuildings: 'Edificios 3D',
  resetViewport: 'Restablecer vista',
  toggleProjection: 'Cambiar proyección',
  moreOptions: 'Más opciones',
  showPlan: 'Mostrar Plan',
  showWorkflows: 'Mostrar Workflows',

  version: 'Versión',
  revision: 'Revisión',
  build: 'Construcción',

  viewName: 'Nombre de la vista',
  viewImage: 'Ver imagen',
  shareToProject: 'Compartir con el proyecto',
  create: 'Crear',
  private: 'Privada',
  listSections: {
    shown: 'Mostrada',
    hidden: 'Oculta',
  },

  deliveryIdNotProvided: 'ID de entrega no proporcionado',
  sendingDeliveryTicket: 'Enviando boleto de entrega al conductor',
  ticketSuccessfullySent: 'Boleto enviado con éxito',
  failedToSendTicket: 'Fallo al enviar el boleto',
  invalidDeliveryTicket: 'Boleto de entrega no válido',
  deliveryDetails: 'Detalles de la Entrega',
  onSiteLogistics: 'Logística en el sitio',

  showAtEntryGate: 'Mostrar en la Puerta de Entrada',
  showAtX: (location: string) => `Mostrar en ${location}`,
  deliveryTicket: 'Boleto de Entrega',
  hasDelivery: 'tiene entrega',
  duplicate_verb: 'Duplicar',
  createFromToDelivery:
    'Cree la entrega De-a. La entrega actual debe estar marcada ≥ [Onsite].',
  duplicateCurrentDelivery: 'Duplicar entrega actual',
  unsubscribeToDelivery: 'Cancelar suscripción a la entrega',
  subscribeToDelivery: 'Suscribirse a la entrega',
  sharedDelivery: 'Entrega compartida',

  requiredFieldCannotBeEmpty: 'El campo requerido no puede estar vacío',
  fieldShouldBeUnique: 'El campo debe ser único',
  pleaseCorrectErrors: 'Por favor, corrija los errores',
  pleaseFillAllFields: 'Por favor llena todos los espacios',

  companiesUpdated: 'Empresas actualizadas',
  companyAdded: 'Empresa añadida',

  forms: 'Formularios',
  formTypes: 'Tipos de Workflows',

  legacy: 'Legado',
  deliveryLocations: 'Ubicaciones de entrega',
  cards: 'Tarjetas',

  columns: 'Columnas',
  struxhubLabs: `Laboratorios ${brandName}`,

  mobileConfiguration: 'Configuración móvil',
  additionalDataUpload: 'Subida de datos adicionales',
  onTimeThresholds: 'Umbrales a tiempo',
  qrScannersSetup: 'Configuración de escáneres de código QR',

  businessPhone: 'Teléfono de negocios',
  businessEmail: 'E-mail de negocios',
  responsibles: 'Responsables',
  responsibleContact: 'Contacto responsable',
  dba: 'DBA',
  licenseNumber: 'Número de licencia',

  deliveryId: 'ID de entrega',
  bookingTime: 'Hora de reserva',
  enterVerificationCodeFromSMS: 'Ingrese el código de verificación del SMS',
  getDirections: 'Obtener direcciones',
  showDirectionsOnMap: 'Mostrar indicaciones en el mapa',
  copyGoogleMapsLink: 'Copiar enlace de Google Maps',
  copyLocationCoordinates: 'Copiar coordenadas de la ubicación',
  moreActions: 'Más acciones',
  copyFailed: 'No se pudo copiar',

  userInviteMessages: {
    oneUserWasInvitedToProject: (userName: string) =>
      `${userName} fue invitado al proyecto`,
    manyUsersWereInvitedToProject: (count: number) =>
      `${xUsers(count)} fueron invitados al proyecto`,
  },

  userEmailLinking: {
    linkYourEmail: 'Vincule su e-mail',
    linkYourEmailAndSetPassword: 'Vincule su e-mail y establezca la contraseña',
    intro: `Usted puede usar su e-mail para to iniciar sessión a ${brandName}. Esta etapa es opcional.`,
    setEmailWithPassword: 'Please create a password.',
    weakPassword: 'Contraseña débil. Verifique el formato válido',
    emailHasBeenLinkedSuccessfully: 'El e-mail ha sido vinculado con éxito',
  },

  thereIsNoProject:
    'No hay proyecto disponible para su cuenta de usuario. Por favor, contacte al administrador',
  noProjectsAvailableTitle: 'No hay proyectos disponibles',

  repeatOn: 'Repetir en',

  operatorValues: {
    isNot: 'NO ES',
    is: 'ES',
    are: 'SON',
    and: 'Y',
    or: 'O',
    for: 'PARA',
    assign: 'ASIGNAR',
    if: 'SI',
  },

  any: 'Cualquier',
  selectCompany: 'Seleccionar empresa',
  selectPermittedCompanies: 'Seleccionar Empresas Permitidas',
  selectLocation: 'Seleccionar ubicación',
  approval: 'Aprobación',
  firewatch: 'Vigilancia de fuego',
  noRules: 'No hay reglas',
  rule: 'Regla',

  xOfY: (x: number, y: number) => `${x} de ${y}`,
  options: 'Opciones',
  selectActivityGroups: 'Seleccionar grupos de actividad',
  selectActionType: 'Selecciona el tipo de acción',
  numberOfBands: 'Número de bandas',
  lbs: 'LBS', // estructura de desglose de ubicación
  lbsTag: 'Etiqueta LBS',
  lbsTagTooltip:
    'LBS (estructura de desglose de ubicación) define dónde se encuentra un recurso dentro del proyecto. Seleccione el recurso asociado a la vista de mapa.',
  workBreakdownStructureWbs: 'Estructura de desglose de trabajo (WBS)',
  locationBreakdownStructureLbs: 'Estructura de desglose de ubicación (LBS)',
  groupByOptions: 'Agrupar por opciones',
  showEmptyBandsLevels: 'Mostrar niveles de bandas vacías',
  customGroupCreation: 'Creación de grupo personalizado',
  bandX: (x: number) => `Banda ${x}`,
  custom: 'Personalizado',
  groupByX: (x: string) => `Agrupar por: ${x}`,

  bookingId: 'ID de reserva',
  responsibleCompany: 'Empresa responsable',
  bookingDateEnTimeOffset: (offset: string) =>
    `Fecha y hora de reserva (${offset})`,
  durHmm_durationShort: 'Dur. (h:mm)',
  locationStatus: 'Estado de ubicación',
  closeLocations: 'Cerrar ubicaciones',
  closeResources: 'Cerrar recursos',
  openLocations: 'Abrir ubicaciones',
  openResources: 'Abrir recursos',
  selectFields: 'Seleccionar campos',
  selectProperty: 'Seleccionar propiedad',
  addAction: 'Agregar acción',
  closureStart: 'Inicio del cierre',
  closureFinish: 'Finalización del cierre',
  permittedCompany: 'Empresa permitida',
  closureWorkflow: 'Workflow de cierre',
  updateForm: 'Actualizar Workflow',
  formStatus: 'Estado del Workflow',
  formStart: 'Inicio del Workflow',
  formFinish: 'Finalización del Workflow',
  materialName: 'Nombre del material',
  productCategory: 'Categoría del producto',
  deliveryMaterialNotes: 'Notas de material de entrega',
  vendor: 'Proveedor',
  quantityOnsite: 'Cantidad en el sitio',
  storageArea: 'Área de almacenamiento',
  requiredOnsiteDate: 'Fecha requerida en el sitio',
  nextDelivery: 'Próxima entrega',
  deliveryBookingDate: 'Fecha de reserva de entrega',
  bookedQuantity: 'Cantidad reservada',
  recordLastUpdated: 'Registro Actualizado por Última Vez',
  numObjects: 'Nº de Objetos',
  showOn: 'Mostrar en',
  noShow: 'No Mostrar',
  moved: 'Movido',
  otherReason: 'Otra Razón',
  mostPertinentDefault: 'Más pertinente (predeterminado)',
  zones: 'Zonas',
  gates: 'Puertas',
  routes: 'Rutas',
  areas: 'Áreas',
  levels: 'Niveles',
  totalX: (x: number) => `Total ${x}`,
  deliveriesEnEquipmentNeedsPerHour:
    'Entregas y Necesidades de Equipo por hora',
  resetFilters: 'Restablecer Filtros',
  saved_activities: 'Guardado',
  since: 'Desde',
  lastP6Update: 'Última actualización de P6',
  lastWeek: 'La semana pasada',
  lastMonth: 'El mes pasado',
  last6Week: 'Últimas 6 semanas',
  dateRange: 'Rango de fechas',
  dateRangeForRecentlyUpdatedActivities:
    'Rango de fechas para actividades recientemente actualizadas',
  myTasks: 'Mis Tareas',
  recentlyUpdated_activities: 'Recientemente Actualizado',
  activitiesWithSpecificStatus: 'Actividades con estado específico',
  actualizedFromSchedule_activities: 'Actualizado desde el horario',
  activitiesWithOpenScheduleComment:
    'Actividades con comentario abierto en el horario',
  notCompleted_activities: 'No Completado',
  criticalPathActivities: 'Actividades de Ruta Crítica',
  flaggedActivities: 'Actividades Marcadas',
  lateActivities: 'Actividades Atrasadas',
  activitiesWithOpenRFI: 'Actividades con RFI abierto',
  activitiesWithDeliveries: 'Actividades con Entregas',
  numberOfActivities: 'Número de Actividades',
  seeXActivities: (x: number) => `Ver ${x} ${activities(x)}`,
  type: 'Tipo',
  types: 'Tipos',
  assigners: 'Asignadores',
  items: 'Artículos',
  projectMember: 'Miembro del Proyecto',
  disableFilter: 'Desactivar filtro',
  totalDeliveries: 'Entregas Totales',
  totalSitemaps: 'Mapas del sitio Totales',
  totalForms: 'Workflows Totales',
  totalAttachments: 'Archivos Adjuntos Totales',
  totalReports: 'Informes Totales',
  totalClosures: 'Cierres Totales',
  totalSensors: 'Sensores Totales',
  seeXUsers: (x: number) => `Ver ${xUsers(x)}`,
  totalLogistics: 'Workflows Totales',
  totalMaterials: 'Materiales Totales',
  seeXDeliveries: (x: number) => `Ver ${x} ${deliveries(x)}`,
  xPlans: (x: number) => `${x} ${plans(x)}`,
  seeXObjects: (x: number) => `Ver ${x} Objeto${ending(x)}`,
  seeXSitemaps: (x: number) => `Ver ${x} Mapa${ending(x)} del sitio}`,
  seeXForms: (x: number) => `Ver ${xForms(x)}`,
  seeXClosures: (x: number) => `Ver ${x} Cierre${ending(x)}`,
  seeXSensors: (x: number) => `Ver ${x} Sensor${ending(x, '', 'es')}`,
  seeXLogistics: (x: number) => `Ver ${x} Workflow${ending(x)}`,
  seeXMaterials: (x: number) => `Ver ${x} Material${ending(x, '', 'es')}`,
  seeXReports: (x: number) => `Ver ${x} Informe${ending(x)}`,
  seeXAttachments: (x: number) =>
    `Ver ${x} Archivo${ending(x)} Adjunto${ending(x)}`,
  currentLookahead: 'Previsión actual',
  fullProject: 'Proyecto Completo',
  numberOfDeliveries: 'Número de Entregas',
  numberOfWorkflows: 'Número de Workflows',
  savedFilters: 'Filtros Guardados',
  saved_deliveries: 'Guardado',
  xDeliveries: (x: number) => `${x} ${deliveries(x)}`,
  xWorkflows: (x: number) => `${x} ${workflows(x)}`,
  savedActivitySets: 'Conjuntos de Actividades Guardados',
  thereAreNoSavedActivityPresets:
    'No hay ajustes preestablecidos de actividades guardados',
  xForms,
  xFilters: (x: number) => `${x} filtro${ending(x)}`,
  thereAreNoSavedFilters: 'No hay filtros guardados',
  makePublic: 'Hacer público',
  workPackageName: 'Nombre del paquete de trabajo',
  selectedFilters: 'Filtros seleccionados',
  share: 'Compartir',
  shareWithLink: 'Compartir con enlace',
  saveCurrentFilters: 'Guardar los filtros actuales',
  saveANewCustomFilter: 'Guardar un filtro personalizado nuevo',
  editACustomFilter: 'Editar un filtro personalizado',

  oneDay: 'Un Día',
  oneDayDefaultToday: 'Un Día Predeterminado Hoy',
  week: 'Semana',
  fixedWeek: 'Semana Fija',
  fixedTwoWeeks: '2 Semanas Fijas',
  fixedThreeWeeks: '3 Semanas Fijas',
  threeWeeks: '3 Semanas',
  fourWeeks: '4 Semanas',
  fixedFourWeeks: '4 Semanas Fijas',
  month: 'Mes',
  fixedSixWeeks: '6 Semanas Fijas',
  sixWeeks: '6 Semanas',
  threeMonth: '3 Meses',
  year: 'Año',
  other: 'Otro',

  xDateToYDate: (xDate: string, yDate: string) => `${xDate} a ${yDate}`,
  assignUsersOrCategories: 'Asignar usuarios o categorías',
  searchUserCompanyTeamOrRole: 'Buscar Usuario, Empresa, Equipo o Rol',
  successfullyUpdatedExistingAssignments:
    'Asignaciones existentes actualizadas con éxito',
  successfullyDeletedAssignments: 'Asignaciones eliminadas con éxito',

  approve_verb: 'Aprobar',
  readyToInspect: 'Listo para inspeccionar',
  toInspect: 'Para inspeccionar',
  notInUse: 'No en uso',
  everyXDaysInspection: (x: number) =>
    x > 1 ? `Inspección cada ${x} días` : 'Inspección todos los días',
  everyXWeeksInspection: (x: number) =>
    x > 1 ? `Inspección cada ${x} semanas` : 'Inspección todas las semanas',
  everyXMonthsInspection: (x: number) =>
    x > 1 ? `Inspección cada ${x} meses` : 'Inspección todos los meses',
  addInspection: 'Agregar Inspección',
  finish_noun: 'Finalizar',
  legend: 'Leyenda',
  startFinishState: 'Estado de Inicio/Finalización',
  intermediateState: 'Estado Intermedio',
  action: 'Acción',
  ballInCourtStep: 'Paso en juego',
  active: 'Activo',
  struxhubActivityX: (code: string) => `Actividad ${brandName} ${code}`,
  openActivity: 'Abrir Actividad',
  openDelivery: 'Abrir Entrega',
  loadingPresentationMode: 'Cargando Modo de Presentación',

  xSelected: (x: number) => `Selección: ${x}`,
  xItemsCount: (x: number) => `${x} Artículo${ending(x)}`,
  xTypesCount: (x: number) => `${x} Tipo${ending(x)}`,
  xFormsCount: (x: number) => `${x} Formulario${ending(x)}`,
  xApplicationsCount: (x: number) => `${x} Aplicaci${ending(x, 'ón', 'ones')}`,
  xStepsCount: (x: number) => `${x} Paso${ending(x)}`,
  xFieldsCount: (x: number) => `${x} Campo${ending(x)}`,
  xOfYSelected: (x: number, y: number) =>
    `${x} de ${y} seleccionado${ending(x)}`,
  xDeliveriesShowing: (x: number) => `${x} ${deliveries(x)} mostrando`,
  xEquipment: (x: number) => `${x} Equipamiento${ending(x)}`,
  xSensorsShowing: (x: number) => `${x} sensor${ending(x, '', 'es')} mostrando`,
  xStruxTags: (x: number) => `${x} ${struxTagsCount(x)}`,
  xResourcesShowing: (x: number, countLabel: string) =>
    `${x} ${countLabel} mostrando`,
  assign_verb: 'Asignar',
  subscribe_verb: 'Suscribir',
  new: 'Nuevo',
  completedTotal: 'Completado/Total',
  searchByBookingIdOrSubscribers: 'Buscar por ID de Reserva o Suscriptores',
  me: 'Mis',
  full_period: 'Completo',
  day: 'Día',
  calendarView: 'Vista de Calendario',
  ganttView: 'Vista de Gantt',
  listView: 'Vista de Lista',
  mapView: 'Vista de Mapa',
  dur_duration: 'Dur',
  loc_locations: 'Ubi',
  equip_equipment: 'Equip',
  mat_materials: 'Mat',
  vend_vendor: 'Prov',
  a_actualFlag: 'A',
  unspecified: 'No especificado',

  yesUnsubscribe: 'Sí, darse de baja',
  deliveriesListDescriptions: {
    subscribeConfirmMassage: (
      dUnsubscribedCount: number,
      dSelectedCount: number,
    ) =>
      `Suscribirse a [${dUnsubscribedCount}] ${deliveries(
        dUnsubscribedCount,
      )} ` +
      `no suscrita${ending(dUnsubscribedCount)} ` +
      `de [${dSelectedCount}] ${deliveries(dSelectedCount)} ` +
      `seleccionada${ending(dSelectedCount)}?`,

    unsubscribeConfirmMessage: (dSelectedCount: number) =>
      `Ya está suscrito a todas las [${dSelectedCount}] ` +
      `${deliveries(dSelectedCount)} seleccionadas .\n\n` +
      '¿Quizás, desea darse de baja?',
  },
  logisticsListDescriptions: {
    subscribeConfirmMassage: (
      dUnsubscribedCount: number,
      dSelectedCount: number,
    ) =>
      `Suscribirse a [${dUnsubscribedCount}] ${workflows(
        dUnsubscribedCount,
      )} ` +
      `no suscrito${ending(dUnsubscribedCount)} de [${dSelectedCount}] ` +
      `${workflows(dSelectedCount)} seleccionado${ending(dSelectedCount)}?`,
    unsubscribeConfirmMessage: (dSelectedCount: number) =>
      `Ya está suscrito a todos los [${dSelectedCount}] ` +
      `${workflows(dSelectedCount)} seleccionados.\n\n` +
      '¿Quizás, desea darse de baja?',
    areYouWantToDelete:
      '¿Está seguro de que desea eliminar todos estos Workflows seleccionados?',
    deletionConfirmMsg: (selectedCount: number) =>
      `Seleccionaste - ${selectedCount} ${workflows(selectedCount)}`,
  },
  sitemapsDataIsLoading: 'Los datos de los mapas del sitio se están cargando',
  editingConfirmation: 'Confirmación de Edición',
  selectDataColumn: 'Seleccionar columna de datos',
  addProperty: 'Agregar propiedad',
  addFieldForEditing: 'Agregar campo para editar',
  deliveryBulkEditorDescriptions: {
    warningMessagePart1:
      'Ninguna de las entregas seleccionadas puede ser ' +
      'cambiada de acuerdo con los permisos de su cuenta',
    warningMessagePart2: 'Haga otra selección o contacte al administrador',
  },
  failedToEdit: 'Fallo al editar',
  editedXFieldsOnYDeliveries: (x: number, y: number) =>
    `${x} Campo${ending(x)} editado${ending(x)} en ${y} ${deliveries(y)}`,
  editXDeliveries_question: (x: number) => `¿Editar ${x} ${deliveries(x)}?`,
  onlyXOfYSelectedDeliveriesCanBeChanged: (x: number, y: number) =>
    `Solo [${x}] de [${y}] ${deliveries(y)} seleccionada${ending(x)} ` +
    `puede${ending(x, '', 'n')} ser cambiada${ending(x)}, ¿proceder?`,
  editXFieldsOnYDeliveries: (x: number, y: number) =>
    `Editar ${x} Campo${ending(x)} en ${y} ${deliveries(y)}`,
  updateXDeliveries: (x: number) => `Actualizar ${x} ${deliveries(x)}`,
  failedToUpdate: 'Fallo al actualizar',
  signedOff: 'Aprobado',
  signedOffFor: (companyName: string) => `Aprobado en nombre de ${companyName}`,
  statusUpdated: 'Estado actualizado',
  statusUpdatedOnXDeliveries: (x: number) =>
    `Estado actualizado en ${x} ${deliveries(x)}`,
  updateDeliveryStatus: 'Actualizar estado de entrega',
  loadingDeliveriesData: 'Cargando datos de entregas',
  loadingFormsData: 'Cargando datos de Workflows',
  form: 'Formulario',
  log: 'Registro',
  utilization: 'Utilización',
  controls: 'Controles',
  resourceProperties: 'Propiedades del recurso',
  resourceControls: 'Controles del recurso',
  mapProperties: 'Propiedades del mapa',
  sessions: 'Sesiones',
  properties: 'Propiedades',
  map: 'Mapa',
  deleteResourceQuestion: '¿Está seguro de que desea eliminar este recurso?',

  weekOfEndDate: 'Semana de fecha de finalización',
  companySummary: 'Resumen de la empresa',
  percentDone: '% Realizado',
  percentOnTime: '% A tiempo',
  trucks: 'Camiones',
  avgTrucksPerDelivery: 'Prom. Camiones / Entrega',
  deliveryGantt: 'Gantt de entrega',
  projectSummary: 'Resumen del proyecto',
  completionRatePercent: 'Porcentaje de finalización',
  completionRate: 'Tasa de finalización',
  onTimeRatePercent: 'Porcentaje de puntualidad',
  onTimeRate: 'Tasa de puntualidad',
  unplanned: 'No planificado',
  unplannedRate: 'Tasa no planificada',
  onTimeStart: 'Inicio puntual',
  lateOnTime: 'Atrasado/A tiempo',
  allDay: 'Todo el día',
  weekly: 'Semanal',
  monthly: 'Mensual',
  onTimeLate: 'A tiempo/Atrasado',
  earnedDuration: 'Duración ganada',
  show_verb: 'Mostrar',
  showXMore: (x: number) => `Mostrar ${x} más`,
  showActive: 'Mostrar activas',
  loadingProjectData: 'Cargando datos del proyecto',
  updateToday: 'Actualizar hoy',
  critical: 'Crítico',
  deltaDays: '∆ Días',
  sumSlip: '∑ Atraso',
  openIssues: 'Problemas abiertos',
  plannedToday: 'Planificado hoy',
  actualToday: 'Actual hoy',
  deltaManpowerToday: '∆ Man. hoy',
  deltaManpowerTrend: 'Tendencia ∆ Man.',
  soon: 'Pronto',
  numberOfPicks: 'Número de selecciones',
  repeatDelivery: 'Repetir entrega',
  every_plural: 'Cada',
  endsOn: 'Termina en',
  thisDelivery: 'Esta entrega',
  thisAndFutureDeliveries: 'Esta y futuras entregas',
  allDeliveries: 'Todas las entregas',
  everyXDays: (x: number) => (x == 1 ? 'Todos los días' : `Cada ${x} días`),
  everyXWeeks: (x: number) =>
    x == 1 ? 'Todas las semanas' : `Cada ${x} semanas`,
  everyXMonths: (x: number) => (x == 1 ? 'Todos los meses' : `Cada ${x} meses`),
  xDays: (x: number) => `día${ending(x)}`,
  xWeeks: (x: number) => `semana${ending(x)}`,
  xMonths: (x: number) => `mes${ending(x)}`,
  xMaterial: (x: number) => `Material Nº ${x}`,
  addMaterial: 'Agregar material',
  forecast: 'Pronóstico',
  unknown: 'Desconocido',
  multipleEntries: {
    add_x: (x: string) => `Agregar ${x}`,
    paste: 'Puede pegar múltiples entradas',
    typeOrPaste: 'Puede escribir o pegar múltiples entradas',
    repeatedValue: 'Entrada duplicada',
  },
  fileType: 'Tipo de archivo',
  fileExtension: 'Extensión de archivo',
  workflowType: 'Tipo de Workflow',
  workflowTypeLabel: 'Tipo de Workflow',
  workflowName: 'Nombre de Workflow',
  workflowCategory: 'Categoría de Workflow',
  workflowId: 'ID de Workflow',
  fileCategory: 'Categoría de archivo',
  uploadedBy: 'Cargado por',
  uploader: 'Cargador',
  source: 'Origen',
  sort: 'Ordenar',
  sortBy: 'Ordenar por',
  sortDirection: 'Dirección de ordenamiento',
  setDefaults: 'Establecer valores predeterminados',
  ascending: 'Ascendente',
  descending: 'Descendente',
  failedToVerifyUploadedFile: 'Fallo al verificar el archivo cargado',

  searchCompanyOrCompanyType: 'Buscar empresa o tipo de empresa',

  newActivity: 'Nueva actividad',
  monitoringName: 'Nombre de monitoreo',
  deviceId: 'ID del dispositivo',
  device: 'Dispositivo',
  lastMaturity: 'Última madurez',
  lastReading: 'Última lectura',
  elapsedTime: 'Tiempo transcurrido (hh:mm)',
  maxElapsedTime: 'Tiempo máximo transcurrido (min)',
  lastTemperature: (temperatureUnit: MaturixTemperatureUnit) =>
    `Última temperatura (${SpanishTree.temperatureUnit(temperatureUnit)})`,
  lastStrength: (strengthUnit: MaturixStrengthUnit) =>
    `Última resistencia (${SpanishTree.strengthUnit(strengthUnit)})`,
  temperatureWithUnit: (temperatureUnit: MaturixTemperatureUnit) =>
    `Temperatura (${SpanishTree.temperatureUnit(temperatureUnit)})`,
  strengthWithUnit: (strengthUnit: MaturixStrengthUnit) =>
    `Resistencia (${SpanishTree.strengthUnit(strengthUnit)})`,
  temperatureUnit: (temperatureUnit: MaturixTemperatureUnit): string => {
    switch (temperatureUnit) {
      case MaturixTemperatureUnit.CELCIUS:
        return 'Celsius'
      case MaturixTemperatureUnit.FAHRENHEIT:
        return 'Fahrenheit'
    }
  },
  strengthUnit: (strengthUnit: MaturixStrengthUnit): string => {
    switch (strengthUnit) {
      case MaturixStrengthUnit.KGPERCMSQ:
        return 'kg/cm²'
      case MaturixStrengthUnit.MPA:
        return 'MPa'
      case MaturixStrengthUnit.NPERMMSQ:
        return 'N/mm²'
      case MaturixStrengthUnit.PSI:
        return 'PSI'
    }
  },

  unconfirmed: 'No confirmado',
  confirmed: 'Confirmado',
  delivering: 'En entrega',
  paused: 'Pausado',
  onHold: 'En espera',

  ticketed: 'Tiquetado',
  recalled: 'Retirado',
  enRoute: 'En ruta',
  pouring: 'Vertiendo',
  delivered: 'Entregado',

  orderSummary: 'Resumen del pedido',
  payloadSummary: 'Resumen de la carga',

  orderStatus: 'Estado del pedido',
  totalVolume: 'Volumen total',
  deliveryLocation: 'Ubicación de entrega',
  mixType: 'Tipo de mezcla',
  slump: 'Asentamiento',
  spacingOrUnloadTime: 'Espaciado o tiempo de descarga',
  pouringMode: 'Modo de vertido',
  elementsToPour: 'Elementos a verter',
  extraCharge: 'Cargo extra',
  orderedBy: 'Ordenado por',

  load: 'Cargar',
  total: 'Total',
  timeOnSite: 'Tiempo en el sitio',
  ticket: 'Ticket',
  volumeLoaded: 'Volumen cargado',
  totalOrderVolume: 'Volumen total del pedido',
  additive: 'Aditivo',
  jobTimes: 'Tiempos de trabajo',
  startLoad: 'Inicio de carga',
  beginPouring: 'Comenzar a verter',
  washing: 'Lavando',
  totalTimeOnSite: 'Tiempo total en el sitio',
  inXHoursAndXMin: (hours: number, minutes: number) =>
    `En ${hours}h ${minutes}min`,
  xHoursAndXMinLate: (hours: number, minutes: number) =>
    `${hours}h ${minutes}min atrasado`,
  inXMin: (minutes: number) => `En ${minutes} min`,
  xMinLate: (minutes: number) => `${minutes} min atrasado`,
  isAboutToArrive: 'Está a punto de llegar',

  projected_time: 'Proyectado',
  recurringDelivery: 'Entrega recurrente',
  recurringWorkflow: 'Recurrente',
  endingXDate: (date: string) => `Finalizando ${date}`,
  isQuestionMandatory: '¿Es obligatoria la pregunta?',
  remainingDuration: 'Duración restante',
  baseUrl: 'URL base',
  siteId: 'ID del sitio',
  isActive: 'Está activo',
  syncStartDate: 'Fecha de inicio de sincronización',
  notUniqueActivityCodeMessage: (code: string) =>
    `La actividad con el código [${code}] ya existe. Prueba otro valor`,
  activityWasUpdated: 'La siguiente actividad fue actualizada',
  activityUpdated: 'Actividad actualizada',
  editXFieldsOnYActivities: (x: number, y: number) =>
    `Editar ${x} Campo${ending(x)} en ${y} ${activities(y)}`,
  editedXFieldsOnYActivities: (x: number, y: number) =>
    `${x} Campo${ending(x)} editado${ending(x)} en ${y} ${activities(y)}`,
  editXActivities_question: (x: number) => `¿Editar ${x} ${activities(x)}?`,
  onlyXOfYSelectedActivitiesCanBeChanged: (x: number, y: number) =>
    `Solo [${x}] de [${y}] ${activities(y)} seleccionada${ending(y)}  ` +
    `se puede${ending(x, '', 'n')} cambiar, ¿proceder?`,
  p6Viewer: 'Visor P6',
  scanner: 'Escáner',
  scannerApp: 'Aplicación de escáner',
  selectScanner: 'Seleccionar escáner',
  createNewScanner: 'Crear nuevo escáner',
  activitySetName: 'Nombre del conjunto de actividades',
  editActivitySet: 'Editar un conjunto de actividades',
  includeXFile: (ext: string) => `Incluir un archivo .${ext}`,
  includeDetailedEachForm: 'Incluir una vista detallada de cada Workflow',
  usersSubscribedTo: {
    deliveries: (uCount: number, iCount: number) =>
      `Suscrito ${uCount} ${users(uCount)} a ${iCount} ${deliveries(iCount)}`,

    activities: (uCount: number, iCount: number) =>
      `Suscrito ${uCount} ${users(uCount)} a ${iCount} ${activities(iCount)}`,
    forms: (uCount: number, iCount: number) =>
      `Suscrito ${uCount} ${users(uCount)} a ${iCount} ${workflows(iCount)}`,
  },
  usersUnSubscribedFrom: {
    deliveries: (uCount: number, iCount: number) =>
      `Dado de baja ${uCount} ${users(uCount)} a ${iCount} ${deliveries(
        iCount,
      )}`,
    activities: (uCount: number, iCount: number) =>
      `Dado de baja ${uCount} ${users(uCount)} a ${iCount} ${activities(
        iCount,
      )}`,
    forms: (uCount: number, iCount: number) =>
      `Dado de baja ${uCount} ${users(uCount)} a ${iCount} ${workflows(
        iCount,
      )}`,
  },
  scannerName: 'Nombre del escáner',
  scanMaster: 'Maestro de escaneo',
  scanStationReport: 'Informe de la estación de escaneo',
  materialTrackingReport: 'Informe de seguimiento de material',
  truckSize: 'Tamaño del camión',
  cancelationReason: 'Razón de cancelación',
  responsibleCompanies: 'Empresas responsables',
  noDataAvailable: 'Datos no disponibles',
  last7Days: 'Los últimos 7 días',
  last30Days: 'últimos 30 días',
  last120Days: 'Últimos 120 días',
  last365Days: 'Últimos 365 días',
  entireProject: 'Proyecto entero',
  frequencyAndPeriod: 'Frecuencia y periodo',
  activityDataLinking: {
    disableLinkingQuestion: (field: string) =>
      `Se deshabilitará el enlace a '${field}' externo. ¿Desea continuar?`,
    enableLinkingQuestion: (fieldValue: string, externalFieldValue: string) =>
      `¿Desea vincular '${fieldValue}' a '${externalFieldValue}' externo? Esto puede afectar a otras actividades.`,
    linkingIsDisabled: 'La vinculación de datos está desactivada',
    linkingIsEnabled: 'La vinculación de datos está activada',
    linkingDisabled: `Enlace de datos desactivado`,
    linkingEnabled: `Vinculación de datos activada`,
    editWarning: `Los cambios en la vinculación podrían afectar a otras actividades`,
    bulkEditWarning: `La vinculación de datos se desactivará para los campos editables`,
  },

  materialsUpload: 'Carga de materiales',
  plannedQuantity: 'Cantidad planificada',

  materialsUploading: {
    uploadMaterials: 'Subir materiales',
    goToTheMaterialsPage: 'Ir a la página de Materiales',
    uploadYourMaterialsFile:
      'Cargue su archivo de Materiales en formato Excel (.xlsx, .xlsm).',
    thereAreNoSheetsError:
      'No hay hojas en el archivo Excel proporcionado o se produjo un error al leer el archivo',
    materialCategoryLvl1: 'Categoría de material (Nivel 1)',
    materialLvl2: 'Materiales (Nivel 2)',
    materialDescription: 'Descripción del material',
    materialPlannedQuantity: 'Cantidad planificada de material',
    startingTheUpload: 'Iniciando la descarga',
    readingMaterialsFile: 'Leyendo el archivo de materiales',
    extractingMaterialsData: 'Extracción de materiales de datos',
    removingOldData: 'Eliminar datos antiguos',
    savingMaterialsData: 'Guardar datos de materiales',
    finishingUpload: 'Finalizando la carga',
    uploadOfMaterialsFailedAt: 'La carga de material ha fallado',
    uploadWasSuccessful: 'La carga fue exitosa',
    pleaseSelectSheetToUpload: (isMultiple: boolean) =>
      `${
        isMultiple ? 'El archivo actual contiene varias hojas. ' : ''
      }Seleccione una hoja del archivo para importar.`,
    makeSureLocationNamesUnique:
      'Asegúrese de que los nombres de las ubicaciones sean únicos en StruxHub',
  },

  materialsUploadHistory: {
    currentMaterials: 'Materiales actuales',
    projectUsesDefaultMaterials: 'El proyecto utiliza materiales estándar',
    latestUpload: 'Última carga',
    materialsRestoringConfirmation:
      'Confirmación de restauración de materiales',
    totalMaterialCategoriesAndMaterials:
      'Material total y categorías de materiales',
    restoreDefaultMaterials: 'Restaurar materiales predeterminados',
    doYouConfirmRestoreToDefaults:
      '¿Confirmar la restauración a los materiales predeterminados?',
    restoredSuccessfully: 'Restaurado con éxito',
    failedToRestore: 'No se pudo restaurar',
  },

  materialsUploadConfirm: {
    confirmDataMerge: 'Confirmar unión de datos',
    confirmDescription1: `Al cargar este archivo se actualizarán todos los valores modificados para
    los materiales existentes y se crearán nuevos registros para los materiales nuevos.`,
    confirmDescription2:
      'Si desea eliminar todos los datos de material existentes y reemplazarlos con datos de archivo,',
    confirmDescription3: 'haga clic aquí',
    replaceDescription: `¿Realmente desea eliminar todos los datos
    del material existente y reemplazarlos con datos del archivo cargado?`,
  },

  collapseAll: 'Colapsar todo',
  expandAll: 'Expandir todo',
  expandMenu: 'Expandir Menu',
  expandPanel: 'Expandir Panel',
  collapseMenu: 'Colapsar Menu',

  myUserProfile: 'Somos señor perfil',
  addTeammates: 'Añadir compañeros',
  addSummaryColumn: 'Agregar columna de resumen',
  configureColumns: 'Configurar columnas',
  summaryColumns: 'Columnas de resumen',
  receipt: 'Recibo',
  summary: 'Resumen',

  remainingQuantity: 'Cantidad restante',

  materialsInDeliveryFormWarning: {
    onlyXMaterialsForForm: (x: number) =>
      `Actualmente solo están habilitados ${x} material${ending(
        x,
        '',
        'es',
      )} para un Formulario de Entrega. Si esto es una limitación, comuníquese con`,
    andWeCanTalk: 'y nosotras podemos hablar',
  },

  deliverySuccessfullyCreated: 'Entrega creada exitosamente',

  xDates,

  materialDescription: 'Descripción del material',
  thisGroupAlreadyExists: 'Este grupo ya existe',

  upload: 'Subir',
  totalBooked: 'Total reservado',
  totalDelivered: 'Total entregado',
  doneDeliveries: 'Entregas finalizadas',

  xVendors: (x: number) => `${x} Proveedor${ending(x, '', 'es')}`,
  thisWeek: 'Esta semana',
  thisMonth: 'Este mes',

  youCannotChangeDelivery:
    'No puedes cambiar la entrega mientras esté en este estado',
  plannedInstallLocation: 'Ubicación de instalación planificada',
  plannedDeliveryLocation: 'Lugar de entrega planificado',
  currentLocation: 'Ubicación actual',
  procurementID: 'ID de adquisición',
  id_short: 'ID',
  readyToInstallQuantity: 'Listo para instalar cantidad',
  totalOnSiteQuantity: 'Cantidad total en el sitio',
  toMoveQuantity: 'Para mover cantidad',
  preciseLocation: 'Ubicación precisa',
  editPreciseLocation: 'Editar ubicación precisa',
  preciseLocationPreviewTooltip:
    'Esta vista previa se usa en el informe PDF. Ajusta el nivel de zoom para controlar cómo aparece el mapa.',
  zoomOverviewReportTooltip:
    'Este nivel de zoom también se usa como imagen de Vista General en los informes PDF para Ubicaciones de Flujo de Trabajo.',
  thisLocationIsNotGeolocated: 'Esta ubicación no está geolocalizada',
  notGeolocated: 'No geolocalizado',
  inheritsFrom: 'Hereda de',
  defaultsTo: 'Se ubica en',
  setPreciseLocation: 'Establecer ubicación precisa',
  movePinPosition: 'Mover la posición del pin',
  markupTools: 'Herramientas de marcado',
  bold: 'Negrita',
  italic: 'Cursiva',
  underline: 'Subrayado',
  fill: 'Relleno',
  border: 'Borde',
  thickness: 'Grosor',
  shortcuts: 'Atajos',
  gestures: 'Gestos',
  gesturePointsGroup: 'Puntos (línea / polígono)',
  gestureTextGroup: 'Texto',
  gestureMapGroup: 'Mapa',
  gestureSelectMarkup: 'Seleccionar una marca',
  gestureEditPoints: 'Editar puntos (línea / polígono)',
  gestureDeselect: 'Deseleccionar',
  gestureMovePoint: 'Mover un punto',
  gestureAddPoint: 'Añadir un punto',
  gestureRemovePoint: 'Eliminar un punto',
  gestureClosePolygon: 'Cerrar en un polígono',
  gestureRectangleCircle: 'Rectángulo / círculo',
  gestureLineArrow: 'Línea / flecha',
  gesturePolygonPolyline: 'Polígono / polilínea',
  gesturePenHighlighterEraser: 'Lápiz / resaltador / borrador',
  gestureAddText: 'Añadir texto',
  gestureEditText: 'Editar texto',
  gestureMoveResizeRotate: 'Mover / redimensionar / rotar',
  gestureRepositionPin: 'Reposicionar el marcador',
  gesturePan: 'Desplazar',
  gestureZoom: 'Zoom',
  gestureRotateTilt: 'Rotar e inclinar',
  gestureTap: 'Tocar',
  gestureTapAgain: 'Tocar de nuevo',
  gestureTapMap: 'Tocar el mapa',
  gestureDrag: 'Arrastrar',
  gestureTapMidpoint: 'Tocar un punto medio',
  gestureLongPress: 'Mantener pulsado',
  gestureTapFirstPoint: 'Tocar el primer punto',
  gestureTapOrDrag: 'Tocar o arrastrar',
  gestureDragOrTapEnds: 'Arrastrar, o tocar inicio y luego fin',
  gestureTapPointsClose: 'Tocar puntos; tocar el primero para cerrar',
  gestureDoubleTap: 'Tocar dos veces',
  gestureDragBodyCornerPin: 'Arrastrar cuerpo / esquina / marcador',
  gestureTapMoveThenDrag: 'Tocar Mover y luego arrastrar el mapa',
  gesturePinch: 'Pellizcar',
  gestureTwoFingerDrag: 'Arrastrar con dos dedos',
  customColor: 'Color personalizado',
  strokeWidth: 'Grosor del trazo',
  strokeWidthSlider: 'Control deslizante de grosor del trazo',
  borderWidth: 'Grosor del borde',
  borderWidthSlider: 'Control deslizante de grosor del borde',
  backgroundColor: 'Color de fondo',
  textColor: 'Color del texto',
  textSize: 'Tamaño del texto',
  fontSize: 'Tamaño de fuente',
  moveToolbar: 'Mover barra de herramientas',
  move: 'Mover',
  savePreciseLocationPosition: '¿Guardar la posición de la ubicación precisa?',
  dontSave: 'No guardar',
  dragTheMapToChangeLocationPinPosition:
    'Arrastra el mapa para editar la posición del pin',
  addMarkups: 'Agregar anotaciones',
  markUpPhoto: 'Anotar foto',
  photoMarkupLoadFailed: 'No se pudo cargar esta foto',
  untitledWorkflow: 'Flujo de trabajo sin título',
  fromDrawing: 'Desde el dibujo',
  continueWorkflow: 'Continuar flujo de trabajo…',
  collapse: 'Contraer',
  expand: 'Expandir',
  createWorkflow: 'Crear flujo de trabajo',
  holdToCreateWorkflow: 'Crear un flujo de trabajo',
  holdToCreateWorkflowHint:
    'Mantén pulsado en cualquier lugar del mapa para colocar un pin e iniciar un flujo de trabajo.',
  gotIt: 'Entendido',
  enterWorkflowTitle: 'Ingrese un título',
  discard: 'Descartar',
  discardWorkflowDraft: '¿Descartar el borrador del flujo de trabajo?',
  discardWorkflowDraftQuestion:
    'Perderás el pin, lo ingresado en el formulario y cualquier anotación dibujada.',
  workflowTitleSubmitted: (name: string) => `${name} enviado`,
  userStamp: 'Sello de usuario',
  userStampTooltip: 'Incluye el nombre del usuario firmante con la firma.',
  inventoryTracking: 'Campos de Seguimiento',
  inventoryTrackingTooltip:
    'Cuando está habilitado, agrega campos de cantidad y ubicación para rastrear el inventario de recursos materiales.',
  moveToMyLocation: 'Ir a mi ubicación',
  noCoordinatesAvailable: 'No hay coordenadas disponibles',
  geolocationPermissionDenied:
    'Permiso de geolocalización denegado. Por favor, habilite el acceso a la ubicación en la configuración de su navegador.',
  geolocationPositionUnavailable:
    'La información de ubicación no está disponible. Por favor, verifique la configuración de ubicación de su dispositivo.',
  geolocationTimeout:
    'La solicitud de ubicación ha expirado. Por favor, inténtelo de nuevo.',
  geolocationError:
    'Ocurrió un error al obtener su ubicación. Por favor, inténtelo de nuevo.',
  dismiss: 'Descartar',
  pictograms: {
    names: {
      building: 'Edificio',
      level: 'Nivel',
      zone: 'Zona',
      area: 'Área',
      route: 'Ruta',
      door: 'Puerta',
      gate: 'Portón',
      shaft: 'Ducto',
      'loading-zone': 'Zona de carga',
      'restrooms-men': 'Baño de hombres',
      entrance: 'Entrada',
      exit: 'Salida',
      'access-a': 'Acceso A',
      'access-b': 'Acceso B',
      'access-c': 'Acceso C',
      'access-d': 'Acceso D',
      'access-e': 'Acceso E',
      'access-f': 'Acceso F',
      'no-access-1': 'Prohibido el paso 1',
      'no-access-2': 'Prohibido el paso 2',
      'no-access-3': 'Prohibido el paso 3',
      'pedestrian-walkway': 'Pasillo peatonal',
      elevator: 'Ascensor',
      stairs: 'Escaleras',
      parking: 'Estacionamiento',
      'bike-parking': 'Estacionamiento de bicicletas',
      'buggy-parking': 'Estacionamiento de carritos',
      'hi-vis-apparel': 'Ropa de seguridad de alta visibilidad',
      'safety-helmet': 'Casco de seguridad',
      'safety-gloves': 'Guantes de seguridad',
      'safety-boots': 'Botas con puntera de seguridad',
      'not-required-all': 'No obligatorio: todo',
      'not-required-apparel': 'No obligatorio: ropa',
      'not-required-helmet': 'No obligatorio: casco',
      'not-required-gloves': 'No obligatorio: guantes',
      'not-required-boots': 'No obligatorio: botas',
      'visitor-check-in': 'Registro de visitantes',
      'admin-office': 'Oficina administrativa',
      'electrical-room': 'Sala eléctrica',
      restrooms: 'Baños',
      'restrooms-women': 'Baño de mujeres',
      'break-area': 'Área de descanso',
      'hydration-station': 'Estación de hidratación',
      'smoking-area': 'Área de fumadores',
      canteen: 'Comedor',
      break: 'Descanso',
      'wash-area': 'Área de lavado',
      dumpster: 'Contenedor de basura',
      'brand-oes': 'OES Equipment',
      'brand-sunbelt': 'Sunbelt Rentals',
      'brand-united-rentals': 'United Rentals',
      'brand-equipmentshare': 'EquipmentShare',
      'brand-feco': 'First Equipment Company',
      deliveries: 'Entregas',
      'construction-vehicles': 'Vehículos de construcción',
      trailer: 'Remolque',
      'security-checkpoint': 'Control de seguridad',
      'temporary-water': 'Agua temporal',
      'equipment-storage': 'Almacén de equipos',
      'material-boxes': 'Almacén de materiales: cajas',
      'material-wood': 'Almacén de materiales: madera',
      'material-steel-1': 'Almacén de materiales: acero 1',
      'material-plumbing': 'Almacén de materiales: plomería',
      'material-steel-2': 'Almacén de materiales: acero 2',
      'construction-debris-1': 'Escombros de construcción 1',
      'construction-debris-2': 'Escombros de construcción 2',
      'information-area': 'Área de información',
      'site-map': 'Plano del sitio',
      'first-aid': 'Primeros auxilios',
      aed: 'Desfibrilador externo automático',
      'covid-checkpoint': 'Control de salud',
      'fire-extinguisher': 'Extintor',
      'muster-point': 'Punto de encuentro',
      'exit-running': 'Salida (figura corriendo)',
      cleanroom: 'Sala limpia / vestimenta',
      'icra-zone': 'Zona ICRA',
      'negative-pressure': 'Presión negativa',
      'esd-zone': 'Zona ESD',
      'no-photography': 'Prohibido tomar fotos',
      'no-drones': 'Prohibido drones',
      'fall-hazard': 'Peligro de caída / borde abierto',
      'silica-dust': 'Sílice / polvo',
      noise: 'Ruido',
      'overhead-load': 'Carga suspendida / giro de grúa',
      excavation: 'Excavación',
      'hot-work': 'Trabajo en caliente',
      'confined-space': 'Espacio confinado',
      'arc-flash': 'Alto voltaje / arco eléctrico',
      stop: 'Alto',
      yield: 'Ceda el paso',
      'speed-limit': 'Límite de velocidad',
      'one-way': 'Un sentido',
      flagger: 'Banderero adelante',
      'truck-crossing': 'Cruce de camiones',
      'fire-lane': 'Carril de bomberos — no obstruir',
      'concrete-washout': 'Lavado de concreto',
      'storm-drain': 'Protección de coladeras',
      'erosion-control': 'Control de erosión',
      'wheel-wash': 'Lavado de llantas',
      'spill-kit': 'Kit para derrames',
      scaffold: 'Andamio',
      'swing-stage': 'Andamio colgante',
      'mast-climber': 'Plataforma trepadora',
      eyewash: 'Lavaojos / ducha de seguridad',
      standpipe: 'Toma de agua / manguera',
      'fire-hydrant': 'Hidrante',
      'alarm-pull-station': 'Estación manual de alarma',
      'crane-zone': 'Zona de grúa / izaje',
      'brand-herc-rentals': 'Herc Rentals',
    },
  },
  iconPicker: {
    library: 'Biblioteca',
    companyLogos: 'Logotipos de empresas',
    selectIcon: 'Seleccionar icono',
    xIcons: (x: number) => `${x} Icono${ending(x)}`,
    noResults: 'No se encontraron iconos',
    libraryIconColor:
      'El color se aplica a rótulos, etiquetas y calendarios, no al icono, que tiene sus propios colores',
  },
  formMaterialSubFields: {
    materialHasQuantityAndLocation:
      'El material tiene campos Cantidad y Ubicación que también puede agregar/editar a continuación.',
    clickToEnableQuantity: 'Haga clic aquí si desea habilitar Cantidad.',
    clickToEnableLocation: 'Haga clic aquí si desea habilitar la Ubicación.',
  },
  newFormType: 'Nuevo tipo de Workflow',
  materialTransferId: 'ID de transferencia de material',
  materialTransferDate: 'Fecha de transferencia de material',
  newTransfer: 'Nueva transferencia',
  doneTransfers: 'Transferencias hechas',
  totalTransferred: 'Total transferido',
  xTransfers: (x: number) => `${x} Transfer${ending(x, 'ir', 'encias')}`,

  youCanSelectMultipleOptions: 'Puedes seleccionar múltiples opciones',
  updateStatus: 'Actualizar Estado',

  review_verb: 'Revisar',
  reviewed: 'Revisado',
  activated: 'Activado',
  submitted: 'Enviado',
  workflowIsDoneOrDenied: (isDenied: boolean) =>
    `El Workflow ${isDenied ? 'es negado' : 'está hecho'}`,
  workflowIsEnded: 'El Workflow está terminado',
  request_noun: 'Solicitud',
  review_noun: 'Revisar',
  recurringInspection: 'Inspección recurrente',
  assignAsBicWhenWorkflowMatches:
    'Asignar como Ball-in-Court cuando el Workflow coincida con',
  thisWorkflowStepIsAssignedByRules:
    'Este paso del Workflow se asigna según las siguientes reglas',
  adminsCanAdvanceWorkflow:
    'Los administradores pueden avanzar el Workflow independientemente de los usuarios asignados.',
  completedBy: 'Completado por',
  anyAssignedUser: 'Cualquier usuario asignado',
  allAssignedUsers: 'Todos los usuarios asignados',
  allAssignedCompanies: 'Todas las empresas asignadas',
  allAssignedUsersAndCompanies: 'Todos los usuarios/empresas asignados',
  oneUserPerRule: 'Un usuario por regla',
  anyAssignedUserDescription:
    'El primer usuario asignado que completa este paso hace avanzar el Workflow.',
  allAssignedUsersDescription:
    'Cada usuario asignado, y una persona de cada empresa asignada, debe completar este paso antes de que el Workflow avance.',
  oneUserPerRuleDescription:
    'Un usuario de cada regla debe completar este paso antes de que el Workflow avance.',
  assignees: 'Asignados',
  optionalInParentheses: '(opcional)',
  workflowCompany: 'Empresa del Workflow',
  completeForWhichRule: '¿Completar para qué regla?',
  countsTowardOneRule:
    'Está asignado por más de una regla en este paso. Completarlo cuenta para una de ellas.',
  ruleName: 'Nombre de la regla',
  allUsersMustCompleteDisabledTooltip:
    'Agregue una regla de usuario BIC para habilitar esta opción.',
  xOfYComplete: (x: number, y: number) => `${x} de ${y} completados`,
  noFieldsSubmitted: 'No se enviaron campos',
  submittedAt: 'Enviado el',
  xRules: (x: number) => `${x} regla${ending(x)}`,
  step: 'Paso',
  stepCompletion: 'Finalización del paso',
  bicUser: 'BIC usuario',
  formSection: 'Sección de Workflow',
  actions: 'Acciones',
  frequency: 'Frecuencia',
  deadline: 'Fecha límite',
  automaticStart: 'Inicio automático',
  addStep: 'Agregar paso',

  workflowConfDescr: {
    mustContainApproval:
      'El Workflow con un paso de Solicitud debe contener un paso de Aprobación',
    cannotBeTheLastStep: (stepName: string) =>
      `${stepName} no puede ser el último paso, agregue otro o elimine este.`,
    workflowStartsOnDate:
      'El Workflow comienza automáticamente en la fecha de inicio.',
    automaticallyStart:
      'Iniciar automáticamente el Workflow en la fecha de inicio',
    replaceWithStep: (stepType: string): string =>
      `Reemplace con el paso ${stepType}`,
    replaceWithStart: 'Reemplace con el paso Iniciar',
    deleteWorkflowStep: 'Eliminar paso del Workflow',
    removeStepDialogMsg: '¿Quieres eliminar este paso del Workflow?',
    toastUnsavedMsg: 'Los cambios realizados se han restablecido',
    recurringHasConstr: 'El paso de inspección recurrente tiene limitaciones:',
    toggleDialogMsg: (isManualStart: boolean): string =>
      `¿Cambiarlo a un paso de inicio ${
        isManualStart ? 'Manual' : 'Automático'
      }?`,
    selectStepMsg:
      'Seleccione un paso del Workflow para agregar/modificar campos',
  },
  workflowConfTooltips: {
    bicBlockFirstLine: `Asigna uno o más usuarios del Workflow como BIC para este paso. Los usuarios BIC son responsables de completar el paso antes de que el Workflow pueda progresar.`,
    bicBlockSecondLine: `Las reglas pueden usarse para asignar usuarios dinámicamente basados en los campos de StruxHub, como Ubicación, Equipo o Empresa`,
  },
  xWorkflowsShowing: (x: number) => `Mostrando ${x} ${workflows(x)}`,
  workflowTableText: {
    seeDescr: 'Ver descripciones de los pasos',
    stepCategory: 'Categoría de paso del Workflow',
    workflowStep: 'Paso del Workflow',
    constraints: 'Restricciones',
    bicText: '¿Se puede especificar el usuario de la pelota en la cancha?',
    multipleText: '¿Se pueden tener varias instancias en el Workflow?',
    submissionDescr:
      'Iniciando el paso del Workflow para enviar un formulario que no requiere aprobación posterior.',
    submissionConstr: 'Solo puede iniciar un Workflow.',
    requestDescr: 'Iniciando paso del Workflow para solicitar una aprobación.',
    requestConstr:
      'Solo puede iniciar un Workflow. Debe tener un paso de Aprobación posterior.',
    reviewDescr:
      'Paso de avance general de un Workflow que se puede utilizar para aprobaciones previas.',
    approvalDescr:
      'Aprobación de una solicitud de Workflow que se puede asignar a usuarios específicos.',
    approvalConstr: 'Debe tener un paso de Solicitud anterior.',
    startDescr:
      'Inicio de un Workflow iniciado al hacer clic en el botón «Iniciar».',
    autoStartDescr:
      'Inicio de un Workflow que se inicia automáticamente en la fecha de inicio del Workflow.',
    onSiteDescr:
      'Se utiliza para indicar la llegada de un vehículo de reparto a la ubicación.',
    readyToInspectDescr:
      'Paso intermedio del Workflow utilizado para notificar a los inspectores.',
    inspectionDescr: 'Paso de inspección general.',
    recurringDescr: 'Inspecciones recurrentes con una frecuencia específica.',
    recurringConstr:
      'Debe estar precedido directamente por un paso de Inicio o de Inicio automático.',
    finishDescr:
      'Finalización de un Workflow marcado al hacer clic en el botón «Listo».',
  },
  workflowSteps: 'Pasos del Workflow',
  workflowDiagram: 'Diagrama de Workflow',
  configurator: 'Configurador',
  workflowStepConfigurator: 'Configurador de pasos del Workflow',
  restoreToLastSavedState: 'Restaurar al último estado guardado',
  endOfTheDay: 'Fin del día',
  everyX_withoutNum: (x: number) => `Cada${ending(x, '', '')}`,
  submission: 'Envío',
  initiation: 'Iniciación',

  deleteWorkflow: 'Eliminar el Workflow',
  workflowDeletionDialogDescr: {
    confirmDelete:
      'Eliminar este Workflow eliminará el Workflow y todas las notas y datos asociados del proyecto.',
    confirmDelete2: 'Haga esto solo si la solicitud de Workflow fue un error.',
  },
  draftWorkflowDeletionDialogDescr: {
    confirmDelete:
      'Esto eliminará permanentemente el Workflow en borrador del proyecto.',
  },
  emailPdfReport: 'Informe PDF por correo electrónico',
  unsubscribe: 'Cancelar suscripción',
  followFormMenuDescr: {
    toReceive: 'Para recibir notificaciones de Workflow',
    toStop: 'Para dejar de recibir notificaciones de Workflow',
  },
  workflowDeleteMenuDescr: {
    isDone: 'El Workflow está hecho/terminado, no se puede eliminar.',
    onlyFor: `Solo disponible para administradores,
    maestros de Workflow, solicitantes, asignados`,
  },
  configureTableColumns: 'Configurar columnas de tabla',
  searchProperties: 'Buscar propiedades',
  shownInTable: 'Se muestra en la tabla',
  hiddenInTable: 'Escondido en la tabla',
  visibleLayers: 'Capas visibles',
  hiddenLayers: 'Capas ocultas',
  finishingUpload: 'Finalizando la subida',
  processingTheUpload: 'Procesando la subida',
  uploadSucceeded: 'Subida completada',
  uploadFailed: 'Error en la subida',
  replaced: 'Reemplazado',
  pleaseWaitThisMayTakeAWhile: 'Espere, esto puede tardar un poco',
  aNewerVersionOfThisFileWasUploaded:
    'Se subió una versión más reciente de este archivo.',
  somethingWentWrongPleaseTryAgain: 'Algo salió mal: inténtelo de nuevo.',
  freezeColumns: 'Congelar columnas arriba',
  restoreDefaults: 'Restaurar los valores predeterminados',
  rememberChoice: 'Recordar mi elección',
  successfullyDeletedForms: (x: number) =>
    `Se eliminaron con éxito ${x} ${workflows(x)}`,
  failedToDelete: 'No se pudo eliminar',
  failedToArchive: 'No se pudo archivar',
  failedToUnarchive: 'No se pudo desarchivar',
  failedToLoadUsageData:
    'No se pudieron cargar los datos de uso. Inténtelo de nuevo.',
  retry: 'Reintentar',
  cannotDeleteDoneForms: 'No se pueden eliminar los Workflows listos',
  cannotDeleteSystemFormsTooltip:
    'La eliminación de Forms predeterminados está prohibida',
  getMobileNativeApp: 'Descarga la app',
  getMobileNativeApp_long: 'Descarga la aplicación móvil de StruxHub',
  getAppOnGoogle: 'Disponible en Google Play',
  getAppOnApple: 'Descárgalo en el App Store',
  oneTimePopup: {
    title: '¡Descarga nuestra nueva aplicación móvil!',
    body: 'Recibe actualizaciones en tiempo real y realiza más trabajo en el campo con StruxHub.',
    dismiss: '¡Ok, entendido!',
  },

  enterX: (x: string) => `Introducir ${x}`,
  selectX: (x: string) => `Seleccionar ${x}`,
  saveMapView: 'Guardar vista',
  saveAsNewMapView: 'Guardar como vista nueva',
  restoreMapView: 'Restaurar vista',
  moreSaveOptions: 'Más opciones de guardado',
  aboutX: (x: string) => `Acerca de ${x}`,
  selectNestedItems: 'Seleccionar elementos anidados',
  deselectNestedItems: 'Deseleccionar elementos anidados',
  createNewX: (x: string) => `Crear ${x}`,
  editX: (x: string) => `Editar ${x}`,
  hoursUnit: 'horas',
  enterProjectCode: 'Introduzca el código del proyecto',
  somethingWentWrongTryAgainLater:
    'Algo salió mal, ¡inténtalo de nuevo más tarde!',
  unsubscribedSuccessfully: 'Cancelada la suscripción exitosamente!',
  exitScanner: 'Salir del escáner',
  deleteScanner: 'Eliminar escáner',
  editScannerText: 'Editar escáner',
  shouldDeleteScanner: 'Debe eliminar el escáner',
  startScanner: 'Activar escáner',
  queue: 'Cola',
  startQueue: 'Iniciar La Cola',
  addedToQueue: 'Agregado A La Cola',
  startSession: 'Iniciar Sesión',
  allowedUsers: 'Usuarios permitidos',
  badgeDescription: 'Descripción de la insignia',
  makeItTimed:
    "Haga de este un escáner 'Cronometrado' para los usuarios. " +
    'Scan-Start : Scan-Stop = Total Time',
  limitAccess:
    "Limitar el acceso a usuarios, equipos o empresas con 'Insignias' específicas",
  endRideConfirm: '¿Estás seguro de que quieres terminar el viaje?',
  changeModeConfirm:
    '¿Estás seguro de que quieres cambiar el modo de conducción?',
  startSessionConfirm: '¿Estás seguro de que quieres comenzar la sesión?',
  isScannerTimedByDay:
    'El temporizador FINALIZA automáticamente al finalizar la jornada laboral.',
  endRide: 'Fin del viaje',
  scanTo: 'Escanear a',
  activeUsers: 'Usuarios activos:',
  scannerActive: 'Escáner activo',
  scannerInactive: 'Escáner inactivo',
  pauseScanner: 'Pausa el escáner',
  resumeScanner: 'Reanudar escáner',
  endSessionForAll: 'Fin de sesión para todos',
  formName: 'Tipo de Workflow',
  undo: 'Deshacer',
  redo: 'Rehacer',
  icon: 'Icono',
  color: 'Color',
  parent: 'Elemento superior',
  lbsParent: 'Padre LBS',
  lbsParentDescription:
    'La LBS (estructura de desglose de ubicaciones) define dónde se encuentra un Recurso dentro del Proyecto. Seleccione la Ubicación Principal de este Recurso.',
  newLocationNeedsParent: 'Una nueva ubicación necesita un padre LBS.',
  locationTypeNotUsableInWorkflows:
    'Este tipo de ubicación todavía no se puede usar en un Workflow.',
  uncreatableLocationTypesSkipped:
    'Se omitieron ubicaciones de un tipo que este proyecto no puede crear.',
  locationTypesStillLoading:
    'Los tipos de ubicación todavía se están cargando. Inténtelo de nuevo en un momento.',
  locationTypesFailedToLoad:
    'No se pudieron cargar los tipos de ubicación. Recargue la página para intentarlo de nuevo.',
  uncreatableLocationType:
    'Este proyecto no puede crear este tipo de ubicación.',
  showInApp: 'Mostrar en la Aplicación',
  allowCreation: 'Permitir creación',
  activeNotActive: 'Activo / No activo',
  notActive: 'No activo',
  allowCreationInfo:
    'Activa o desactiva la creación de este tipo de flujo de trabajo.',
  creationAccess: 'Acceso de creación',
  creationAccessInfo:
    'Define quién puede crear este tipo de flujo de trabajo.\nTodos: cualquier miembro del proyecto.\nSolo administradores: reservado para administradores.',
  adminsOnly: 'Solo administradores',
  everyone: 'Todos',
  blockRequestsForNonWorkTimes: 'Bloquear solicitudes de tiempo no laboral',
  general: 'General',
  steps: 'Pasos',
  views: 'Vistas',
  confirmation: 'Confirmación',
  deletionOfDefaultForms:
    'La eliminación de Workflows predeterminados está prohibida',
  theFollowingEntitiesWereUpdated: 'Se actualizaron las siguientes entidades',
  globeViews: 'Vistas Globo',
  whiteboards: 'Pizarras',
  whiteboard: 'Pizarra',
  georeferenced: 'Georreferenciado',
  nonGeoreferenced: 'No georreferenciado',
  areYouSureDeleteMapView: '¿Está seguro de que desea eliminar este mapa?',
  areYouSureDeleteTileset:
    '¿Está seguro de que desea eliminar este conjunto de teselas?',
  areYouSureDeleteRemoteTileset:
    '¿Está seguro de que desea eliminar este conjunto de teselas remoto?',
  addRemoteTileset: 'Añadir conjunto de teselas remoto',
  fileProperties: 'Propiedades del archivo',
  shapeProperties: 'Propiedades de la forma',
  createLocationWithThisShape: 'Crear ubicación con esta forma',
  latitude: 'Latitud',
  longitude: 'Longitud',
  zoom: 'Zoom',
  zoomIn: 'Acercar',
  zoomOut: 'Alejar',
  parts: 'Partes',
  geometryType: 'Tipo de geometría',
  noMapViewsConfigured:
    'Aún no hay vistas de mapa configuradas para este proyecto.',
  noMapViewsMatchingX: (x: string) =>
    `Ninguna vista de mapa coincide con "${x}".`,
  mapViewsManager: 'Administrador de vistas de mapa',
  selectMapView: 'Seleccionar vista de mapa',
  xMapViews: (x: number) => `${x} vistas de mapa`,
  xOfYMapViews: (visible: number, total: number) =>
    `${visible} de ${total} vistas de mapa`,
  createdAt: 'Creado el',
  byLBS: 'Por LBS',
  byLocation: 'Por ubicación',
  byType: 'Por tipo',
  collapseOneLevel: 'Contraer un nivel',
  expandOneLevel: 'Expandir un nivel',
  previousMapView: 'Vista de mapa anterior',
  nextMapView: 'Vista de mapa siguiente',
  closeMapViews: 'Cerrar vistas de mapa',
  mapViewsBrowseAndSelect:
    'Explore y seleccione una vista de mapa para el proyecto.',
  selectAssociatedResource: 'Seleccionar recurso asociado',
  selectPermittedResources: 'Seleccionar recursos permitidos',
  selectResources: 'Seleccionar recursos',
  openActionsForX: (x: string) => `Abrir acciones para ${x}`,
  openX: (x: string) => `Abrir ${x}`,
  openAMap: 'Abrir un mapa',
  backToX: (x: string) => `Volver a ${x}`,
  selectMapViewX: (x: string) => `Seleccionar vista de mapa ${x}`,
  available: 'Disponible',
  useMapLabels: 'Mostrar nombres de lugares',
  notifiedUsers: 'Usuarios notificados',
  selectNotificationUsers:
    'Seleccione los usuarios que recibirán una notificación después de completar este paso',
  wouldYouEnableNotificationsForSteps:
    '¿Le gustaría habilitar las notificaciones para este campo en todos los pasos del Workflow?',
  allowNotificationsFor: (fieldName: string) =>
    `Permitir notificaciones para ${fieldName}`,
  notifyFor: 'Notificar para',
  selectWorkflowSteps: 'Seleccionar pasos del Workflow',
  allSteps: 'Todos los pasos',
  workflowBookingDeadline: 'Fecha límite para la reserva de Workflows',
  workflowDeadlineDescr: `Los Workflows deben ser solicitados antes de la fecha límite.
Establezca cuántos días de antelación a continuación.
El interruptor puede configurarse para bloquear las solicitudes tardías de Workflows.
El usuario recibirá una advertencia y deberá elegir un día más lejano`,
  workflowsMustBeRequested:
    'Los Workflows deben ser solicitados con antelación',
  workflowWasntRequestedInAdvance: (interval: string) =>
    `Este Workflow no fue solicitado${
      interval ? ` ${interval}` : ''
    } con antelación`,
  workflowDateTimeBlocked: 'La fecha y hora del Workflow están bloqueadas',
  pleaseMoveThisWorkflow: (days: number) =>
    `Por favor, mueva este Workflow. Las solicitudes deben ser ${days} día${ending(
      days,
    )} laborable${ending(
      days,
    )} con Antelación a la fecha y hora solicitadas y en una fecha-hora laborable`,
  requestsMustBeInAdvance: (days: number) =>
    `Las solicitudes deben ser ${days} día${ending(days)} laborable${ending(
      days,
    )} con Antelación a la fecha y hora solicitadas`,
  hide: 'Ocultar',
  warnUsers: 'Advertir a los Usuarios',
  blockUsers: 'Bloquear a los Usuarios',
  duplicateCurrentWorkflow: 'Duplicar el Workflow actual',
  mapNotifications: 'Notificaciones de mapas',
  olderWorkflowCantBeDuplicate:
    'No se puede duplicar la versión anterior del Workflow.',
  invalidNumberFormat: 'Formato de número inválido',

  restrooms: 'Baños',
  canteen: 'Comedor',
  toilet: 'Baño',
  breakText: 'Descanso',
  dumpster: 'Basurero',
  elevator: 'Ascensor',
  electricalRoom: 'Sala eléctrica',
  access: 'Acceso',
  washArea: 'Área de lavado',
  medicalArea: 'Área médica',
  musterPoint: 'Punto de encuentro',
  parking: 'Estacionamiento',
  smokingArea: 'Área de fumadores',
  stairway: 'Escalera',
  covidCheckpoint: 'Punto de control Covid',
  breakArea: 'Área de descanso',
  walkway: 'Paso peatonal',
  trailer: 'Caseta de obra',
  informationPoints: 'Puntos de información',
  glyphNames: {
    truck: 'Camión',
    'concrete-mixer-truck': 'Camión revolvedor',
    'equipment-storage': 'Almacén de equipo',
    'material-storage-boxes': 'Almacén: cajas',
    'material-storage-wood': 'Almacén: madera',
    'material-storage-steel': 'Almacén: acero',
    'material-storage-pipes': 'Almacén: tubería',
    'material-lifting-steel': 'Izaje de acero',
    scaffolding: 'Andamio',
    'swing-stage': 'Andamio colgante',
    'personnel-lift': 'Elevador de personal',
    info: 'Info',
    'site-map': 'Plano de la obra',
    'log-out': 'Salida',
    'no-entry': 'No entrar',
    stop: 'Alto',
    'no-pedestrians': 'Prohibido peatones',
    'no-access': 'Acceso prohibido',
    'security-checkpoint-window': 'Control de seguridad',
    'visitor-check': 'Registro de visitantes',
    'high-visibility-vest': 'Chaleco de alta visibilidad',
    'safety-helmet': 'Casco de seguridad',
    'safety-gloves': 'Guantes de seguridad',
    'safety-toe-boots': 'Botas con casquillo',
    'ppe-not-required': 'EPP no requerido',
    'no-vest': 'Sin chaleco',
    'no-helmet': 'Sin casco',
    'no-gloves': 'Sin guantes',
    'no-boots': 'Sin botas',
    'cleanroom-gowning': 'Sala limpia / vestimenta',
    'icra-zone': 'Zona ICRA',
    'negative-pressure': 'Presión negativa',
    'esd-zone': 'Zona ESD',
    'no-photography': 'Prohibido tomar fotos',
    'no-drones': 'Prohibido drones',
    'fall-hazard': 'Peligro de caída',
    'silica-dust': 'Sílice / polvo',
    noise: 'Ruido',
    'confined-space': 'Espacio confinado',
    'energized-equipment': 'Equipo energizado',
    'bike-parking': 'Estacionamiento de bicicletas',
    'utility-cart': 'Carrito utilitario',
    'loading-zone': 'Zona de carga',
    'fire-lane': 'Carril de bomberos',
    'flagger-ahead': 'Banderero adelante',
    'one-way': 'Un sentido',
    'speed-limit': 'Límite de velocidad',
    trash: 'Basura',
    'admin-office': 'Oficina administrativa',
    'hand-wash-station': 'Estación de lavado de manos',
    'spill-kit': 'Kit para derrames',
    'erosion-control': 'Control de erosión',
    'storm-drain-inlet-protection': 'Protección de coladeras',
    'concrete-washout': 'Lavado de concreto',
    'wheel-wash': 'Lavado de llantas',
    'restroom-men': 'Baño de hombres',
    'restroom-women': 'Baño de mujeres',
    sofa: 'Sala de descanso',
    hydration: 'Hidratación',
    'heart-pulse': 'DEA',
    eyewash: 'Lavaojos',
    'fire-extinguisher': 'Extintor',
    'fire-hydrant': 'Hidrante',
    'fire-alarm-button': 'Botón de alarma',
    'standpipe-hose': 'Toma de agua / manguera',
    flame: 'Trabajo en caliente',
    excavator: 'Excavadora',
    'overhead-load': 'Carga suspendida',
    'brand-oes': 'OES Equipment',
    'brand-sunbelt': 'Sunbelt Rentals',
    'brand-united-rentals': 'United Rentals',
    'brand-equipmentshare': 'EquipmentShare',
    'brand-feco': 'First Equipment Company',
    'brand-herc-rentals': 'Herc Rentals',
  },
  informationPointTypes: {
    general_information_point: 'Punto de información general',
    access: 'Acceso',
    exit: 'Salida',
    restricted_area: 'Área restringida',
    security_checkpoint: 'Control de seguridad',
    ppe_zone: 'Zona de EPP',
    controlled_environment: 'Ambiente controlado',
    site_rules: 'Reglas de la obra',
    hazard_zone: 'Zona de peligro',
    confined_space: 'Espacio confinado',
    energized_equipment: 'Equipo energizado',
    walkway: 'Paso peatonal',
    parking: 'Estacionamiento',
    loading: 'Carga',
    traffic_control: 'Control de tráfico',
    waste: 'Desechos',
    stormwater_erosion_control: 'Aguas pluviales y erosión',
    elevated_access: 'Acceso elevado',
    site_office: 'Oficina de obra',
    trailer: 'Caseta de obra',
    electrical_room: 'Sala eléctrica',
    utilities: 'Suministros',
    restrooms: 'Baños',
    break_area: 'Área de descanso',
    canteen: 'Comedor',
    hydration_station: 'Estación de hidratación',
    handwash_station: 'Estación de lavado de manos',
    smoking_area: 'Área de fumadores',
    medical_area: 'Área médica',
    fire_protection: 'Protección contra incendios',
    muster_point: 'Punto de encuentro',
  },
  informationPointGroups: {
    general: 'General',
    entry_restrictions: 'Acceso y restricciones',
    hazards: 'Peligros',
    site_logistics: 'Logística de obra',
    offices_utilities: 'Oficinas y suministros',
    restrooms_breaks: 'Baños y descanso',
    emergency: 'Emergencias',
  },
  informationPointPicker: {
    title: 'Colocar un punto',
    byType: 'Por tipo',
    byIcon: 'Por icono',
    xTypes: (x: number) => `${x} Tipo${ending(x)}`,
  },

  shaft: 'Eje',
  crane: 'Grúa',
  hoist: 'Polipasto',
  aerialLift: 'Elevador aéreo',
  forkLift: 'Montacargas',
  gradall: 'Gradall',
  allFormsSharingCalendarEnabled:
    'Se habilitó el uso compartido de todos los Workflows en el calendario',
  allDeliveriesSharingCalendarEnabled:
    'Habilitó todas las entregas compartidas en el calendario',
  allFormsSharingNotificationEnabled:
    'Habilitó el uso compartido de todos los Workflows en notificaciones',
  allDeliveriesSharingNotificationEnabled:
    'Habilitó todas las entregas compartidas en notificaciones',

  workflowTitle: 'Título del Workflow',
  workflowStatus: 'Estado del Workflow',

  openWorkflow: 'Abrir Workflow',
  attachmentPhotoAdded: 'Adjunto/Fotografía ha sido añadida',
  attachment: 'Anexo',
  sharingAllFormsToCalendarAndNotificationsEnabled:
    'Se habilitó compartir todos los Workflows en el calendario y las notificaciones',
  sharingAllDeliveriesToCalendarAndNotificationsEnabled:
    'Se habilitó compartir todas las entregas en el calendario y las notificaciones',
  fromRules: 'De reglas',
  calendarLinkInputText:
    'Puedes proporcionar este enlace a aplicaciones de calendario de terceros como Google Calendar, TeamUp, Outlook, etc.',
  enableIcal: 'Habilitar iCalendar',
  generate: 'Generar',

  ended: 'Terminado',
  externalWorkflowSharing: 'Compartir Workflow externo',
  addOtherStxCalendarsToProject:
    'Agregar otros calendarios de StruxHub al proyecto',

  shareBasedOnSettingsIn: 'Compartir según la configuración en',
  ifSelected: 'Si se selecciona',
  onlyWorkflowsSatisfyingModuleRulesWillBeShared: (module: string) =>
    `, solo se compartirán los Workflows que cumplan las reglas del módulo "${module}".`,
  onlyDeliveriesSatisfyingModuleRulesWillBeShared: (module: string) =>
    `, solo se compartirán las Entregas que cumplan las reglas del módulo "${module}".`,
  from: 'De',
  to: 'Para',
  _to: 'a',
  shareAllForms: 'Compartir todos los Workflows',
  shareAllDeliveries: 'Compartir todas las entregas',
  formSharing: 'Compartir Workflow',
  deliverySharing: 'Entrega compartida',
  chooseDate: 'Elegir fecha',
  autoFinishWorkflowOnFinishDate:
    'Finalizar automáticamente el Workflow en la fecha de finalización',
  executeAutomaticFinish: 'Ejecutar finalización automática',
  atAnyStep: 'En cualquier paso',
  onlyAt: 'Solo en',
  onlyAtOrAfter: 'Solo en o después de',
  youHaveUnsavedChanges: 'Tienes cambios no guardados.',
  unsavedChangesDiscarded: 'Todos los cambios no guardados se perderán.',
  stay: 'Permanecer',
  continue: 'Continuar',
  workflowMessageAdded: 'Mensaje de Workflow añadido',
  createdBy: 'Creado por',
  categoryCreatedByStruxHub: 'Categoría creada por StruxHub',
  linkForProjectCopiedToClipboard: (projectCode: string) =>
    `¡Enlace para el proyecto ${projectCode} copiado al portapapeles!`,
  invitedSuccessfully: 'Invitación enviada con éxito',
  unsubscriberSuccessfully: 'Desuscripción realizada con éxito',
  provideLink: 'Proporcione un enlace',
  youCannotSubscribeToYourOwnProject:
    'No puedes suscribirte a tu propio proyecto',
  fetchingEventsOnBackground: 'Obteniendo eventos en segundo plano',
  importedSuccessfully: 'Importado con éxito',
  thisProjectIsAlreadyInvited: 'Este proyecto ya ha sido invitado',
  confirmDeleteSubscriber: (subscriberCode: string) =>
    `¿Estás seguro de que deseas eliminar el proyecto ${subscriberCode}?`,
  sharedFromWithName: companyName => `Compartido desde ${companyName}`,
  sharedFrom: `Compartido desde`,
  enableRestrictions: 'Habilitar restricciones',
  notProvided: 'No proporcionado',
  shared: 'Compartido',
  generateAndCopyLinkForStxProjects:
    'Generar y copiar enlace para proyectos Stx',
  inviteStruxProjectsByItsCode: 'Invitar proyectos Strux por su código',
  invitedProjectCodes: 'Códigos de proyectos invitados',
  youDontHaveAnyInviteesYet: 'Aún no tienes invitados',
  subscribeToOtherStruxHubProjects: 'Suscribirse a otros proyectos de StruxHub',
  subscribeToExternalProjects: 'Suscribirse a proyectos externos',
  pastUrlFromOtherProject: 'Pegar URL de otro proyecto',
  yourSubscriptions: 'Tus suscripciones',
  youDoNotHaveAccessToProject: 'No tienes acceso al proyecto',
  youDontHaveAnySubscriptionsYet: 'Aún no tienes suscripciones',
  youSubscribedToNExternalProjects: (n: number) =>
    `Has suscrito a ${n} proyecto${ending(n)} externo${ending(n)}`,
  visibility: 'Visibilidad',
  inviteLink: 'Enlace de invitación',
  sharedProjectName: 'Nombre del proyecto compartido',
  dontShare: 'No compartir',
  disableAccess: 'Deshabilitar acceso',
  shareAllDeliveriesWithinSpecificDate:
    'Compartir todas las entregas en un período específico',
  shareAllFormsWithinSpecificDate:
    'Compartir todos los Workflows en un período específico',
  areYouSureYouWantToDisableAccess:
    '¿Estás seguro de que quieres deshabilitar el acceso?',
  deleteSubscription: 'Eliminar suscripción',
  allExternalProjectsWillLosePermission:
    'Todos los proyectos externos que usen este enlace como suscripción a tu proyecto perderán el permiso para revisar tus Workflows.',
  areYouSureYouWantToDeleteSubscription:
    '¿Estás seguro de que quieres eliminar la suscripción al proyecto?',
  inThisCaseYouWillNotBeAbleToSeeAnyWorkflowsFromThisProject:
    'En este caso, no podrás ver ningún Workflow de este proyecto',
  sharingSettings: 'Configuración de compartición',
  shareIcalCalendarLink: 'Compartir enlace iCal',
  shareToExternalProject: 'Compartido con proyecto externo',
  generateShareableLink: `Genera un enlace compartible para permitir a los propietarios de proyectos Struxhub externos ver tus Workflows según tus configuraciones de compartición.`,
  generateShareableCalendarLink: `Genera un enlace compartible para permitir a los calendarios externos
(Apple Calendar, Google Calendar, etc.) ver tus Workflows según tus configuraciones de compartición.`,
  subscribeShareableLink: `Pega un enlace compartible para obtener datos de un proyecto Struxhub externo.`,
  ifTheProjectIsEnabled:
    'Si el interruptor está activado, el proyecto externo podrá ver tus Workflows compartidos según las configuraciones de compartición que has configurado.',
  ifTheProjectIsDisabled:
    'Si el interruptor está desactivado, el proyecto externo ya no podrá ver ningún Workflow compartido.',
  import: 'Importar',
  preparingUpload: 'Preparando carga...',
  uploadEquipmentFile:
    'Suba su archivo de equipo en formato Excel (.xlsx, .xlsm) o CSV (.csv).',
  importComplete: 'Importación completa',
  created: 'Creado:',
  skippedValues: 'Algunas Propiedades quedaron vacías',
  skippedValuesDescription:
    'Todas las filas fueron creadas, pero las Propiedades a continuación no pudieron coincidir con registros existentes y se guardaron en blanco.',
  xPropertiesSkipped: (n: number) =>
    `${xProperties(n)} omitida${n === 1 ? '' : 's'}`,
  mapColumnsToProperties: 'Mapear columnas a propiedades',
  id: 'ID',
  selectIdColumn: 'Seleccionar columna ID',
  selectTitleColumn: 'Seleccionar columna de título',
  selectColumn: 'Seleccionar columna',
  template: 'Plantilla',
  selectTemplate: 'Seleccionar plantilla',
  loadingProperties: 'Cargando propiedades...',
  equipmentProperties: (n: number) => `Propiedades del equipo (${n})`,
  materialProperties: (n: number) => `Propiedades del material (${n})`,
  locationProperties: (n: number) => `Propiedades de la ubicación (${n})`,
  inviteCodeCantBeEmpty: 'El código de invitación no puede estar vacío',
  goToTheProject: 'Ir al proyecto',
  locationsFrom: 'Ubicaciones de origen',
  locationsTo: 'Ubicaciones de destino',
  goToLocation: (locationName: string) => `Ir a ${locationName}`,
  callTo: (name: string) => `Llamar a ${name}`,
  mailTo: (name: string) => `Enviar un correo a ${name}`,
  goToTheBuilding: 'Ir al edificio',
  selectWorkflowType: 'Seleccionar el Tipo de Workflow',
  createNew: 'Crear nuevo',
  defaultDuration: 'Duración predeterminada',
  maximumDuration: 'Duración máxima',
  maxDurationError: (maxDuration: string) =>
    `La duración entre 'inicio' y 'fin' excede el límite máximo permitido de ${maxDuration}`,
  nonWorkDayOrDays: 'Día/s no laborable/s',
  nonWorkHours: 'Horas no laborables',
  failedToSave: 'Error al guardar',
  failedToSaveChangesTryAgain:
    'Error al guardar los cambios. Por favor, inténtalo de nuevo.',
  failedToDeleteFormsTryAgain:
    'No se pudieron eliminar los formularios. Inténtalo de nuevo.',

  filterType: 'Filtrar tipo',
  workWeek: 'Semana laboral',
  maximumDurationTime: 'Tiempo máximo de duración',
  defaultDurationTime: 'Tiempo de duración predeterminado',
  allowRepeatingWorkflow: 'Permitir repetir Workflow',
  maximumRepeatingDuration: 'Duración máxima de repetición',
  iCalSharingSettings: 'Configuración de compartición de iCal',
  nothing: 'nada',
  if: 'Si',
  thenShow: 'entonces mostrar',
  showAllSettings: 'Mostrar toda la configuración',
  hideAllSettings: 'Ocultar toda la configuración',
  addDependentField: 'Agregar campo dependiente',
  addField: 'Agregar campo',
  alreadyAdded: 'Ya agregado',
  item: 'Elemento',
  addAssociatedField: 'Agregar campo de elemento',
  associatedFields: 'Campos de elemento',
  xAssociatedFields: (x: number) =>
    `${x} Campo${ending(x)} de elemento agregado${ending(x)}`,
  addTableColumn: 'Agregar columna a la tabla',
  tableColumns: 'Columnas de la tabla',
  xTableColumns: (x: number) => `${x} Columna${ending(x)} de tabla`,
  newRule: 'Nueva regla',
  inactive: 'Inactivo',
  repeatWorkflow: 'Repetir Workflow',
  thisWorkflow: 'Este Workflow',
  showOnSourceMap: 'Mostrar en el mapa de origen',
  thisWorkflowIsPinnedOnADifferentMapView:
    'Este Workflow está fijado en otra vista de mapa',
  thisWorkflowIsHiddenOnTheMap: 'Este Workflow está oculto en el mapa',
  switchedToMapViewX: (name: string) => `Se cambió a la vista de mapa ${name}`,
  thisWorkflowIsNotShownOnThisMapView:
    'Este Workflow no se muestra en esta vista de mapa',
  thisWorkflowHasNoPinOnAnyMapView:
    'Este Workflow no tiene marcador en ninguna vista de mapa',
  thisAndFutureWorkflows: 'Este Workflow y los futuros',
  usedInMaterialsTableCalculations:
    'Utilizado en los cálculos de la tabla de materiales',
  usedToSpecifySourceLocationInMaterialsTable:
    'Utilizado para especificar la ubicación de origen en la tabla de materiales',

  note: 'Nota',
  dependentFieldsTooltip: {
    title: 'Campos dependientes',
    description: `Crea reglas para mostrar u ocultar otros campos
  en función del valor seleccionado en este campo.`,
  },
  dynamicFiltersTooltip: {
    title: 'Filtros dinámicos',
    description:
      'Crea filtros para mostrar dinámicamente opciones basadas en valores y condiciones de campos.',
  },
  selection: 'Selección',
  selectionSettings: 'Ajustes de selección',
  conditionalFilters: 'Filtros condicionales',
  xConditionalFilters: (x: number) =>
    `${x} Filtro${ending(x)} condicional${ending(x, '', 'es')}`,
  addConditionalFilter: 'Agregar filtro condicional',
  conditionalFields: 'Campos condicionales',
  xConditionalFields: (x: number) =>
    `${x} Campo${ending(x)} condicional${ending(x, '', 'es')}`,
  addConditionalField: 'Agregar campo condicional',
  selectionTooltip:
    'Establece cómo los usuarios agregan elementos \u2014 crear, seleccionar o ambos. Define el ordenamiento, agrupación y filtros predeterminados.',
  itemFieldsTooltip:
    'Captura detalles adicionales por elemento. Cada elemento se convierte en una fila; estos campos son sus columnas.',
  conditionalFiltersTooltip:
    'Filtra la lista de elementos disponibles según los valores de otros campos en este formulario.',
  conditionalFieldsTooltip:
    'Muestra campos adicionales solo cuando se selecciona un elemento específico.',
  cardView: 'Vista de tarjeta',
  lastUpdatedBy: 'Última actualización por',
  deleteFormsConfirm: (x: number) =>
    `¿Está seguro de que desea eliminar ${x} formulario${ending(x)}?`,
  removeSelected: 'Eliminar seleccionados',
  addOption: 'Añadir opción',
  optionName: 'Nombre de la opción',
  removed: 'eliminado',
  editProperty: 'Editar Propiedad',
  createNewProperty: 'Crear Nueva Propiedad',
  deleteProperty: 'Eliminar propiedad',
  archiveProperty: 'Archivar propiedad',
  unarchiveProperty: 'Desarchivar propiedad',
  typeToConfirm: 'Escriba para confirmar',
  propertyUsedByXResources: (x: number) =>
    `Esta Propiedad es utilizada por ${x} Recurso${ending(x)}.`,
  deletePropertyConfirmation: (count: number) =>
    count > 0
      ? `Eliminarla eliminará permanentemente esta Propiedad y todos sus datos de esos Recursos. Escriba el número de Recursos asociados o "Delete" para confirmar.`
      : 'Esta Propiedad no es utilizada por ningún Recurso. Será eliminada permanentemente.',
  archivePropertyConfirmation: (count: number) =>
    count > 0
      ? `Archivar eliminará esta Propiedad del uso activo en esos Recursos. Esto se puede deshacer. Escriba el número de Recursos asociados o "Archive" para confirmar.`
      : 'Esta Propiedad no es utilizada por ningún Recurso. Será archivada.',
  propertyDetails: 'Detalles de la propiedad',
  propertyPreview: 'Vista previa',
  propertyPreviewUnavailable: 'Vista previa no disponible',
  configurePropertyBeforePreview:
    'Configure los detalles de la propiedad antes de previsualizarla.',
  managedRelationPreviewHelp:
    'Esta relación se puede gestionar después de crear el recurso.',
  calculatedPropertyPreviewHelp:
    'Este valor se calcula automáticamente y no se puede editar.',
  unsupportedPropertyPreviewHelp:
    'Este tipo de propiedad no se puede previsualizar.',
  backToDetails: 'Volver a detalles',
  textSettings: 'Configuración de texto',
  enableMultilineInput: 'Habilitar entrada multilínea',
  internalName: 'Nombre interno',
  internalNameCannotBeChanged: 'El nombre interno no se puede cambiar',
  dropdownOptionsSettings: 'Configuración de opciones desplegables',
  newOption: 'Nueva opción',
  xOptionsAdded: (x: number) =>
    `${x} opción${ending(x, '', 'es')} agregada${ending(x)}`,
  allowUsersToCreateNewOptions: 'Permitir al usuario crear nuevas opciones',
  allowUsersToSelectExistingWorkflows:
    'Allow Users to select existing workflows',
  allowUsersToCreateNewWorkflow: 'Allow Users to create new workflow',
  selectExisting: 'Seleccionar existente',
  noResultsFound: 'No se encontraron resultados',
  allowUsersToSelectExisting: (label: string) =>
    `Permitir a los usuarios seleccionar ${label} existentes`,
  allowUsersToCreateNew: (label: string) =>
    `Permitir a los usuarios crear nuevos ${label}`,
  allowToSelect: 'Permitir seleccionar',
  activityFieldRestrictedTooltip:
    'El campo Actividad está restringido solo a tipos de flujo de trabajo de Actividad.',
  cantSeeResource: (resource: string) => `¿No encuentra ${resource}?`,
  openNonWorkflowResources: (resource: string) =>
    `Abrir ${resource} que no son Recursos de flujo`,
  alsoManageFromControls: 'Además, puede realizar estos cambios desde la',
  resourceControlsPage: (resource: string) => `página Controles de ${resource}`,
  enablingResourcesHint: (resource: string) =>
    `Estos ${resource} aún no son Recursos de flujo. Al guardar se activa su interruptor «WR» y se permiten para este campo, y también para cualquier campo configurado como «Cualquier ${resource}».`,
  anyWorkflowTypes: 'Cualquier tipo de flujo',
  allowNoneSelection: "Permitir selección 'Ninguno'",
  saveChanges: 'Guardar cambios',
  noIconsFoundByYourRequest: 'No se encontraron iconos por su solicitud',
  generalWorkflow: 'Workflow general',
  ganttChart: 'Diagrama de Gantt',
  activity: 'Actividad',
  automation: 'Automatización',
  craneBooking: 'Reservación de grúa',
  deliveryBooking: 'Reservación de entrega',
  environmental: 'Ambiental',
  airMitigation: 'Mitigación de aire',
  eco: 'ECO',
  authorizedVehicleAccess: 'Acceso de vehículo autorizado',
  fireExtinguisherPermit: 'Permiso de extintor de fuego',
  heavyEquipmentUsageLog: 'Registro de uso de equipo pesado',
  ladderPermit: 'Permiso de escalera',
  surfacePenetrationPermit: 'Permiso de penetración de superficie',
  interiorConcretePenetrationPermit: 'Permiso de penetración interior/hormigón',
  aerialLiftInspection: 'Inspección de elevador aéreo',
  forkliftRoomWorkPermit: 'Permiso de trabajo de montacargas',
  materialTransfer: 'Transferencia de materiales',
  procurementTracking: 'Seguimiento de compras',
  quality: 'Calidad',
  qualityControlInspection: 'Inspección de control de calidad',
  report: 'Reporte',
  timesheet: 'Hoja de tiempo',
  safety: 'Seguridad',
  energizedRoomWorkPermit: 'Permiso de trabajo de sala energizada',
  hotWorkPermit: 'Permiso de trabajo caliente',
  jobHazardAnalysis: 'Análisis de riesgos del trabajo',
  siteCommunication: 'Comunicación del sitio',
  closureRequest: 'Solicitud de cierre',
  siteAnnouncement: 'Anuncio del sitio',
  comingSoon: 'Próximamente',
  incorrectDateOrTime: 'Fecha o hora incorrecta',
  invalidSettings: 'Configuración inválida',
  imageDimensionsCanNotBeGreaterThan: (
    maxWidth?: number,
    maxHeight?: number,
  ) => {
    if (maxWidth && maxHeight) {
      return `Las dimensiones de la imagen no pueden ser mayores a ${maxWidth}x${maxHeight}`
    } else if (maxWidth) {
      return `El ancho de la imagen no puede ser mayor a ${maxWidth}px`
    } else if (maxHeight) {
      return `La altura de la imagen no puede ser mayor a ${maxHeight}px`
    }
    return 'Dimensiones de imagen inválidas'
  },
  fileSizeCanNotBeGreaterThan: (maxFileSize: string) =>
    `El tamaño del archivo no puede ser mayor a ${maxFileSize}`,
  controlledAccessZone: 'Zona de acceso controlado',
  guardrailRemovalPermit: 'Permiso de retirada de barandilla',
  roofAccessPermit: 'Permiso de acceso a la cubierta',
  smartFormImport: 'Importación inteligente de Workflow',
  smartFormImportDescription:
    'Carga un archivo PDF o imagen y la IA detectará y mapeará automáticamente los campos.',
  dragAndDropFile: 'Arrastrar y soltar archivo',
  or: 'o',
  chooseFile: 'Elegir archivo',
  fileSupportText: 'Soporta archivos PDF, JPG, PNG',
  importFile: 'Importar archivo',
  workingOnIt: '🛠️ Estamos trabajando en ello.',
  detectingHeaders: '🔎 Detectando encabezados…',
  findingFields: '📑 Encontrando campos…',
  scanningCheckboxes: '☑️ Escaneando casillas de verificación…',
  organizingWorkflowSteps: '🗂️ Organizando pasos de Workflow…',
  aligningWithStruxHubSchema: '🧩 Alignando con el esquema StruxHub…',
  doubleCheckingFieldTypes: '🛡️ Doble-verificando tipos de campos…',
  tidyingUpYourForm:
    '🧹 Limpiando tu formulario (más limpio que el escritorio de un trailer).',
  sharpeningDigitalPencils: '✏️ Aflando los lápices digitales…',
  sortingThingsIntoTheRightBins: '🏗️ Ordenando cosas en los bins correctos…',
  moveFieldsIntoDifferentWorkflowStepsAfterImport:
    '💡 Puedes mover campos a diferentes pasos de workflow después de la importación.',
  addWorkflowStepsForApprovalsInspectionsAndStartTimes:
    '💡 Puedes agregar pasos de workflow para aprobaciones, inspecciones y tiempos de inicio.',
  routeWorkflowStepsToBallInCourtUsersWithConditionalLogic:
    '💡 Puedes redirigir pasos de workflow a usuarios de pelota en cancha con lógica condicional.',
  notifyWorkflowMembersAfterSpecificSteps:
    '💡 Puedes notificar a los miembros del workflow después de pasos específicos.',
  triggerResourceClosureAfterSpecificWorkflowStep:
    '💡 Puedes activar un cierre de recurso después de un paso de workflow específico.',
  linkWorkflowToAnotherWorkflow:
    '💡 Puedes vincular un workflow a otro workflow a través del campo «Workflow vinculado».',
  createTemplatesFromImportedFormsToReuseAcrossProjects:
    '💡 Puedes crear plantillas desde formularios importados para reutilizar en proyectos.',
  assignResponsibilityForWorkflowStepsToSpecificCompanies:
    '💡 Puedes asignar responsabilidad para pasos de workflow a empresas específicas.',
  rollUpFormDataIntoExportableReports:
    '💡 Puedes acumular datos de formularios en informes exportables.',
  attachDocumentsAndPhotosToAnyWorkflow:
    '💡 Puedes adjuntar documentos y fotos a cualquier workflow.',
  safetyFirstNoHardHatRequiredWhileWeProcessYourForm:
    '🦺 Seguridad en primer lugar: no se requiere casco mientras procesamos tu formulario.',
  ourScannersLiftFormsFasterThanALifterLiftsSteel:
    '🚜 Nuestros escáneres levantan formularios más rápido que las gruas levantan el acero.',
  everSeenPaperworkMoveThisFastOnAJobsiteNeitherHaveWe:
    '🏃 ¿Has visto alguna vez que el papel se mueva tan rápido en un sitio de trabajo? Ni nosotros.',
  importingFormsNoRFIsRequired:
    '📨 Importación de formularios — no se requieren RFIs.',
  dontWorryThisWontTakeAsLongAsAConcretePour:
    '🏗️ No te preocupes, esto no tomará tanto tiempo como un vertido de concreto.',
  processingFormsFasterThanFindingTheRightWrenchInYourToolbox:
    '🔧 Procesar formularios más rápido que encontrar la llave correcta en tu herramienta.',
  proTipThisIsTheOnlyTimePaperworkWillDoTheWorkForYou:
    '🤓 Consejo: esta es la única vez que el papel hará el trabajo por ti.',
  scanningWithoutThePaperCuts: '✋ Escaneando… sin las cortes de papel.',
  ourImportToolDoesntNeedAForemanYellingAtIt:
    '📣 Nuestro herramienta de importación no necesita que un jefe de obra lo grite.',
  almostThereUnlikeThatElevatorInstallation:
    '🚧 Casi allí — a diferencia de la instalación de un elevador.',
  wrappingThingsUp: '🎁 Poniendo las últimas anotaciones…',
  puttingFinishingTouchesOnYourForm:
    '✅ Poniendo las últimas anotaciones en tu formulario…',
  justOneMoreCheck: '📦 Una última verificación…',
  workflowWasSuccessfullyImported: (workflowName: string) =>
    `🎉 ${workflowName} fue importado con éxito!`,
  somethingWentWrongWhileImportingFile:
    'Algo salió mal mientras importabas tu archivo',
  importInProgress: 'Ya hay una importación en curso',
  viewWorkflow: 'Ver workflow',
  enableMaximumDuration: 'Habilitar duración máxima',
  enableDefaultDuration: 'Habilitar duración por defecto',
  minute_plural: (x: number) => `Minuto${ending(x)}`,
  hour_plural: (x: number) => `Hora${ending(x)}`,
  day_plural: (x: number) => `Día${ending(x)}`,
  week_plural: (x: number) => `Semana${ending(x)}`,
  month_plural: (x: number) => `Mes${ending(x, '', 'es')}`,
  durationCannotBeGreaterThan: `La duración no puede ser mayor que`,
  saveAsDraft: 'Guardar como borrador',
  draftSavedSuccessfully:
    'Borrador guardado con éxito. Todas las modificaciones se han guardado.',
  createdXofYWorkflows: (created: number, total: number) =>
    `${created} de ${total} workflows creados`,
  skippedWorkflows: 'Omitidos',
  unknownLocation: 'Ubicación desconocida',
  blockedByClosure: 'bloqueado por cierre en',
  blockedByProjectClosed: 'bloqueado por intervalo de cierre del proyecto',
  blockedByNonWorkingDay: 'en días no laborables',
  blockedByOperatingHours: 'fuera del horario de operación de',
  reservedForOtherCompanies: 'Reservado para otras empresas',
  discardedUnsavedDrawing:
    'Tu dibujo sin guardar se descartó porque el mapa se cerró',
  failedToCreateWorkflow: 'No se pudo crear el Workflow. Inténtalo de nuevo.',
  failedToCreateRecurringWorkflows:
    'Error al crear flujos de trabajo recurrentes. Por favor, inténtelo de nuevo o contacte con soporte',
  unavailableDraftWorkflowsSelected:
    'Indisponible: Uno o más Workflows seleccionados están en estado de borrador',
  draft_noun: 'Borrador',
  linkedWorkflows: 'Workflows vinculados',
  addLinkedWorkflow: 'Agregar workflow vinculado',
  dateTime: 'Fecha y Hora',
  file: 'Archivo',
  allWorkflow_s: 'Todos los flujos de trabajo',
  appliedTo: (count: number) => `Aplicado a (${count})`,
  closuresConflictTitle: 'Conflicts de Cierres',
  closuresConflictMessage:
    'Las ubicaciones seleccionadas tienen cierres que confligen con las fechas del Workflow. Las ubicaciones en conflicto se eliminaron automáticamente. Revise su formulario y ajústelo si es necesario.',
  closuresConflictRecurringMessage:
    'Algunas ocurrencias recurrentes caen en fechas cerradas. Puede continuar creando el Workflow recurrente o revisar y ajustar sus selecciones.',
  selectWorkflowTypes: 'Seleccionar Tipos de Flujo de Trabajo',

  resourcePropertyText: 'Texto',
  resourcePropertyNumber: 'Número',
  resourcePropertySelect: 'Menú desplegable',
  resourcePropertyHierarchy: 'Jerarquía',
  resourcePropertyCheckbox: 'Casilla de verificación',
  resourcePropertyDatetime: 'Fecha/Hora',
  resourcePropertyRelation: 'Relación',
  resourcePropertyLocation: 'Ubicación',
  resourcePropertyRollup: 'Campo de resumen',
  resourcePropertyComputed: 'Campo calculado',
  resourcePropertyUser: 'Usuario',
  resourcePropertyCompany: 'Empresa',
  resourcePropertyFile: 'Archivo',
  uploadFiles: 'Subir archivos',
  uploadFile: 'Subir archivo',
  attachFile: 'Adjuntar archivo',
  attachFiles: 'Adjuntar archivos',
  camera: 'Cámara',
  maxFilesReached: 'Se alcanzó el número máximo de archivos',
  fileTooLarge: (maxMB: number) =>
    `El archivo es demasiado grande (máx. ${maxMB} MB)`,
  sitemapsTooLargeForMobile:
    'Algunos mapas del sitio son demasiado grandes para mostrarse en la aplicación. Abra StruxHub en su navegador para verlos.',
  fileTypeNotAllowed: 'El tipo de archivo no está permitido',
  pdfPageImagesCouldNotBeRendered:
    'Algunas imágenes de esta página PDF no se pudieron mostrar y faltan en el mapa importado.',
  associatedProperties: 'Propiedades asociadas',
  appliedToColumn: 'Aplicado a',
  xResourceObjects: (count: number, resourceTypeId: string) => {
    const noun =
      (
        {
          offloading_equipment: 'de equipo',
          material: 'de material',
          sensor: 'de sensor',
        } as Record<string, string>
      )[resourceTypeId] ?? ''
    return `${count} Objeto${ending(count, '', 's')} ${noun}`
  },
  deleteSelected: 'Eliminar seleccionados',
  selectASingleWorkflowToDuplicate:
    'Seleccionar un flujo de trabajo para duplicar',
  subscribeSelected: 'Suscribir seleccionados',
  assignSelected: 'Asignar seleccionados',
  doYouWantToDeleteSelectedWorkflows:
    '¿Quieres eliminar los flujos de trabajo seleccionados?',
  allDataRelatedToSelectedWorkflowsWillBeDeleted:
    'Todas las datos relacionados con los flujos de trabajo seleccionados serán eliminados permanentemente y no podrán ser recuperados.',
  statuses: 'estados',
  updateSelectedWorkflowsStatusTo:
    'Actualizar el estado de los Workflows seleccionados a:',
  youAreGoingToUpdate: 'Estás a punto de actualizar el',
  _for: 'para',
  updateXWorkflows: (x: number) => `Actualizar ${x} Workflow${ending(x)}`,
  subscribeUsers: 'Suscribir usuarios',
  subscribed: 'Suscrito',
  noUsersHaveSubscribedYet: 'No hay usuarios suscritos todavía',
  thisUserHasSubscribedToOnlySomeOfTheWorkflowsYouSelected:
    'Este usuario ha suscrito a solo algunos de los workflows que has seleccionado.',
  subscribedTo: 'Suscrito a:',
  notSubscribedTo: 'No suscrito a:',
  signature: 'Firma',
  signaturePreview: 'Vista previa de la firma',
  draw: 'Dibujar',
  typeHere: 'Escriba aquí',
  addTextHere: 'Agregue texto aquí',
  signHere: 'Firme aquí',
  editStyle: 'Editar estilo',
  clickToAddSignature: 'Haga clic para agregar firma',
  newUserSignupWorkflow: 'Flujo de registro de nuevos usuarios',
  whiteListDomains: 'Dominios de lista blanca',
  whiteListDomainsTooltip:
    'Cuando se agrega un dominio a la lista blanca, permite a todos los usuarios con direcciones de correo electrónico verificadas de estos dominios unirse a su proyecto StruxHub sin ser invitados específicamente.',
  whiteListDomainsTooltipNote:
    'NOTA: No agregue a la lista blanca dominios de correo electrónico genéricos como @gmail.com, @outlook.com, @iCloud.com. Hacerlo permitiría el acceso de usuarios no verificados a su proyecto.',
  usersCanCreateAccountByLink:
    'Los usuarios pueden crear una cuenta mediante este enlace:',
  orSnappingThisQRCode: 'O escaneando este código QR:',
  print: 'Imprimir',
  noPermissionToCreateWorkflowType:
    '¡Lo sentimos! No tiene los permisos necesarios para crear este tipo de flujo de trabajo',
  approvalConfiguration: 'Configuración de aprobación',
  sendBicApprovalTo: 'Enviar aprobación BIC a',
  bicApprovalTooltip:
    'Estos usuarios reciben una notificación de aprobación ball-in-court cuando se aplica esta regla.',
  allProjectCoordinators: 'Todos los coordinadores del proyecto',
  selectApprovers: 'Seleccionar aprobadores',
  addFilter: 'Agregar filtro',
  onlyWhen: 'Solo cuando:',
  selectCompanyType: 'Seleccionar tipo de empresa',
  deleteRule: 'Eliminar regla',
  deleteView: 'Eliminar vista',
  deleteResource: 'Eliminar recurso',
  deleteTag: 'Eliminar etiqueta',
  deleteItem: 'Eliminar elemento',
  deleteField: 'Eliminar campo',
  deleteFieldQuestion: '¿Está seguro de que desea eliminar el campo?',
  deleteViewQuestion: (name: string) =>
    `¿Está seguro de que desea eliminar la vista ${name}?`,
  removeUser: 'Eliminar usuario',
  xAllowedFormsHaveBeenDeleted: (x: number) =>
    `${x} Formulario${ending(x)} permitido${ending(x)} ${ending(
      x,
      'ha',
      'han',
    )} sido eliminado${ending(x)}`,
  confirmResourceControlChangesTitle: '¿Actualizar los controles del recurso?',
  resourceControlChangeAffectsWorkflows: (count: number) =>
    `Este recurso todavía se usa en ${count} tipo${ending(
      count,
    )} de workflow. Esto lo eliminará de cada uno:`,
  cascadeConditionalFieldsRemoved: (count: number) =>
    `${count} campo${ending(count)} condicional${ending(
      count,
      '',
      'es',
    )} eliminado${ending(count)}`,
  cascadeConditionalFiltersRemoved: (count: number) =>
    `${count} filtro${ending(count)} condicional${ending(
      count,
      '',
      'es',
    )} eliminado${ending(count)}`,
  cascadeNoLongerSelectableInFields: (count: number) =>
    `Ya no se puede seleccionar en ${count} campo${ending(count)}`,
  cascadeNoLongerSelectableInConditionalFilters: (count: number) =>
    `Ya no se puede seleccionar en ${count} filtro${ending(
      count,
    )} condicional${ending(count, '', 'es')}`,
  cascadeNoLongerSelectableInConditionalFields: (count: number) =>
    `Ya no se puede seleccionar en ${count} campo${ending(
      count,
    )} condicional${ending(count, '', 'es')}`,
  cascadeRemovedFromExclusion: (count: number) =>
    `Eliminado de ${count} lista${ending(count)} de exclusión`,
  cascadeOtherReferencesRemoved: 'Otras referencias eliminadas',
  couldNotCheckAffectedWorkflows:
    'No se pudieron verificar los flujos de trabajo afectados. Inténtelo de nuevo.',
  someResourcesCouldNotBeDisabled: (count: number) =>
    `No se pudo deshabilitar ${count} recurso${ending(
      count,
    )}. Inténtelo de nuevo.`,
  someResourcesCouldNotBeUpdated: (count: number) =>
    `No se pudo actualizar ${count} recurso${ending(
      count,
    )}. Inténtelo de nuevo.`,
  projectLocationDeletionProhibited:
    'La eliminación de Project Location está prohibida',
  projectLocationControlEditProhibited: (controlName: string) =>
    `La edición de «${controlName}» para Project Location está prohibida`,
  projectLocationBulkEditProhibited:
    'La edición en masa de Project Location está prohibida',
  selectionHasResourcesYouCannotChange:
    'Los recursos solo pueden ser editados o eliminados por los administradores y sus propietarios (usuario responsable o miembros de la empresa responsable)',
  outsideHours: 'Fuera de horario',
  closedStatus: 'Cerrado',
  tooLong: 'Demasiado largo',
  fullyBooked: 'Completo',
  reserved: 'Reservado',
  unavailable: 'No disponible',
  closedOnWeekdays: (weekday: string) => `Cerrado cada ${weekday}`,
  closedOnX: (day: string) => `Cerrado el ${day}`,
  onlyOpenXToY: (opens: string, closes: string) =>
    `Solo abierto ${opens} – ${closes}`,
  opensAtX: (time: string) => `Abre a las ${time}`,
  closesAtX: (time: string) => `Cierra a las ${time}`,
  closesAtXReopensAtY: (closes: string, reopens: string) =>
    `Cierra a las ${closes}, reabre a las ${reopens}`,
  hasBreakDuringThisBooking: 'Tiene una pausa durante esta reserva',
  xToYEveryDay: (start: string, end: string) =>
    `${start} – ${end} todos los días`,
  allDayX: (day: string) => `Todo el día el ${day}`,
  xMinutesDuration: (x: number) => `${x} minuto${ending(x)}`,
  xHoursDuration: (x: number) => `${x} hora${ending(x)}`,
  xDaysDuration: (x: number) => `${x} día${ending(x)}`,
  xWeeksDuration: (x: number) => `${x} semana${ending(x)}`,
  xMonthsDuration: (x: number) => `${x} mes${ending(x, '', 'es')}`,
  limitIsX: (limit: string) => `Límite: ${limit}`,
  limitIsOneWorkingDay: 'Límite: un día laborable',
  limitIsOneWorkWeek: 'Límite: una semana laboral',
  thisBookingIsX: (length: string) => `Esta reserva dura ${length}.`,
  listWithXMore: (items: string[], moreCount: number) =>
    moreCount ? `${items.join(', ')} y ${moreCount} más` : items.join(' y '),
  aBooking: 'una reserva',
  takenByX: (bookings: string) => `Ocupado por ${bookings}`,
  takenByXAtY: (booking: string, time: string) =>
    `Ocupado por ${booking}, ${time}`,
  bookingWithTime: (booking: string, time: string) => `${booking}: ${time}`,
  noRoomAtThisTime: 'Sin disponibilidad en este horario',
  takesXBookingsAtATime: (x: number) =>
    `Admite ${x} reserva${ending(x)} a la vez.`,
  fullyBookedOnAllXRepeats: (total: number) =>
    `Completo en las ${total} repeticiones.`,
  fullyBookedOnXOfYRepeats: (failing: number, total: number, first: string) =>
    `Completo en ${failing} de ${total} repeticiones, la primera el ${first}.`,
  forX: (companies: string) => `Para ${companies}`,
  forOtherCompanies: 'Para otras empresas',
  cantBeBooked: 'No se puede reservar',
  affectsAllXRepeats: (total: number) => `Afecta a las ${total} repeticiones.`,
  affectsXOfYRepeats: (failing: number, total: number, first: string) =>
    `Afecta a ${failing} de ${total} repeticiones, la primera el ${first}.`,
  projectHoursHeading: 'Horario laboral del proyecto:',
  usesProjectHoursHeading: 'Usa el horario laboral del proyecto:',
  hoursHeading: 'Horario de operación:',
  noneSet: 'sin definir',
  notRequestedXAhead: (deadline: string) =>
    `No solicitado con ${deadline} de antelación`,
  notRequestedInAdvance: 'No solicitado con antelación',
  outsideWorkingHours: 'Fuera del horario laboral',
  youCanStillApprove:
    'Aún puede aprobar. Asegúrese de que la obra pueda asumirlo.',
  youCanStillSubmit: 'Aún puede enviar. Su aprobador verá estas notas.',
  approveAnyway: 'Aprobar de todos modos',
  submitAnyway: 'Enviar de todos modos',
  xMore: (x: number) => `${x} más`,
}

export default SpanishTree
